/**
 * Lightweight fetch wrapper + typed endpoint helpers for the DreamTrials backend.
 *
 * In dev, the Vite proxy (vite.config.ts) forwards `/api/*` to the backend at
 * http://localhost:4000. In production, the React bundle is served from
 * CloudFront and `/api/*` routes through API Gateway to the same Lambda.
 *
 * `auth.*` helpers wrap the backend's DREAM-1 endpoints with strict typing.
 */

import { encryptId } from '@/lib/encryptId';

let authToken: string | null = null;

export function setAuthToken(token: string | null): void {
  authToken = token;
}

export function getAuthToken(): string | null {
  return authToken;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface ApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
}

/**
 * API base URL. In dev (Vite), defaults to `/api` so the dev-server proxy
 * forwards to `http://localhost:4000`. In production builds, set
 * `VITE_API_BASE` at build time to the absolute API Gateway URL — e.g.
 * `https://abc123.execute-api.us-east-1.amazonaws.com`.
 */
const API_BASE = (import.meta.env?.VITE_API_BASE as string | undefined) ?? '/api';

/**
 * Optional unauthorized-handler hook. Set from `AuthContext` on mount so
 * the client can clear the session + redirect when any API call comes back
 * 401 (token expired, revoked, or never present). Path passed to client to
 * keep this module free of router/auth dependencies.
 */
let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: (() => void) | null): void {
  onUnauthorized = fn;
}

function resolveToken(): string | null {
  if (authToken) return authToken;
  // HMR in dev resets module-level variables while React state survives.
  // Fall back to sessionStorage so the in-memory cache is repopulated.
  try {
    const raw = sessionStorage.getItem('dreamit2:session');
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { token?: string };
    if (parsed?.token) { authToken = parsed.token; return authToken; }
  } catch { /* ignore */ }
  return null;
}

async function api<T>(path: string, opts: ApiOptions = {}): Promise<T> {
  const headers: HeadersInit = { Accept: 'application/json' };
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  const token = resolveToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  const response = await fetch(`${API_BASE}${path}`, {
    method: opts.method ?? 'GET',
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
    credentials: 'omit',
  });

  if (response.status === 204) return undefined as T;
  const text = await response.text();
  const data = text ? JSON.parse(text) : null;

  if (!response.ok) {
    const err = data?.error;
    // 401 only matters for authenticated calls. The pre-auth flows
    // (/auth/login, /auth/mfa/*, /auth/forgot-password) get 401s by design
    // when credentials are wrong — those must NOT trigger session-clear.
    // Same for /auth/re-authenticate: a 401 there means "wrong ceremony
    // credentials" (bad password / TOTP during a Sign Visit / Reopen
    // ceremony), NOT "session expired". Treating it as expiry wipes the
    // still-valid JWT and boots the user to /login mid-signing.
    // Routes outside these allow-lists are treated as session-expiry signals.
    const isPreOrCeremonyAuth =
      path.startsWith('/auth/login')
      || path.startsWith('/auth/re-authenticate');
    if (response.status === 401 && authToken && !isPreOrCeremonyAuth) {
      try { onUnauthorized?.(); } catch { /* never let cleanup throw */ }
    }
    throw new ApiError(
      response.status,
      err?.code ?? 'UNKNOWN',
      err?.message ?? data?.message ?? response.statusText ?? 'Request failed',
      err?.details,
    );
  }
  return data as T;
}

// =====================================================================
// Typed DTOs
// =====================================================================

export type LoginNextStep =
  | 'mfa_totp'
  | 'mfa_otp'
  | 'mfa_enrol_required'
  /** F3 - user passed MFA but must accept the current T&C version. */
  | 'terms_acceptance_required';
export type AmrClaim = 'pwd' | 'totp' | 'email' | 'recovery';

export interface LoginResponse {
  next: LoginNextStep;
  challenge_token: string;
}

export interface LoginCompleteResponse {
  access_token: string;
  token_type: 'Bearer';
  expires_in: number;
  user: {
    user_id: string;
    email: string;
    full_name: string;
    role: string;
    site_id: string;
    allowed_sites: string[];
    site_names: Record<string, string>;
    site_time_zone: string | null;
  };
}

/**
 * F3 - alternative shape loginMfa returns when the caller's stored
 * terms_accepted_version does not match TERMS_VERSION. The FE detects
 * `next === 'terms_acceptance_required'` and routes to /auth/accept-terms
 * where the user reads + accepts before the session JWT is minted.
 */
export interface TermsGateResponse {
  next: 'terms_acceptance_required';
  challenge_token: string;
  terms_version: string;
  amr: AmrClaim[];
}

export function isTermsGate(
  r: LoginCompleteResponse | TermsGateResponse,
): r is TermsGateResponse {
  return (r as TermsGateResponse).next === 'terms_acceptance_required';
}

export interface EnrolStartResponse {
  qr_data_uri: string;
  secret: string;
  recovery_codes: string[];
}

export interface EnrolVerifyResponse {
  enrolled: true;
  challenge_token: string;
}

export interface OtpRequestResponse {
  next: 'mfa_otp';
  challenge_token: string;
  otp_challenge_token: string;
  destination_masked: string;
}

export interface MeResponse {
  user_id: string;
  email: string;
  full_name: string;
  role: string;
  site_id: string;
  allowed_sites: string[];
  site_names: Record<string, string>;
  site_time_zone: string | null;
  mfa_enrolled: boolean;
  amr: AmrClaim[];
  recovery_codes?: {
    enrolled_count: number;
    consumed: number;
    remaining: number;
    low: boolean;
  };
}

// =====================================================================
// Endpoint helpers
// =====================================================================

export const auth = {
  login: (email: string, password: string) =>
    api<LoginResponse>('/auth/login', { method: 'POST', body: { email, password } }),

  loginMfa: (opts: {
    challenge_token: string;
    method: 'totp' | 'email' | 'recovery';
    code: string;
    otp_challenge_token?: string;
  }) => api<LoginCompleteResponse | TermsGateResponse>('/auth/login/mfa', { method: 'POST', body: opts }),

  // F3 - consume the terms-acceptance challenge and mint the session JWT.
  acceptTerms: (opts: {
    challenge_token: string;
    accepted_version: string;
    amr: AmrClaim[];
  }) => api<LoginCompleteResponse>('/auth/accept-terms', { method: 'POST', body: opts }),

  mfaEnrolStart: (challenge_token: string) =>
    api<EnrolStartResponse>('/auth/mfa/enrol/start', { method: 'POST', body: { challenge_token } }),

  mfaEnrolVerify: (challenge_token: string, code: string) =>
    api<EnrolVerifyResponse>('/auth/mfa/enrol/verify', { method: 'POST', body: { challenge_token, code } }),

  mfaOtpRequest: (challenge_token: string) =>
    api<OtpRequestResponse>('/auth/mfa/otp/request', { method: 'POST', body: { challenge_token } }),

  forgotPassword: (email: string) =>
    api<{ message: string }>('/auth/forgot-password', { method: 'POST', body: { email } }),

  /** `full_name` only applies to first-time account setup (no password yet). */
  resetPassword: (token_id: string, token: string, new_password: string, full_name?: string) =>
    api<{ message: string }>('/auth/reset-password', {
      method: 'POST',
      body: { token_id, token, new_password, full_name },
    }),

  // F2 — self-service change password (session JWT + current password).
  changePassword: (current_password: string, new_password: string) =>
    api<{ ok: true; message: string }>('/auth/change-password', {
      method: 'POST',
      body: { current_password, new_password },
    }),

  // F2 — self-service authenticator reset (session JWT + password + current TOTP).
  resetMfaSelf: (password: string, totp_code: string) =>
    api<{ ok: true; message: string }>('/auth/mfa/reset-self', {
      method: 'POST',
      body: { password, totp_code },
    }),

  me: () => api<MeResponse>('/auth/me'),

  refresh: () =>
    api<{
      access_token: string;
      token_type: 'Bearer';
      site_id: string;
      allowed_sites: string[];
      site_names: Record<string, string>;
      site_time_zone: string | null;
    }>('/auth/refresh', { method: 'POST' }),

  logout: (idle = false) =>
    api<{ ok: true }>('/auth/logout', { method: 'POST', body: { idle } }),

  switchSite: (site_id: string) =>
    api<{ access_token: string; token_type: 'Bearer'; expires_in: number; site_id: string; role: string; site_time_zone: string | null }>(
      '/auth/sites/switch',
      { method: 'POST', body: { site_id } },
    ),

  acceptInvite: (invite_id: string, token: string, password: string, full_name?: string) =>
    api<{ activated: true; user_id: string; email: string; site_id: string; role: string }>(
      '/invitations/accept',
      { method: 'POST', body: { invite_id, token, password, full_name } },
    ),
};

// =====================================================================
// Team / invitations (DREAM-9)
// =====================================================================

export type InvitableRole =
  | 'principal_investigator'
  | 'sub_investigator'
  | 'coordinator'
  | 'regulatory_specialist'
  | 'site_pharma'
  | 'finance_billing'
  | 'site_study_designer'
  | 'monitor'
  | 'auditor';

export interface PendingInvitation {
  invite_id: string;
  site_id: string;
  email: string;
  role: string;
  expires_at_iso: string;
  invited_by_user_id: string;
  email_status: 'sent' | 'failed' | null;
  email_error: string | null;
}

export const team = {
  listPending: (site_id: string) =>
    api<{ rows: PendingInvitation[]; count: number }>(
      `/sites/${encodeURIComponent(site_id)}/invitations`,
    ),

  invite: (
    // Client feedback 2026-07-27: role is optional and defaults to
    // 'site_user' (lowest privilege; 'monitor' is not invitable to a site).
    // Callers who need to grant a specific
    // role pass it explicitly; server validates it's within the caller's
    // grant tier and 403s on lateral escalation.
    site_id: string,
    input: { email: string; full_name?: string; role?: string; study_role?: string; study_ids?: string[] },
  ) =>
    api<{
      invite_id: string;
      site_id: string;
      role: string;
      expires_at_iso: string;
      studies_assigned?: number;
    }>(`/sites/${encodeURIComponent(site_id)}/invitations`, {
      method: 'POST',
      body: input,
    }),

  resend: (site_id: string, invite_id: string) =>
    api<{
      invite_id: string;
      site_id: string;
      role: string;
      expires_at_iso: string;
    }>(
      `/sites/${encodeURIComponent(site_id)}/invitations/${encodeURIComponent(invite_id)}/resend`,
      { method: 'POST' },
    ),
};

// =====================================================================
// Compliance (DREAM-11)
// =====================================================================

export interface AuditRow {
  audit_id: string;
  timestamp: string;
  user_id: string | null;
  user_full_name: string | null;
  user_role_at_time: string | null;
  site_id: string | null;
  study_id: string | null;
  action: string;
  resource_type: string | null;
  resource_id: string | null;
  field_changed: string | null;
  old_value: string | null;
  new_value: string | null;
  ip: string | null;
  user_agent: string | null;
  request_id: string | null;
  source: string;
  pii_included: boolean;
  /**
   * Resolved server-side (2026-10-09). The stored row holds ids only, so
   * without these the table and its export read as a wall of UUIDs.
   * `study_name` is "Name (PROTOCOL)" — name primary, per the client rule.
   */
  study_name?: string | null;
  site_name?: string | null;
}

export interface AccessLogRow {
  access_id: string;
  timestamp: string;
  user_id: string | null;
  user_role_at_time: string | null;
  site_id: string | null;
  study_id: string | null;
  subject_id: string | null;
  resource_type: string;
  resource_id: string | null;
  action_detail: string;
  ip: string | null;
  user_agent: string | null;
  request_id: string | null;
  source: string;
  /** Resolved server-side — see AuditTrailRow. */
  user_name?: string | null;
  study_name?: string | null;
  site_name?: string | null;
  subject_ref?: string | null;
}

/**
 * Phase 4 base — Jawahar's authoritative category enum. Empty on
 * legacy rows written before the Phase 4 migration.
 */
export type QueryCategory =
  | 'missing_document'
  | 'incorrect_metadata'
  | 'expired_document'
  | 'compliance_flag'
  | 'other';

/** Phase 4 extended — raiser-origin taxonomy. See queryTypeFromOrigin server-side. */
export type QueryOrigin = 'sponsor_query' | 'cra_query' | 'internal' | 'system';
/** Punch-list f1 — the client-facing Internal/External label, derived from `origin`. */
export type QueryVisibilityType = 'internal' | 'external';
export function queryTypeFromOrigin(origin: QueryOrigin | null | undefined): QueryVisibilityType {
  return origin === 'cra_query' || origin === 'sponsor_query' ? 'external' : 'internal';
}

export interface ComplianceQueryRow {
  query_id: string;
  /** Human-readable id (e.g. "QRY-0005") — prefer this over query_id in any UI label. */
  reference_number?: string;
  study_id: string;
  site_id: string;
  subject_id: string | null;
  field_ref: string | null;
  body: string;
  raised_by_user_id: string;
  /** Resolved server-side on list responses — carries no role restriction,
   *  unlike the site-users roster endpoint (which 403s for monitor/auditor). */
  raised_by_name?: string | null;
  raised_at: string;
  status: 'open' | 'answered' | 'closed';
  closed_at: string | null;
  closed_by_user_id: string | null;
  // Phase 4 base — Jawahar's Query Tracking columns.
  category?: QueryCategory | null;
  assignee_user_id?: string | null;
  assignee_role?: string | null;
  folder_id?: string | null;
  section_ref?: string | null;
  document_id?: string | null;
  origin?: QueryOrigin | null;
  reply_text?: string | null;
  /** Punch-list f4 — visit linkage. */
  visit_id?: string | null;
  /** Punch-list f3 — resolved server-side (only present on list responses)
   *  from document_id, so a query row raised from a binder document can
   *  link back to it. Null when document_id is unset or unresolvable. */
  binder_id?: string | null;
  binder_reference_number?: string | null;
  /** Field-level queries: the field the query targets (label in field_ref). */
  field_id?: string | null;
  /** Resolved server-side on list responses (23 Oct 2026) so the list can
   *  say where each query was raised. Null when not applicable. */
  document_title?: string | null;
  assignee_name?: string | null;
  study_title?: string | null;
  study_protocol_number?: string | null;
  study_reference_number?: string | null;
  subject_external_ref?: string | null;
  subject_ivrf_number?: string | null;
  visit_name?: string | null;
  created_at: string;
  updated_at: string;
}

export interface AuditFilters {
  site_id?: string;
  study_id?: string;
  /** Sprint-2 — narrow to a (study × site) row id (used by wizard / upload AuditTrailPanel). */
  study_site_id?: string;
  /** Filter by resource_id (e.g. a subject ID to scope the wizard audit panel). */
  resource_id?: string;
  /** Single id or list — list is serialised as a comma-separated query string. */
  user_id?: string | string[];
  /** Single action code or list — list serialises as comma-separated. */
  action?: string | string[];
  from?: string;
  to?: string;
  limit?: number;
  page?: number;
}

export interface AccessFilters extends AuditFilters {
  subject_id?: string;
  ip?: string;
  /** Single resource_type or list. */
  resource_type?: string | string[];
}

function qs(params: Record<string, string | number | string[] | undefined> | object): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params as Record<string, unknown>)) {
    if (v === undefined || v === '' || v === null) continue;
    if (Array.isArray(v)) {
      const filtered = v.filter((x) => x !== '' && x !== null && x !== undefined);
      if (filtered.length === 0) continue;
      // Comma-separated — backend `csvList()` splits this back into an array.
      sp.set(k, filtered.map(String).join(','));
      continue;
    }
    sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

export interface ComplianceOverviewResponse {
  site_id: string;
  study_id: string | null;
  queries: {
    open: number;
    aging: { lt_3d: number; '3_7d': number; '7_14d': number; gt_14d: number };
    oldest_open_days: number | null;
  };
  tasks: {
    open: number;
    by_severity: { critical: number; high: number; medium: number; low: number };
    expired_documents: number;
    protocol_amendments: number;
  };
  reconsent: { subjects_pending: number };
  studies_active: number;
  generated_at: string;
}

export const compliance = {
  listAuditTrail: (filters: AuditFilters = {}) =>
    api<{ rows: AuditRow[]; total: number; hasMore: boolean; page: number; limit: number }>(
      `/compliance/audit-trail${qs(filters)}`,
    ),

  exportAuditTrailUrl: (filters: AuditFilters = {}): string =>
    `${API_BASE}/compliance/audit-trail${qs({ ...filters, format: 'csv' })}`,

  listAccessLog: (filters: AccessFilters = {}) =>
    api<{ rows: AccessLogRow[]; total: number; hasMore: boolean; page: number; limit: number }>(
      `/compliance/access-log${qs(filters)}`,
    ),

  exportAccessLogUrl: (filters: AccessFilters = {}): string =>
    `${API_BASE}/compliance/access-log${qs({ ...filters, format: 'csv' })}`,

  listQueries: (
    filters: {
      study_id?: string;
      subject_id?: string;
      status?: 'open' | 'answered' | 'closed';
      // Phase 4 base — AC 4.4 filters.
      assignee_user_id?: string;
      category?: QueryCategory;
      // Punch-list f4/f6 — visit linkage + list-convenience filters.
      visit_id?: string;
      type?: QueryVisibilityType;
      raised_by_me?: boolean;
      assigned_to_me?: boolean;
      unassigned?: boolean;
    } = {},
  ) =>
    api<{ rows: ComplianceQueryRow[]; count: number }>(
      `/compliance/queries${qs(filters as Record<string, string | number | undefined>)}`,
    ),

  raiseQuery: (input: {
    study_id: string;
    subject_id?: string;
    field_id?: string;
    field_ref?: string;
    body: string;
    // Phase 4 base — Jawahar Query Tracking metadata.
    category?: QueryCategory;
    assignee_user_id?: string;
    assignee_role?: string;
    folder_id?: string;
    section_ref?: string;
    document_id?: string;
    // Punch-list f4 — visit linkage.
    visit_id?: string;
    /** Explicit Internal/External override — omit to fall back to the
     *  server's role-based default (see deriveOrigin server-side). */
    origin?: QueryOrigin;
  }) =>
    api<{ query_id: string; status: 'open'; raised_at: string }>(
      '/compliance/queries',
      { method: 'POST', body: input },
    ),

  respondQuery: (query_id: string) =>
    api<{ query_id: string; status: 'answered' }>(
      '/compliance/queries/respond',
      { method: 'POST', body: { query_id } },
    ),

  closeQuery: (query_id: string) =>
    api<{ query_id: string; status: 'closed' }>(
      '/compliance/queries/close',
      { method: 'POST', body: { query_id } },
    ),

  reopenQuery: (input: { query_id: string; reason: string }) =>
    api<{ query_id: string; status: 'open'; comment_id: string }>(
      '/compliance/queries/reopen',
      { method: 'POST', body: input },
    ),

  addComment: (input: { query_id: string; body: string }) =>
    api<{ comment_id: string; query_id: string; kind: 'reply' | 'reopen' | 'status_change'; created_at: string }>(
      '/compliance/queries/comments',
      { method: 'POST', body: input },
    ),

  listComments: (query_id: string) =>
    api<{ rows: QueryCommentRow[]; count: number }>(
      `/compliance/queries/${encodeURIComponent(query_id)}/comments`,
    ),

  overview: (filters: { study_id?: string; site_id?: string } = {}) =>
    api<ComplianceOverviewResponse>(`/compliance/overview${qs(filters)}`),

  // Phase 4 extended contract — dashboard read models.
  queryAging: (filters: { study_id?: string; site_id?: string } = {}) =>
    api<{
      buckets: Array<{ key: 'lt_3d' | '3_7d' | '7_14d' | 'gt_14d'; label: string; count: number }>;
      total: number;
      generated_at: string;
    }>(`/compliance/queries/aging${qs(filters)}`),

  studyOpenQueriesDelta: (studyId: string) =>
    api<{
      study_id: string;
      open_count: number;
      weekly_delta: number | null;
      snapshot_date: string | null;
    }>(`/studies/${encodeURIComponent(studyId)}/open-queries-with-delta`),

  studySubjectOpenQueryCounts: (studyId: string) =>
    api<{
      study_id: string;
      counts: Array<{ subject_id: string; open_count: number }>;
    }>(`/studies/${encodeURIComponent(studyId)}/subjects/open-query-counts`),
};

export interface QueryCommentRow {
  comment_id: string;
  query_id: string;
  author_user_id: string;
  /** Resolved server-side on list responses — see raised_by_name's note. */
  author_name?: string | null;
  author_role_at_time: string | null;
  body: string;
  created_at: string;
  comment_kind: 'reply' | 'reopen' | 'status_change';
}

// =====================================================================
// Subjects + PHI (DREAM-14)
// =====================================================================

/**
 * Stored subject status. Mirrors the backend's statusGate.ts; labels and legal
 * transitions live in lib/subjectStatus.ts, NOT in humanise.ts (see that file).
 *
 * `consented` was removed on 2026-10-14 — the client's vocabulary has no slot
 * for it and the 3 rows holding it were migrated to `enrolled`.
 */
export type SubjectStatus =
  | 'screening'
  | 'screened'
  | 'screen_fail'
  | 'enrolled'
  | 'active'
  | 'withdrawn'
  | 'completed';

export interface SubjectRow {
  subject_id: string;
  study_id: string;
  site_id: string;
  external_ref: string;
  /**
   * Subject IVRF reference from the IVRS/IWRS system, or null when the
   * vendor has not issued one. Optional on the type as well as in the data
   * because GET /subjects/{id} returns it under `wizard_state`, not here.
   */
  ivrf_number?: string | null;
  status: SubjectStatus;
  re_consent_required: boolean;
  created_at: string;
}

// VisitRow is defined later in the DREAM-21 block as a superset of the
// Sprint-1 shape (Sprint-1 fields kept for backwards compat).

export interface SubjectPhiResponse {
  fields: Record<string, string | undefined>;
  redactedFields: string[];
  unmaskedFields: string[];
}

/**
 * Who handled the subject's consent, from the latest consent record
 * (16 Oct 2026). Staff names and the signed time only: the server never
 * sends the subject's personal details for this.
 */
export interface SubjectConsentSummary {
  consent_record_id: string;
  signed_at: string | null;
  obtained_by_name: string | null;
  signed_by_name: string | null;
}

export interface SubjectDetailResponse {
  subject: SubjectRow & { updated_at: string };
  wizard_state: SubjectWizardState;
  phi: SubjectPhiResponse | null;
  visits: VisitRow[];
  consent_summary?: SubjectConsentSummary | null;
  blinded?: boolean;
  blinded_role_master_ref?: string | null;
  /** Punch-list DT2-BLIND-4 — drives whether "Emergency Unblind" is shown. */
  study_blinding_type?: StudyBlindingType | null;
}

export interface PhiGrantRow {
  grant_id: string;
  study_id: string;
  site_id: string;
  monitor_user_id: string;
  field_keys: string[];
  granted_at: string;
  granted_by_user_id: string;
  revoked_at: string | null;
}

export interface SubjectWizardState {
  /**
   * Subject IVRF reference from the IVRS/IWRS system. Optional — the vendor
   * often has not issued one at screening. Replaced `screening_number` on
   * 2026-10-14; distinct from `reference_number` (SUB-0001, minted by us)
   * and from the randomization number.
   */
  ivrf_number?: string | null;
  /** Year of birth only — a full DOB lives on the consent document, not here. */
  date_of_birth_year?: number | null;
  screening_outcome?: 'pass' | 'fail' | null;
  screening_notes?: string | null;
  screened_at?: string | null;
  active_consent_record_id?: string | null;
  demographics_sex?: string | null;
  demographics_gender?: string | null;
  demographics_height_ft?: number | null;
  demographics_height_in?: number | null;
  demographics_weight_lbs?: number | null;
  demographics_ethnicity?: string | null;
  demographics_race?: string | null;
  demographics_preferred_language?: string | null;
  demographics_recorded_at?: string | null;
  randomization_id?: string | null;
  enrolled_at?: string | null;
}

// Sprint-6 — wizard step actions
export const subjectsWizard = {
  recordScreening: (
    subjectId: string,
    body: { outcome: 'pass' | 'fail'; notes?: string; ivrf_number?: string | null },
  ) =>
    api<{ ok: true; subject_id: string; outcome: 'pass' | 'fail' }>(
      `/subjects/${encodeURIComponent(subjectId)}/screening`,
      { method: 'POST', body },
    ),
  recordDemographics: (
    subjectId: string,
    body: {
      sex?: string | null;
      gender?: string | null;
      height_ft?: number | null;
      height_in?: number | null;
      weight_lbs?: number | null;
      ethnicity?: string | null;
      race?: string | null;
      preferred_language?: string | null;
      date_of_birth_year?: number | null;
    },
  ) =>
    api<{ ok: true; subject_id: string }>(`/subjects/${encodeURIComponent(subjectId)}/demographics`, {
      method: 'POST',
      body,
    }),
  // Phase 1 Item 4 — upsert subject email + phone into ctms_subject_phi.
  // At least one field must be non-undefined. Backend enforces E.164 for
  // phone and RFC-shaped format for email. Blank string clears the field.
  recordContactInfo: (
    subjectId: string,
    body: { email_personal?: string; phone_e164?: string },
  ) =>
    api<{
      ok: true;
      subject_id: string;
      fields_updated: string[];
      has_email: boolean;
      has_phone: boolean;
    }>(`/subjects/${encodeURIComponent(subjectId)}/contact-info`, {
      method: 'POST',
      body,
    }),
  updateProfile: (
    subjectId: string,
    body: {
      full_name?: string;
      preferred_name?: string;
      date_of_birth?: string;
      address_street?: string;
      address_city?: string;
      address_state?: string;
      address_zip?: string;
      address_country?: string;
      highlights?: string;
      /**
       * NOT PHI. Lives on ctms_subjects and is written with updateSubject
       * server-side; it rides this endpoint only so the profile page keeps
       * one save button. Send '' to clear it.
       */
      ivrf_number?: string;
    },
  ) =>
    api<{ ok: true; subject_id: string; fields_updated: string[] }>(
      `/subjects/${encodeURIComponent(subjectId)}/profile`,
      { method: 'POST', body },
    ),
  enroll: (
    subjectId: string,
    body: {
      randomization_id?: string;
      baseline_date?: string;
    },
  ) =>
    api<{
      ok: true;
      subject_id: string;
      /**
       * 'screened' since 2026-10-15 — this step finalises screening and no
       * longer enrols. Read it rather than assuming: the field is here so the
       * caller never has to hard-code where the subject landed.
       */
      status: SubjectStatus;
      randomization_id?: string | null;
      baseline?: string;
      visits_generated: number;
      visits_skipped: number;
      /** Was `already_enrolled`; the step no longer enrols anybody. */
      already_finalised?: boolean;
    }>(`/subjects/${encodeURIComponent(subjectId)}/enroll`, {
      method: 'POST',
      body,
    }),
};

/**
 * The signed-in user's own permissions at their active site.
 *
 * `modules` is module -> verbs held (read is always included where any verb
 * is). `registered_modules` names the modules the RBAC grid actually governs
 * today — everything else is still decided by the hard-coded tables, and the
 * client must not treat "absent from modules" as "denied" for those.
 */
export interface MyPermissions {
  site_id: string;
  role: string;
  /**
   * module -> verbs held. A UNION across the operations in each cell, so it
   * answers "should this module appear in the left panel?" and NOT "may they
   * press this button?". Use `operations` for the latter.
   */
  modules: Record<string, string[]>;
  /** The specific operation keys held, e.g. 'studies.create'. */
  operations: string[];
  registered_modules: string[];
  module_meta: Record<string, { label: string; nav_path: string | null }>;
}

export const myPermissions = {
  get: () => api<MyPermissions>('/me/permissions'),
};

/**
 * RBAC operations — the ENFORCED half of /admin/rbac.
 *
 * Each row is an operation the backend guards with `requireOperation`, so a
 * change saved here genuinely changes who the API lets through. The page-level
 * grid on the same screen is still advisory; these are not.
 */
export interface RbacOperation {
  operation_key: string;
  /** The (module, verb) cell of the grid this operation belongs to. */
  module: string;
  verb: string;
  /** What the code ships — what "revert" restores. */
  default_roles: string[];
  /** What is in force right now, at this site. */
  effective_roles: string[];
  overridden: boolean;
  /**
   * Roles the grid must NOT offer for this operation, mapped to why.
   *
   * A cell grants one gate; where a second gate refuses the same role
   * unconditionally, ticking it does nothing. Computed server-side from the
   * gates' own constants so the UI cannot drift from what is enforced.
   */
  ungrantable?: Record<string, string>;
}

/**
 * RBAC is per SITE. Both calls default to the caller's active site; pass
 * `siteId` only to inspect or edit another one.
 */
export const rbacOperations = {
  list: (siteId?: string) =>
    api<{ site_id: string; operations: RbacOperation[]; count: number }>(
      `/admin/rbac/operations${siteId ? `?site_id=${encodeURIComponent(siteId)}` : ''}`,
    ),
  /**
   * Sending an operation's default roles reverts it — the server deletes the
   * override rather than storing a copy of the default.
   *
   * The server also expands the save: granting any verb grants read on that
   * module, so `applied` can name more operations than were sent.
   *
   * SENT IN CHUNKS, DELIBERATELY. Caspio serialises writes to a table at about
   * 0.25s each however wide the handler fans out, so a grid-wide save of ~110
   * rows ran past the 29s API Gateway ceiling. The gateway then returned its
   * own error instead of the handler's JSON, which surfaced as a bare "could
   * not apply the changes" with no server message behind it. Chunking keeps
   * every request well inside the ceiling, so save size stops mattering.
   *
   * Chunks go one after another rather than in parallel — they write to the
   * same table, so racing them would only buy contention. Each chunk is
   * applied on arrival; a failure part-way leaves the earlier chunks in force
   * and throws, which is the same partial-application behaviour a single
   * oversized request already had.
   */
  save: async (changes: Array<{ operation_key: string; roles: string[] }>, siteId?: string) => {
    const CHUNK = 30;
    type Applied = { operation_key: string; roles: string[]; action: string };
    const applied: Applied[] = [];
    let site_id = siteId ?? '';
    let applied_at = new Date().toISOString();

    for (let i = 0; i < changes.length; i += CHUNK) {
      const slice = changes.slice(i, i + CHUNK);
      const res = await api<{
        site_id: string;
        applied: Applied[];
        count: number;
        applied_at: string;
      }>('/admin/rbac/operations', {
        method: 'PUT',
        body: siteId ? { site_id: siteId, changes: slice } : { changes: slice },
      });
      applied.push(...res.applied);
      site_id = res.site_id;
      applied_at = res.applied_at;
    }

    return { site_id, applied, count: applied.length, applied_at };
  },
};

/**
 * Subject clinical data — medical history and concomitant medications
 * (2026-10-08). Both are study-scoped source data: the backend gates them
 * exactly like the rest of the subject surface (admins blocked, approved
 * DoA delegation required).
 *
 * `is_ongoing` is a real boolean on the wire in both directions; the server
 * handles the fact that Caspio stores it as the string 'true' / 'false'.
 */
export type ConditionStatus = 'resolved' | 'ongoing' | 'intermittent';

export interface MedicalHistoryRow {
  history_id: string;
  subject_id: string;
  condition_verbatim: string;
  meddra_code: string | null;
  condition_status: ConditionStatus;
  onset_date: string | null;
  resolution_date: string | null;
  created_at: string;
  updated_at: string;
}

export interface ConmedRow {
  conmed_id: string;
  subject_id: string;
  medication_name_verbatim: string;
  whodrug_code: string | null;
  indication_text: string;
  dose_amount: string;
  frequency_route: string;
  start_date: string | null;
  end_date: string | null;
  is_ongoing: boolean;
  created_at: string;
  updated_at: string;
}

export const subjectClinical = {
  listMedicalHistory: (subjectId: string) =>
    api<{ rows: MedicalHistoryRow[]; count: number }>(
      `/subjects/${encryptId(subjectId)}/medical-history`,
    ),
  createMedicalHistory: (
    subjectId: string,
    body: {
      condition_verbatim: string;
      condition_status: ConditionStatus;
      meddra_code?: string;
      onset_date?: string;
      resolution_date?: string;
    },
  ) =>
    api<MedicalHistoryRow>(`/subjects/${encryptId(subjectId)}/medical-history`, {
      method: 'POST',
      body,
    }),
  updateMedicalHistory: (
    subjectId: string,
    historyId: string,
    body: Partial<{
      condition_verbatim: string;
      condition_status: ConditionStatus;
      meddra_code: string | null;
      onset_date: string | null;
      resolution_date: string | null;
    }>,
  ) =>
    api<MedicalHistoryRow>(
      `/subjects/${encryptId(subjectId)}/medical-history/${encodeURIComponent(historyId)}`,
      { method: 'PATCH', body },
    ),
  deleteMedicalHistory: (subjectId: string, historyId: string) =>
    api<{ history_id: string; deleted: true }>(
      `/subjects/${encryptId(subjectId)}/medical-history/${encodeURIComponent(historyId)}`,
      { method: 'DELETE' },
    ),

  listConmeds: (subjectId: string) =>
    api<{ rows: ConmedRow[]; count: number }>(`/subjects/${encryptId(subjectId)}/conmeds`),
  createConmed: (
    subjectId: string,
    body: {
      medication_name_verbatim: string;
      indication_text: string;
      dose_amount: string;
      frequency_route: string;
      is_ongoing: boolean;
      whodrug_code?: string;
      start_date?: string;
      end_date?: string;
    },
  ) => api<ConmedRow>(`/subjects/${encryptId(subjectId)}/conmeds`, { method: 'POST', body }),
  updateConmed: (
    subjectId: string,
    conmedId: string,
    body: Partial<{
      medication_name_verbatim: string;
      indication_text: string;
      dose_amount: string;
      frequency_route: string;
      is_ongoing: boolean;
      whodrug_code: string | null;
      start_date: string | null;
      end_date: string | null;
    }>,
  ) =>
    api<ConmedRow>(`/subjects/${encryptId(subjectId)}/conmeds/${encodeURIComponent(conmedId)}`, {
      method: 'PATCH',
      body,
    }),
  deleteConmed: (subjectId: string, conmedId: string) =>
    api<{ conmed_id: string; deleted: true }>(
      `/subjects/${encryptId(subjectId)}/conmeds/${encodeURIComponent(conmedId)}`,
      { method: 'DELETE' },
    ),
};

export const subjects = {
  list: (filters: {
    study_id?: string;
    site_id?: string;
    status?: string;
    re_consent?: 'yes' | 'no';
    page?: number;
    pageSize?: number;
    limit?: number;
  } = {}) =>
    api<{ rows: SubjectRow[]; count: number; total: number; page: number; pageSize: number; has_more: boolean }>(
      `/subjects${qs(filters)}`,
    ),
  create: (input: {
    study_id: string;
    /**
     * Phase 2 Defect 2.4 — external_ref is server-minted as SUB-####.
     * Any client-supplied value is IGNORED (accepted for backward
     * compatibility during rollout; will be dropped in a later cleanup).
     */
    external_ref?: string;
    status?: SubjectStatus;
    re_consent_required?: boolean;
    phi?: Record<string, string>;
  }) =>
    api<SubjectRow>('/subjects', { method: 'POST', body: input }),
  detail: (subject_id: string) =>
    api<SubjectDetailResponse>(`/subjects/${encryptId(subject_id)}`),
  /** Structured JSON export of the subject (profile, visits, forms, queries). */
  exportJson: (subject_id: string) =>
    api<Record<string, unknown>>(`/subjects/${encryptId(subject_id)}/export`),
  /**
   * CTMS-005 — Drive the subject status state machine.
   * Allowed edges: screening->enrolled|withdrawn, enrolled->withdrawn|completed.
   * Withdrawn / completed are terminal. Same-status calls return no_op=true.
   */
  transitionStatus: (
    subject_id: string,
    body: { to: SubjectStatus; reason?: string },
  ) =>
    api<{ ok: true; status: SubjectStatus; no_op: boolean }>(
      `/subjects/${encodeURIComponent(subject_id)}/status`,
      { method: 'POST', body },
    ),

  /** Punch-list DT2-BLIND-4 — record an emergency unblinding event. */
  emergencyUnblind: (subject_id: string, body: { reason: string }) =>
    api<{ subject_id: string; occurred_at: string; sponsor_notified: boolean }>(
      `/subjects/${encodeURIComponent(subject_id)}/emergency-unblind`,
      { method: 'POST', body },
    ),
};

export const phi = {
  listGrants: (filters: { study_id?: string; monitor_user_id?: string; site_id?: string } = {}) =>
    api<{ rows: PhiGrantRow[]; count: number }>(`/phi/grants${qs(filters)}`),
  grant: (input: { monitor_user_id: string; study_id: string; site_id: string; field_keys: string[] }) =>
    api<{ grant_id: string; granted_at: string; field_keys: string[] }>('/phi/grants', {
      method: 'POST',
      body: input,
    }),
  revoke: (grant_id: string) =>
    api<{ grant_id: string; status: 'revoked' | 'already_revoked'; revoked_at?: string }>(
      '/phi/grants/revoke',
      { method: 'POST', body: { grant_id } },
    ),
  toggleRemoteView: (input: { enabled: boolean; study_id: string; site_id: string; reason?: string }) =>
    api<{ ok: true; enabled: boolean }>('/phi/remote-view/toggle', { method: 'POST', body: input }),
};

// =====================================================================
// Studies
// =====================================================================

/**
 * 7-stage study lifecycle (Sprint-2 DREAM-17). Legacy values (planning|paused|closed)
 * may still appear on un-backfilled rows; humanStatus() handles both.
 */
export type StudyLifecycleStage =
  | 'draft'
  | 'initiation'
  | 'active'
  | 'closed_to_enrollment'
  | 'follow_up'
  | 'completed'
  | 'archived';

export interface StudyRow {
  study_id: string;
  site_id: string;
  protocol_number: string;
  sponsor: string;
  title: string;
  status: StudyLifecycleStage | 'planning' | 'paused' | 'closed';
  phase: string | null;
  therapeutic_area: string | null;
  planned_start_date: string | null;
  planned_end_date: string | null;
  enrollment_target: number;
  enrollment_actual: number;
  activation_progress_pct: number;
  pi_user_id: string | null;
  /** Resolved at query time from ctms_users.full_name. */
  pi_name?: string | null;
  created_at: string;
  updated_at: string;
  /** CTMS-010 — Study Lock columns. Present on rows that have been locked. */
  locked_at?: string | null;
  locked_by?: string | null;
  lock_reason?: string | null;
  /** Punch-list f9 — free-text identifier as used in the sponsor's own systems. */
  sponsor_study_id?: string | null;
  /** Punch-list DT2-BLIND-1 — trial blinding design, selected at study creation. */
  blinding_type?: StudyBlindingType | null;
  /**
   * Feedback (2026-09-29) — 'paper' exempts the study from needing a
   * signature layout on the ICF to activate. Null/absent = electronic.
   */
  consent_mode?: 'paper' | 'electronic' | null;
  binder_template_id?: string | null;
  unblinded_binder_template_id?: string | null;
}

/** Punch-list DT2-BLIND-1. */
export type StudyBlindingType = 'open_label' | 'single_blind' | 'double_blind' | 'triple_blind';
export const isBlindedStudyType = (t: StudyBlindingType | null | undefined): boolean =>
  !!t && t !== 'open_label';

export type BinderTemplateBlindingScope = 'blinded' | 'unblinded';
export type BinderBlindingScope = 'open' | BinderTemplateBlindingScope;

/**
 * DREAM-17 portfolio paginated/filtered/searched query. Accepts either
 * a single 7-stage value (Jawahar AC8: filter tabs reflect each stage)
 * OR a bucket alias ('onboarding' = Draft+Initiation, 'closeout' = Closed+Follow-up+Completed)
 * used by the 4 summary tiles' click-through.
 */
export type PortfolioLifecycleFilter =
  | 'all'
  | StudyLifecycleStage
  | 'onboarding'
  | 'closeout';

export interface PortfolioParams {
  /** Single stage, comma-separated multi-stages, or 'all'. */
  lifecycle?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}

export interface PortfolioResponse {
  rows: StudyRow[];
  total: number;
  page: number;
  pageSize: number;
  has_more: boolean;
}

/** DREAM-17 summary tiles + funnel. */
export type FunnelMetric =
  | 'total_screened' | 'in_screening' | 'active'
  | 'screen_failed' | 'randomized' | 'dropped' | 'completed';
export type FunnelCounts = Record<FunnelMetric, number>;

export interface PortfolioSummaryResponse {
  tiles: {
    total: number;
    active: number;
    onboarding: number;
    closeout: number;
    archived: number;
  };
  funnel: FunnelCounts;
  window: { key: string; from: string | null; to: string | null };
}

export type StudyLinkCategory = 'lms' | 'randomization' | 'regulatory' | 'other';

export interface StudyLinkRow {
  link_id: string;
  study_id: string | null;
  site_id: string;
  label: string;
  url: string;
  category: StudyLinkCategory;
  created_at: string;
  created_by: string;
}

/**
 * Sprint-18 (B) — active staff delegations for a study, joined with user
 * profiles. Powers the Add-Staff picker on the DoA grid editor.
 */
export interface DelegatedStudyUser {
  user_id: string;
  full_name: string;
  email: string;
  role: string;
  effective_from: string;
  effective_to: string | null;
}

/** Punch-list f11 — full sponsor detail (company info + optional base64 logo). */
export interface SponsorDetailRow {
  sponsor_id: string;
  name: string;
  reference_number: string;
  address: string | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  website: string | null;
  logo_data_uri: string | null;
}

export const studies = {
  list: () => api<{ rows: StudyRow[]; count: number }>('/studies'),

  /**
   * Punch-list f8 — post-onboarding edit for a study's non-identity
   * fields. protocol_number/title/sponsor are intentionally not
   * accepted (locked core fields).
   */
  update: (
    studyId: string,
    body: Partial<{
      phase: string | null;
      therapeutic_area: string | null;
      planned_start_date: string | null;
      planned_end_date: string | null;
      enrollment_target: number;
      enrollment_target_site: number;
      sponsor_study_id: string | null;
      blinding_type: StudyBlindingType | null;
      consent_mode: 'paper' | 'electronic' | null;
      /** Changeable until the study's binder exists (server-checked). */
      binder_template_id: string | null;
      unblinded_binder_template_id: string | null;
    }>,
  ) =>
    api<{ study: StudyRow }>(`/studies/${encodeURIComponent(studyId)}`, {
      method: 'PATCH',
      body,
    }),

  /**
   * Sprint-18 (B) — active delegations on the study, joined with user
   * profiles. Use to populate user / role pickers that need to scope to
   * the study's known staff (e.g. DoA Add-Staff dialog).
   */
  listDelegatedUsers: (studyId: string) =>
    api<{ study_id: string; users: DelegatedStudyUser[] }>(
      `/studies/${encodeURIComponent(studyId)}/delegated-users`,
    ),

  /** Returns sorted sponsor names from the ctms_sponsors table, plus lightweight rows (no logo) for admin editing. */
  listSponsors: () =>
    api<{ sponsors: string[]; rows: Array<{ sponsor_id: string; name: string; reference_number: string }> }>(
      '/studies/sponsors',
    ),

  /** Persist a new sponsor immediately. Idempotent — returns existing if name matches (case-insensitive). */
  createSponsor: (name: string) =>
    api<{ sponsor: { sponsor_id: string; name: string }; created: boolean }>(
      '/studies/sponsors',
      { method: 'POST', body: { name } },
    ),

  /**
   * Punch-list f11 — full sponsor detail (company info + logo) by
   * case-insensitive name match. `ctms_studies.sponsor` is a plain string,
   * not a foreign key, so Study Detail resolves this way.
   */
  sponsorDetail: (name: string) =>
    api<{ sponsor: SponsorDetailRow }>(`/studies/sponsors/detail${qs({ name })}`),

  /** Punch-list f11 — edit a sponsor's company detail + optional logo. */
  updateSponsor: (
    sponsorId: string,
    body: Partial<{
      address: string | null;
      contact_name: string | null;
      contact_email: string | null;
      contact_phone: string | null;
      website: string | null;
      logo_data_uri: string | null;
    }>,
  ) =>
    api<{ sponsor: SponsorDetailRow }>(
      `/studies/sponsors/${encodeURIComponent(sponsorId)}`,
      { method: 'PATCH', body },
    ),

  /** DREAM-17 — paginated portfolio (site-scoped). */
  portfolio: (params: PortfolioParams = {}) =>
    api<PortfolioResponse>(`/studies/portfolio${qs(params as Record<string, string | number | undefined>)}`),

  /** DREAM-17 — tile counts + aggregate enrollment funnel for the report window. */
  portfolioSummary: (params: { window?: 'this_week' | 'last_week' | 'weekly' | 'monthly' | 'custom'; from?: string; to?: string } = {}) =>
    api<PortfolioSummaryResponse>(`/studies/portfolio-summary${qs(params as Record<string, string | number | undefined>)}`),

  /** CTMS-010 — stamp the study into read-only mode. */
  lock: (studyId: string, body: { reason: string }) =>
    api<{ ok: true; locked_at: string }>(
      `/studies/${encodeURIComponent(studyId)}/lock`,
      { method: 'POST', body },
    ),

  /** CTMS-010 — clear the lock. Platform admin only on the backend. */
  unlock: (studyId: string, body: { reason: string }) =>
    api<{ ok: true }>(
      `/studies/${encodeURIComponent(studyId)}/unlock`,
      { method: 'POST', body },
    ),
};

// ===========================================================================
// Sprint-20 — Site Dashboard aggregations
// ===========================================================================

export interface StudiesCompliantResponse {
  /** Studies whose binder is fully complete (every Core folder has a current version). */
  compliant: number;
  /** Studies at the active site that have an instantiated binder. */
  total: number;
}

export const dashboard = {
  /**
   * Backs the "Studies Compliant" tile on the Site Dashboard (eISF folder-tree AC11).
   * Rolls up per-folder completeness across every study the active site is on.
   */
  studiesCompliant: () => api<StudiesCompliantResponse>('/dashboard/studies-compliant'),
};

// ===========================================================================
// DREAM-18 — Study Onboarding 5-Step Wizard
// ===========================================================================

export type OnboardingStepNumber = 1 | 2 | 3 | 4 | 5;
export type OnboardingStepStatus = 'not_started' | 'in_progress' | 'completed';

export interface OnboardingStepRow {
  progress_id: string;
  study_site_id: string;
  step: OnboardingStepNumber;
  step_status: OnboardingStepStatus;
  step_payload: string;
  completed_at: string | null;
  completed_by: string | null;
  updated_at: string;
}

export interface StudySiteRow {
  study_site_id: string;
  study_id: string;
  site_id: string;
  status: StudyLifecycleStage | 'planning' | 'paused' | 'closed';
  enrollment_target_site: number | null;
  enrollment_actual_site: number;
  readiness_econsent: boolean;
  readiness_ebinder: boolean;
  readiness_staff: boolean;
  readiness_esource: boolean;
  activated_at: string | null;
  activated_by: string | null;
  // 2026-07-28 Feedback #9b - PI approval gate on study activation.
  pi_approved_at?: string | null;
  pi_approved_by?: string | null;
  pi_approval_notes?: string | null;
  created_at: string;
  created_by: string;
  updated_at: string;
}

export type ReadinessFlagKey = 'econsent' | 'ebinder' | 'staff' | 'esource';

/**
 * DREAM-18 dynamic readiness — each flag is derived server-side from real
 * study data (protocol uploads, team assignments, consent docs, eSource
 * configuration). The `reason` is a human-readable explanation of why the
 * flag is currently Ready or Not ready, and `evidence` is the underlying
 * raw data the BE used to compute the value (useful for a "View evidence"
 * disclosure in the UI).
 */
export interface DerivedFlag {
  value: boolean;
  reason: string;
  evidence: Record<string, unknown>;
}

export type ActivationGateReason = string;

export interface OnboardingContext {
  study: StudyRow;
  study_site: StudySiteRow;
  progress: OnboardingStepRow[];
  /** DREAM-18 dynamic readiness — derived from real data, not manual toggles. */
  readiness: {
    econsent: DerivedFlag;
    ebinder: DerivedFlag;
    staff: DerivedFlag;
    esource: DerivedFlag;
  };
  activation: {
    canActivate: boolean;
    reasons: ActivationGateReason[];
  };
}

/** pre_screening/unscheduled/ad_hoc default to non-billable but can be overridden — billability is contractual, not locked to category. */
export type VisitCategory = 'screening' | 'pre_screening' | 'scheduled' | 'unscheduled' | 'ad_hoc' | 'follow_up';

export interface VisitTemplateEntry {
  visit_name: string;
  visit_order: number;
  nominal_day: number;
  window_minus_days: number;
  window_plus_days: number;
  required_visit_type?: string;
  /** Billable rate captured during onboarding. */
  visit_category?: VisitCategory;
  is_billable?: boolean;
  /** Per-subject rate — multiplied by completed-subject-count. */
  billable_amount_cents?: number | null;
  /**
   * Client feedback 2026-08-11 — flat fee paid to the site once per
   * invoice/revenue run for this visit type, independent of subject
   * count. Tracked separately, never merged into billable_amount_cents.
   */
  fixed_fee_cents?: number | null;
}

export interface Step2Payload {
  protocol_number?: string;
  title?: string;
  sponsor?: string;
  phase?: string;
  therapeutic_area?: string;
  planned_start_date?: string;
  planned_end_date?: string;
  /** Global cap across all sites. Writes to ctms_studies.enrollment_target. */
  enrollment_target?: number;
  /**
   * Per-site cap (Jawahar DREAM-18: "per site and global"). When omitted,
   * BE falls back to the global enrollment_target value.
   */
  enrollment_target_site?: number;
  visit_template?: VisitTemplateEntry[];
  /** Punch-list f9 — free-text identifier as used in the sponsor's own systems. */
  sponsor_study_id?: string;
}

export const onboarding = {
  /** GET full wizard context for a study at the caller's active site. */
  get: (studyId: string) =>
    api<OnboardingContext>(`/studies/${encodeURIComponent(studyId)}/onboarding`),

  /** POST a partial step payload (Save & Resume Later). */
  saveDraft: (studyId: string, step: OnboardingStepNumber, payload: unknown) =>
    api<{ ok: true; step: OnboardingStepNumber }>(
      `/studies/${encodeURIComponent(studyId)}/onboarding/draft`,
      { method: 'POST', body: { step, payload } },
    ),

  /** POST to mark a step complete; cannot skip earlier steps. */
  completeStep: (studyId: string, step: OnboardingStepNumber, payload?: unknown) =>
    api<{ ok: true; step: OnboardingStepNumber; completed_at: string }>(
      `/studies/${encodeURIComponent(studyId)}/onboarding/complete-step`,
      { method: 'POST', body: { step, payload } },
    ),

  /** Flip one readiness flag on the study-site (eConsent / eBinder / Staff / eSource). */
  setReadiness: (studyId: string, flag: ReadinessFlagKey, value: boolean) =>
    api<{ ok: true; flag: ReadinessFlagKey; value: boolean; updated: number }>(
      `/studies/${encodeURIComponent(studyId)}/readiness`,
      { method: 'POST', body: { flag, value } },
    ),

  /** POST activation transition. 403 if the activation gate refuses. */
  activate: (studyId: string) =>
    api<{ ok: true; study_site_id: string; status: 'active' }>(
      `/studies/${encodeURIComponent(studyId)}/activate`,
      { method: 'POST', body: {} },
    ),

  /**
   * 2026-07-28 Feedback #9b - Principal Investigator signs off on
   * study_site activation. 2026-07-30 - no longer gates the Activate
   * button (activationGate no longer has a piApproved check); kept as a
   * standalone PI acknowledgment action.
   */
  piApprove: (studyId: string, body: { notes?: string } = {}) =>
    api<{
      ok: true;
      study_site_id: string;
      pi_approved_at: string;
      pi_approved_by: string;
      already_approved: boolean;
    }>(`/studies/${encodeURIComponent(studyId)}/pi-approve`, { method: 'POST', body }),

  /**
   * POST a lifecycle transition beyond Active.
   * Allowed: active -> closed_to_enrollment -> follow_up -> completed -> archived.
   */
  transitionLifecycle: (studyId: string, target: 'closed_to_enrollment' | 'follow_up' | 'completed' | 'archived') =>
    api<{ ok: true; study_site_id: string; status: StudyLifecycleStage }>(
      `/studies/${encodeURIComponent(studyId)}/lifecycle`,
      { method: 'POST', body: { target } },
    ),
};

// =====================================================================
// Study staff assignments (onboarding wizard Staff card)
// =====================================================================

export interface StaffAssignment {
  delegation_id: string;
  user_id: string;
  full_name: string;
  email: string;
  role: string;
  /** False when deactivated or removed from the site (still assigned on file). */
  active?: boolean;
}

export interface StaffCandidate {
  user_id: string;
  full_name: string;
  email: string;
}

export const STUDY_STAFF_ROLES = [
  'principal_investigator',
  'sub_investigator',
  'coordinator',
  'regulatory_specialist',
  'site_pharma',
  'finance_billing',
  'monitor',
  'auditor',
] as const;

export type StudyStaffRole = (typeof STUDY_STAFF_ROLES)[number];

export const studyStaff = {
  list: (studyId: string) =>
    api<{
      assignments: StaffAssignment[];
      candidates: StaffCandidate[];
      /** Monitors/auditors on Monitor Assignments — not staff; New Training can pick them by name. */
      reviewers?: Array<StaffCandidate & { role: string }>;
    }>(
      `/studies/${encodeURIComponent(studyId)}/staff`,
    ),

  assign: (
    studyId: string,
    body: {
      user_id: string;
      role: string;
      /** Punch-list DT2-BLIND-3 — set blinding status at assignment time. Omit to leave untouched. */
      blinded?: boolean;
    },
  ) =>
    api<StaffAssignment & { blinded: boolean | null }>(`/studies/${encodeURIComponent(studyId)}/staff`, {
      method: 'POST',
      body,
    }),

  revoke: (studyId: string, delegationId: string) =>
    api<{ ok: boolean; delegation_id: string }>(
      `/studies/${encodeURIComponent(studyId)}/staff/${encodeURIComponent(delegationId)}`,
      { method: 'DELETE' },
    ),
};

/**
 * Phase 1 Item 9 — per-study DoA responsibility subset.
 * GET returns catalog + subset + is_default; PUT atomically replaces
 * the subset. Empty codes[] clears the opt-in (study inherits full
 * active catalog again).
 */
export interface StudyDoaResponsibilitiesResponse {
  study_id: string;
  catalog: Array<{
    responsibility_id: string;
    code: string;
    label: string;
    definition: string;
    sort_order: number;
  }>;
  subset: string[];
  is_default: boolean;
}

export const studyDoaResponsibilities = {
  get: (studyId: string) =>
    api<StudyDoaResponsibilitiesResponse>(
      `/studies/${encodeURIComponent(studyId)}/doa-responsibilities`,
    ),
  put: (studyId: string, body: { codes: string[] }) =>
    api<{ study_id: string; codes: string[]; is_default: boolean; saved: number }>(
      `/studies/${encodeURIComponent(studyId)}/doa-responsibilities`,
      { method: 'PUT', body },
    ),
};

/** DREAM-18 — POST /studies creates a draft study + study_site for the active site.
 *  protocol_number is server-minted when omitted (see [[auto-generate-identifiers]]). */
export const studiesCreate = (body: {
  protocol_number?: string;
  title: string;
  sponsor: string;
  phase?: string;
  therapeutic_area?: string;
  planned_start_date?: string;
  planned_end_date?: string;
  enrollment_target?: number;
  enrollment_target_site?: number;
  // F8c - eISF binder template (FR-ISF-3). Omit to let the BE fall back
  // to the seeded default template.
  binder_template_id?: string;
  unblinded_binder_template_id?: string;
  /** Punch-list f9 — free-text identifier as used in the sponsor's own systems. */
  sponsor_study_id?: string;
  /** Punch-list DT2-BLIND-1 — trial blinding design. */
  blinding_type?: StudyBlindingType;
}) =>
  api<{ study: StudyRow; study_id: string }>('/studies', {
    method: 'POST',
    body,
  });

// F8c - lightweight typed helper the wizard uses to list active
// templates for the picker without pulling in the whole adminBinderTemplates
// namespace (site-facing role wouldn't have those permissions).
export const listActiveBinderTemplates = () =>
  api<{
    templates: Array<{
      template_id: string;
      reference_number: string;
      name: string;
      description: string;
      is_default: 'true' | 'false';
      blinding_scope: BinderTemplateBlindingScope;
      /** CR-2026-09-29 — unmapped mandatory documents, if any. */
      readiness?: { ready: boolean; missing: Array<{ code: string; name: string }> };
    }>;
  }>('/binder-templates/active');

// ===========================================================================
// DREAM-19 — Protocol Document Upload (inside DREAM-18 step 2)
// ===========================================================================

export type ProtocolChecksumStatus = 'pending' | 'verified' | 'failed';
export type ProtocolProcessingState = 'in_progress' | 'completed' | 'needs_attention';

export interface ProtocolDocumentRow {
  document_id: string;
  study_site_id: string;
  library_scope: 'Study Library';
  category: 'Protocol and Amendments';
  sub_category: 'Protocol';
  file_name: string;
  file_size: number;
  mime_type: string;
  checksum_sha256: string | null;
  checksum_status: ProtocolChecksumStatus;
  s3_bucket: string;
  s3_key: string;
  s3_version_id: string | null;
  version_label: string;
  processing_state: ProtocolProcessingState;
  retry_attempt: number;
  last_error: string | null;
  uploaded_at: string;
  uploaded_by: string;
}

export const protocol = {
  /** Returns a presigned PUT URL + reserved document_id; FE uploads, then calls finalise. */
  presign: (studyId: string, body: { file_name: string; mime_type: string }) =>
    api<{
      document_id: string;
      upload_url: string;
      s3_bucket: string;
      s3_key: string;
      study_site_id: string;
    }>(`/studies/${encodeURIComponent(studyId)}/protocol/presign`, { method: 'POST', body }),

  /** Record the completed upload; enqueues server-side checksum job. */
  finalise: (studyId: string, body: {
    document_id: string;
    s3_bucket: string;
    s3_key: string;
    file_name: string;
    file_size: number;
    mime_type: string;
    client_sha256?: string;
  }) =>
    api<{
      document_id: string;
      version_label: string;
      processing_state: ProtocolProcessingState;
      previous_version_id: string | null;
    }>(`/studies/${encodeURIComponent(studyId)}/protocol/finalise`, { method: 'POST', body }),

  /** Polled status for the upload card (every ~5 s). */
  status: (studyId: string, docId: string) =>
    api<{ document: ProtocolDocumentRow }>(
      `/studies/${encodeURIComponent(studyId)}/protocol/${encodeURIComponent(docId)}`,
    ),

  /** Returns every retained version (newest first). */
  versions: (studyId: string) =>
    api<{ versions: ProtocolDocumentRow[]; count: number }>(
      `/studies/${encodeURIComponent(studyId)}/protocol/versions`,
    ),

  /** Returns a 15-minute presigned GET URL for a protocol document. */
  download: (studyId: string, docId: string) =>
    api<{ document_id: string; file_name: string; version_label: string; url: string; expires_in_seconds: number }>(
      `/studies/${encodeURIComponent(studyId)}/protocol/${encodeURIComponent(docId)}/download`,
    ),

  // ---- 2026-09-28 — protocol amendment approval workflow ----------------
  listAmendments: (studyId: string) =>
    api<ProtocolAmendmentsResponse>(`/studies/${encodeURIComponent(studyId)}/protocol/amendments`),

  submitAmendment: (studyId: string, body: {
    document_id: string;
    s3_bucket: string;
    s3_key: string;
    file_name: string;
    file_size: number;
    mime_type: string;
    client_sha256?: string;
    amendment_number: string;
    amendment_date: string;
    summary_of_changes: string;
    amendment_reason: string;
    impact: ProtocolAmendmentImpact;
  }) =>
    api<{
      document_id: string;
      version_label: string;
      approval_status: 'pending_review';
      supersedes_document_id: string;
      approval_task_id: string | null;
      pi_assigned: boolean;
    }>(`/studies/${encodeURIComponent(studyId)}/protocol/amendments`, { method: 'POST', body }),

  approveAmendment: (studyId: string, docId: string, body: {
    re_auth_receipt: string;
    re_auth_method: ReAuthMethod;
    approval_notes?: string;
  }) =>
    api<{
      document_id: string;
      approval_status: 'approved';
      approved_at: string;
      binder_version_id: string | null;
      training_assigned: number;
      review_tasks_created: number;
      review_tasks: string[];
    }>(`/studies/${encodeURIComponent(studyId)}/protocol/amendments/${encodeURIComponent(docId)}/approve`, { method: 'POST', body }),

  rejectAmendment: (studyId: string, docId: string, body: { rejection_reason: string }) =>
    api<{ document_id: string; approval_status: 'rejected'; rejected_at: string }>(
      `/studies/${encodeURIComponent(studyId)}/protocol/amendments/${encodeURIComponent(docId)}/reject`,
      { method: 'POST', body },
    ),

  acknowledgeAmendmentTask: (studyId: string, docId: string, taskId: string, body: {
    outcome: 'updated' | 'no_change_needed';
    note: string;
  }) =>
    api<{ task_id: string; status: 'resolved'; amendment_closed_at: string | null }>(
      `/studies/${encodeURIComponent(studyId)}/protocol/amendments/${encodeURIComponent(docId)}/tasks/${encodeURIComponent(taskId)}/acknowledge`,
      { method: 'POST', body },
    ),
};

export interface ProtocolAmendmentImpactArea { changed: boolean; note?: string }
export interface ProtocolAmendmentImpact {
  schedule_of_assessments: ProtocolAmendmentImpactArea;
  esource_forms: ProtocolAmendmentImpactArea;
  doa_responsibilities: ProtocolAmendmentImpactArea;
  informed_consent: ProtocolAmendmentImpactArea;
  eligibility: ProtocolAmendmentImpactArea;
  other?: string;
}
export type ProtocolApprovalStatus = 'pending_review' | 'approved' | 'rejected';
export interface ProtocolAmendmentReviewTask {
  task_id: string;
  kind: string;
  label: string;
  message: string;
  assignee_role: string | null;
  assignee_user_id: string | null;
  assignee_name: string | null;
  status: 'open' | 'resolved' | 'dismissed';
  outcome: 'updated' | 'no_change_needed' | null;
  acknowledgement_note: string | null;
  resolved_at: string | null;
  resolved_by_name: string | null;
  created_at: string;
}
export interface ProtocolAmendmentRow {
  document_id: string;
  reference_number: string;
  version_label: string;
  file_name: string;
  file_size: number;
  processing_state: ProtocolProcessingState;
  checksum_status: ProtocolChecksumStatus;
  uploaded_at: string;
  uploaded_by: string;
  uploaded_by_name: string | null;
  is_current: boolean;
  approval_status: ProtocolApprovalStatus | null;
  is_legacy: boolean;
  amendment_number: string | null;
  amendment_date: string | null;
  summary_of_changes: string | null;
  amendment_reason: string | null;
  impact: ProtocolAmendmentImpact | null;
  submitted_at: string | null;
  approved_at: string | null;
  approved_by_name: string | null;
  approval_notes: string | null;
  rejected_at: string | null;
  rejected_by_name: string | null;
  rejection_reason: string | null;
  closed_at: string | null;
  supersedes_document_id: string | null;
  approval_task_status: string | null;
  review_tasks: ProtocolAmendmentReviewTask[];
  open_review_tasks: number;
}
export interface ProtocolAmendmentsResponse {
  amendments: ProtocolAmendmentRow[];
  count: number;
  current_document_id: string | null;
  requires_approval: boolean;
  pi_user_id: string | null;
  pi_name: string | null;
  can_decide: boolean;
  has_pending: boolean;
}

// ===========================================================================
// DREAM-20 — Study Workspace Hub
// ===========================================================================

export interface WorkspaceTiles {
  enrolled: { count: number; target: number; site_target: number | null };
  open_queries: { count: number; weekly_change: number };
  forms_completed: { count: number; total: number; pct: number };
  upcoming_visits_7d: number;
}
export interface VisitPanelEntry {
  visit_id: string;
  subject_id: string;
  visit_name: string | null;
  planned_date: string | null;
  nominal_day: number | null;
  window_minus_days: number | null;
  window_plus_days: number | null;
  status: string | null;
  visit_type: string | null;
}
export interface TeamMember {
  user_id: string;
  role: string;
  full_name: string | null;
  email: string | null;
  /** False when deactivated or removed from the site (still assigned on file). */
  active?: boolean;
}
export interface ConfiguredFormSummary { form_id: string; name: string; version: string; status: 'draft' | 'published' }
export interface EnrollmentSeriesPoint { bucket: string; enrolled: number }

export interface WorkspaceHubResponse {
  study: StudyRow;
  study_site: StudySiteRow;
  tiles: WorkspaceTiles;
  enrollment_series: EnrollmentSeriesPoint[];
  visit_panel: VisitPanelEntry[];
  team_members: TeamMember[];
  configured_forms: ConfiguredFormSummary[];
  /** Punch-list f17 — gates the "Onboard subject" entry point. */
  doa_complete: boolean;
}

export const workspace = {
  get: (studyId: string) =>
    api<WorkspaceHubResponse>(`/studies/${encodeURIComponent(studyId)}/workspace`),
};

// ===========================================================================
// DREAM-21 — Visits (calendar + unscheduled)
// ===========================================================================

export type VisitType = 'scheduled' | 'unscheduled';
export type VisitStatus = 'planned' | 'completed' | 'missed' | 'cancelled';
export type WindowCompliance = 'in_window' | 'early' | 'late' | 'excluded';

export interface VisitRow {
  visit_id: string;
  subject_id: string;
  study_id: string;
  study_site_id?: string | null;
  visit_type?: VisitType | null;
  visit_name?: string | null;
  visit_label?: string;
  nominal_day?: number | null;
  window_minus_days?: number | null;
  window_plus_days?: number | null;
  planned_date?: string | null;
  scheduled_at?: string | null;
  actual_date?: string | null;
  completed_at?: string | null;
  status?: VisitStatus | null;
  status_reason?: string | null;
  window_compliance?: WindowCompliance | null;
  unscheduled?: boolean;
  /** Sprint-1 legacy fields kept for backwards compat. */
  signed_by_user_id?: string | null;
  signed_at?: string | null;
  /** Phase 8 AC15 — FK to visit template. */
  template_visit_id?: string | null;
  /** Phase 8 AC19 — out-of-window deviation flag. */
  flagged_deviation?: string | null;
  flagged_deviation_at?: string | null;
  flagged_deviation_days?: number | null;
  reminder_generated_at?: string | null;
  /** Phase 8 slice — PI Sign Visit lock state (unlocked / signed_pending_sdv / locked). */
  lock_state?: 'unlocked' | 'signed_pending_sdv' | 'locked' | null;
  lock_transitioned_at?: string | null;
  current_signature_id?: string | null;
}

export interface EisfDocumentNode {
  study_id: string;
  protocol_number: string;
  study_title: string;
  documents: ProtocolDocumentRow[];
}

export const eisf = {
  listDocuments: () =>
    api<{ tree: EisfDocumentNode[]; count: number }>('/eisf/documents'),
};

export const visits = {
  list: (params: {
    scope: 'site' | 'study' | 'subject';
    study_id?: string;
    subject_id?: string;
    from?: string;
    to?: string;
  }) =>
    api<{ visits: VisitRow[]; count: number }>(`/visits${qs(params as Record<string, string | number | undefined>)}`),

  createUnscheduled: (body: { study_id: string; subject_id: string; visit_date: string; reason?: string }) =>
    api<{ visit_id: string; planned_date: string; visit_type: 'unscheduled' }>(
      '/visits/unscheduled',
      { method: 'POST', body },
    ),

  getById: (visitId: string) =>
    api<{ visit: VisitRow }>(`/visits/${encodeURIComponent(visitId)}`),

  /**
   * Move an appointment inside its protocol window.
   *
   * Writes `scheduled_at` only — `planned_date` is the window's anchor and
   * stays put, so a visit cannot walk out of its own window a few days at a
   * time. The server re-checks the window; `visitWindowBounds` below is for
   * bounding the picker, not for deciding.
   */
  reschedule: (
    visitId: string,
    /** `scheduled_day` is the day as picked on the SITE's clock — the window is
     *  validated against it, because the instant's UTC day can be the next one. */
    body: { scheduled_at: string; scheduled_day?: string; reason?: string },
  ) =>
    api<{ visit_id: string; scheduled_at: string; planned_date: string | null }>(
      `/visits/${encodeURIComponent(visitId)}/reschedule`,
      { method: 'PATCH', body },
    ),

  recordActual: (visitId: string, body: { actual_date: string; notes?: string }) =>
    api<{ ok: true; visit_id: string; window_compliance: WindowCompliance; actual_date: string }>(
      `/visits/${encodeURIComponent(visitId)}/record-actual`,
      { method: 'POST', body },
    ),

  /** CTMS-004 — record visit outcome (completed/missed/cancelled). */
  setStatus: (
    visitId: string,
    body: { status: 'completed' | 'missed' | 'cancelled'; reason?: string },
  ) =>
    api<{ ok: true; visit_id: string; status: VisitStatus }>(
      `/visits/${encodeURIComponent(visitId)}/status`,
      { method: 'POST', body },
    ),
};

// =====================================================================
// DREAM39/DREAM40 — Budget & Finance
// =====================================================================

export type FinancePeriodKey = 'month' | 'year' | 'custom';

export interface FinancePeriod {
  key: FinancePeriodKey;
  from: string;
  to: string;
}

export interface StudyFinanceCardRow {
  study_id: string;
  study_reference_number: string;
  protocol_number: string | null;
  sponsor_name: string;
  pi_name: string | null;
  status: string;
  enrolled_count: number;
  completed_billable_count: number;
  invoiced_count: number;
  invoiced_amount_cents: number;
  to_be_invoiced_count: number;
  to_be_invoiced_amount_cents: number;
}

export interface InvoiceListRow {
  invoice_id: string;
  invoice_number: string;
  study_id: string;
  study_reference_number: string;
  sponsor_name: string;
  invoice_date: string;
  total_amount_cents: number;
  /** Null on revenue reports generated before the fixed-fee feature shipped. */
  subject_fee_total_cents: number | null;
  fixed_fee_total_cents: number | null;
  currency_code: string;
  visit_count: number;
  status: 'generated';
}

export interface InvoiceExclusion {
  visit_id: string;
  visit_reference: string;
  reason: 'not_billable' | 'rate_unresolved';
}

export interface FixedFeeLineItem {
  templateKey: string;
  visit_name: string;
  fixed_fee_cents: number;
}

export interface VisitBreakdownItem {
  visit_name: string;
  subject_count: number;
  per_subject_cents: number;
  fixed_fee_cents: number;
  subtotal_cents: number;
}

export interface InvoicePreview {
  eligible_count: number;
  /** Grand total = subject_fee_total_cents + fixed_fee_total_cents. */
  total_amount_cents: number;
  /** Sum of per-subject line items only. */
  subject_fee_total_cents: number;
  /** Sum of the flat per-visit-type site fees (client feedback 2026-08-11). */
  fixed_fee_total_cents: number;
  fixed_fee_line_items: FixedFeeLineItem[];
  /** One row per distinct visit type included in this run. */
  visit_breakdown: VisitBreakdownItem[];
  currency_code: string;
  excluded: InvoiceExclusion[];
}

export type InvoiceGenerateResult =
  | {
    generated: true;
    invoice_id: string;
    invoice_number: string;
    reference_number: string;
    invoice_date: string;
    format: 'pdf' | 'csv';
    visit_count: number;
    total_amount_cents: number;
    subject_fee_total_cents: number;
    fixed_fee_total_cents: number;
    currency_code: string;
    page_count: number;
    download_url: string;
    excluded: InvoiceExclusion[];
  }
  | {
    generated: false;
    reason: 'no_eligible_visits' | 'too_many_visits';
    eligible_count: number;
    excluded: InvoiceExclusion[];
  };

export interface VisitBillingDetailRow {
  visit_id: string;
  visit_reference: string;
  subject_reference: string | null;
  visit_name: string;
  visit_order: number | null;
  actual_date: string | null;
  billable: boolean;
  amount_cents: number;
  invoiced: boolean;
  invoice_number: string | null;
}

export const finance = {
  studyCards: (params: { period?: FinancePeriodKey; from?: string; to?: string } = {}) =>
    api<{ period: FinancePeriod; cards: StudyFinanceCardRow[] }>(`/finance/study-cards${qs(params)}`),

  listInvoices: (params: { study_id?: string; page?: number; page_size?: number } = {}) =>
    api<{ rows: InvoiceListRow[]; total: number; page: number; page_size: number; has_more: boolean }>(
      `/finance/invoices${qs(params)}`,
    ),

  visitBillingDetail: (params: { study_id: string; from: string; to: string }) =>
    api<{ period: FinancePeriod; rows: VisitBillingDetailRow[]; visit_breakdown: VisitBreakdownItem[] }>(
      `/finance/visit-billing-detail${qs(params)}`,
    ),

  previewInvoice: (params: { study_id: string; period_from: string; period_to: string }) =>
    api<InvoicePreview>(`/finance/invoices/preview${qs(params)}`),

  generateInvoice: (body: { study_id: string; period_from: string; period_to: string; format?: 'pdf' | 'csv' }) =>
    api<InvoiceGenerateResult>('/finance/invoices/generate', { method: 'POST', body }),

  downloadInvoice: (invoiceId: string) =>
    api<{ download_url: string; expires_in_seconds: number }>(
      `/finance/invoices/${encodeURIComponent(invoiceId)}/download`,
    ),
};

/** DREAM-17 — Study Links panel CRUD. */
export const studyLinks = {
  list: (params: { study_id?: string } = {}) =>
    api<{ links: StudyLinkRow[]; count: number }>(`/study-links${qs(params)}`),

  create: (body: { study_id?: string; label: string; url: string; category: StudyLinkCategory }) =>
    api<{ link: StudyLinkRow }>('/study-links', { method: 'POST', body }),

  delete: (linkId: string) =>
    api<{ deleted: true; link_id: string }>(`/study-links/${encodeURIComponent(linkId)}`, { method: 'DELETE' }),
};

// =====================================================================
// Punch-list f10 — per-study configurable milestone tracking
// =====================================================================

export type StudyMilestoneStatus = 'pending' | 'completed' | 'at_risk' | 'missed';

export interface StudyMilestoneRow {
  milestone_id: string;
  study_id: string;
  reference_number: string;
  milestone_name: string;
  target_date: string | null;
  actual_date: string | null;
  status: StudyMilestoneStatus;
  notes: string | null;
  sort_order: number;
  created_at: string;
  created_by: string;
  updated_at: string;
  updated_by: string;
}

export const studyMilestones = {
  list: (studyId: string) =>
    api<{ rows: StudyMilestoneRow[]; count: number }>(
      `/studies/${encodeURIComponent(studyId)}/milestones`,
    ),

  create: (
    studyId: string,
    body: { milestone_name: string; target_date?: string; status?: StudyMilestoneStatus; notes?: string },
  ) =>
    api<{ milestone: StudyMilestoneRow }>(
      `/studies/${encodeURIComponent(studyId)}/milestones`,
      { method: 'POST', body },
    ),

  update: (
    studyId: string,
    milestoneId: string,
    body: Partial<{
      milestone_name: string;
      target_date: string | null;
      actual_date: string | null;
      status: StudyMilestoneStatus;
      notes: string | null;
      sort_order: number;
    }>,
  ) =>
    api<{ milestone: StudyMilestoneRow }>(
      `/studies/${encodeURIComponent(studyId)}/milestones/${encodeURIComponent(milestoneId)}`,
      { method: 'PATCH', body },
    ),

  delete: (studyId: string, milestoneId: string) =>
    api<{ deleted: true; milestone_id: string }>(
      `/studies/${encodeURIComponent(studyId)}/milestones/${encodeURIComponent(milestoneId)}`,
      { method: 'DELETE' },
    ),
};

// =====================================================================
// CSR export (DREAM-15)
// =====================================================================

export type CsrJobStatus = 'queued' | 'running' | 'ready' | 'failed' | 'expired';

export interface CsrJobRow {
  job_id: string;
  study_id: string;
  site_id: string;
  status: CsrJobStatus;
  pii_included: boolean;
  requested_by_user_id: string;
  requested_at: string;
  file_size_bytes: number | null;
  expires_at: string | null;
  completed_at: string | null;
  error_message: string | null;
  download_available: boolean;
}

export const csr = {
  create: (input: {
    study_id: string;
    include_phi: boolean;
    reauth_password?: string;
    /** CTMS-009 — strip PHI columns + tokenise external_ref. */
    deidentified?: boolean;
  }) =>
    api<{ job_id: string }>('/csr/exports', { method: 'POST', body: input }),
  list: () => api<{ rows: CsrJobRow[]; count: number }>('/csr/exports'),
  status: (job_id: string) => api<CsrJobRow>(`/csr/exports/${encodeURIComponent(job_id)}`),
  download: (input: { job_id: string; reauth_password?: string }) =>
    api<{
      job_id: string;
      download_url: string | null;
      file_b64: string | null;
      file_size_bytes: number | null;
      expires_at: string | null;
    }>(`/csr/exports/${encodeURIComponent(input.job_id)}/download`, {
      method: 'POST',
      body: { job_id: input.job_id, ...(input.reauth_password ? { reauth_password: input.reauth_password } : {}) },
    }),
};

export const sites = {
  // DREAM-9 Sprint-1: no role in body. Server picks the granted role from
  // the caller's role (platform/application_admin → site_admin;
  // site_admin → monitor). Strict schema returns 400 on any extra field.
  invite: (
    siteId: string,
    body: { email: string; full_name?: string; role?: string; study_role?: string; study_ids?: string[] },
  ) =>
    api<{ invite_id: string; site_id: string; role: string; expires_at_iso: string; studies_assigned?: number }>(
      `/sites/${encodeURIComponent(siteId)}/invitations`,
      { method: 'POST', body },
    ),
};

// =====================================================================
// DREAM-8 — Platform Admin (Sites CRUD + Users + Audit + Health)
// =====================================================================

export interface AdminSiteRow {
  site_id: string;
  tenant_id: string;
  name: string;
  reference_number?: string;
  status: 'active' | 'inactive' | 'onboarding';
  s3_bucket_name: string;
  region: string;
  time_zone?: string | null;
  created_at: string;
  updated_at: string;
}

export interface AdminUserSite {
  site_id: string;
  role: string | null;
}

export interface AdminUserRow {
  user_id: string;
  email: string;
  full_name: string;
  status: 'invited' | 'active' | 'locked' | 'disabled';
  mfa_enrolled: boolean;
  email_verified_at: string | null;
  locked_at: string | null;
  lock_reason: string | null;
  last_login_at: string | null;
  created_at: string;
  created_by_user_id: string | null;
  /** Cross-tenant role, sourced from _platform_role_global delegation. Null for site-only users. */
  platform_role: string | null;
  sites: AdminUserSite[];
}

export interface AdminAuditRow {
  audit_id: string;
  timestamp: string;
  user_id: string | null;
  user_role_at_time: string | null;
  site_id: string | null;
  study_id: string | null;
  action: string;
  resource_type: string | null;
  resource_id: string | null;
  field_changed: string | null;
  old_value: string | null;
  new_value: string | null;
  ip: string | null;
  source: 'web' | 'api' | 'system' | 'cli';
}

export interface AdminLambdaHealth {
  name: string;
  invocations: number;
  errors: number;
  error_rate: number;
}

export interface AdminSystemHealth {
  stage: string;
  region: string;
  generated_at: string;
  caspio: { ok: boolean; latency_ms: number | null; error?: string };
  metrics_available: boolean;
  window_seconds: number;
  lambdas: AdminLambdaHealth[];
}

export interface BlindingRow {
  blinding_id: string;
  user_id: string;
  study_id: string;
  blinded_role_master_ref: string | null;
  /**
   * 'unblinded_staff' = explicitly UNBLINDED on the study (f88e1b9, 22 Sep
   * 2026); 'blinded' or empty (pre-designation rows) = blinded.
   */
  status?: 'blinded' | 'unblinded_staff' | '' | null;
  reason: string | null;
  active_from: string;
  active_to: string | null;
  created_at: string;
  created_by: string;
  updated_at: string;
  updated_by: string;
  active: boolean;
  user_name?: string | null;
  user_email?: string | null;
}

export interface BlindingListRow extends BlindingRow {
  study_reference_number: string | null;
  study_protocol_number: string | null;
  study_title: string | null;
}

export const adminBlinding = {
  list: (params?: { studyId?: string }) => {
    const qs = new URLSearchParams();
    if (params?.studyId) qs.set('study_id', params.studyId);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return api<{ blindings: BlindingListRow[]; active_count: number; total_count: number }>(
      `/admin/blinding${suffix}`,
    );
  },
  listByStudy: (studyId: string) =>
    api<{ study_id: string; blindings: BlindingRow[]; active_count: number }>(
      `/admin/blinding/by-study/${encodeURIComponent(studyId)}`,
    ),
  listByUser: (userId: string) =>
    api<{ user_id: string; blindings: BlindingRow[]; active_count: number }>(
      `/admin/blinding/by-user/${encodeURIComponent(userId)}`,
    ),
  assign: (body: {
    user_id: string;
    study_id: string;
    blinded_role_master_ref?: string;
    reason?: string;
    active_from?: string;
  }) =>
    api<{ blinding: BlindingRow }>('/admin/blinding', { method: 'POST', body }),
  /**
   * Feedback (2026-09-29) — the only way an Investigator on a blinded study
   * becomes unblinded. Requires a reason and an explicit expiry; the routine
   * paths (Assign Staff, DoA grid) refuse to unblind a PI or SI at all.
   * Revoke early with `update(id, { end_now: true })`.
   */
  emergencyUnblind: (body: {
    user_id: string;
    study_id: string;
    reason: string;
    /** ISO datetime the unblinding expires. */
    active_to: string;
  }) =>
    api<{ blinding: BlindingRow; expires_at: string }>('/admin/blinding/emergency-unblind', {
      method: 'POST',
      body,
    }),
  update: (
    blindingId: string,
    body: {
      end_now?: boolean;
      active_to?: string | null;
      blinded_role_master_ref?: string | null;
      reason?: string | null;
    },
  ) =>
    api<{ blinding: BlindingRow }>(
      `/admin/blinding/${encodeURIComponent(blindingId)}`,
      { method: 'PATCH', body },
    ),
};

export const adminSites = {
  list: () => api<{ sites: AdminSiteRow[] }>('/sites'),
  create: (body: { name: string; region?: string; tenant_id?: string; admin_email?: string; time_zone?: string }) =>
    api<{ site_id: string; tenant_id: string; s3_bucket_name: string; region: string }>(
      '/sites', { method: 'POST', body },
    ),
  rename: (siteId: string, name: string) =>
    api<{ site_id: string; name: string }>(
      `/sites/${encodeURIComponent(siteId)}`, { method: 'PATCH', body: { name } },
    ),
  update: (siteId: string, body: { name?: string; time_zone?: string }) =>
    api<{ site_id: string; name: string; time_zone: string | null }>(
      `/sites/${encodeURIComponent(siteId)}`, { method: 'PATCH', body },
    ),
  setStatus: (siteId: string, action: 'enable' | 'disable') =>
    api<{ site_id: string; status: string; changed: boolean }>(
      `/sites/${encodeURIComponent(siteId)}/${action}`, { method: 'POST', body: {} },
    ),
  remove: (siteId: string, confirm_name: string) =>
    api<{ site_id: string; deleted: boolean }>(
      `/sites/${encodeURIComponent(siteId)}`, { method: 'DELETE', body: { confirm_name } },
    ),
};

export const adminUsers = {
  list: () => api<{ rows: AdminUserRow[]; count: number }>('/admin/users'),
  /**
   * Create a user with a chosen role. Used by the topbar + Team-page invite
   * modals when a Platform Admin picks "Platform Admin" from the role
   * dropdown — that path can't go through /sites/{id}/invitations because
   * the resulting user has no site context. For "Site Admin" picks the FE
   * still uses `sites.invite()` so the site_id ends up on the invitation.
   */
  create: (body: {
    email: string;
    full_name?: string;
    role: string;
    site_id?: string;
    send_invite?: boolean;
  }) =>
    api<{
      user_id: string;
      email: string;
      is_new_user: boolean;
      role: string;
      site_id: string | null;
      invitation?: { invite_id: string; token: string; expires_at_iso: string };
      reset?: { token_id: string; token: string; expires_at_iso: string };
    }>('/admin/users', { method: 'POST', body }),
  lock: (userId: string, reason?: string) =>
    api<{ user_id: string; locked: true }>(
      `/admin/users/${encodeURIComponent(userId)}/lock`,
      { method: 'POST', body: { reason } },
    ),
  unlock: (userId: string) =>
    api<{ ok: true; user_id: string }>('/admin/users/unlock', {
      method: 'POST', body: { user_id: userId },
    }),
  changeRole: (userId: string, site_id: string, role: string) =>
    api<{ user_id: string; site_id: string; role: string; changed: boolean }>(
      `/admin/users/${encodeURIComponent(userId)}/role`,
      { method: 'POST', body: { site_id, role } },
    ),
  /**
   * Direct-assign an existing user to a site (no invite email).
   * Counterpart to the invite flow — used by Admin > Users and the Site
   * Detail > Members tab for already-onboarded users.
   */
  addSite: (userId: string, body: { site_id: string; role: string }) =>
    api<{ user_id: string; email: string; site_id: string; site_name: string; role: string }>(
      `/admin/users/${encodeURIComponent(userId)}/sites`,
      { method: 'POST', body },
    ),
  removeSite: (userId: string, site_id: string) =>
    api<{ user_id: string; site_id: string; removed: boolean }>(
      `/admin/users/${encodeURIComponent(userId)}/sites/${encodeURIComponent(site_id)}`,
      { method: 'DELETE' },
    ),
  /** Sprint-1 follow-up: admin-driven MFA reset for stale-authenticator cases. */
  resetMfa: (userId: string) =>
    api<{ user_id: string; mfa_reset: boolean }>(
      `/admin/users/${encodeURIComponent(userId)}/reset-mfa`,
      { method: 'POST', body: {} },
    ),
  /** Soft-disable (status=disabled). Different from lock (lock is policy-driven). */
  disable: (userId: string) =>
    api<{ user_id: string; status: string; changed: boolean }>(
      `/admin/users/${encodeURIComponent(userId)}/disable`,
      { method: 'POST', body: {} },
    ),
  enable: (userId: string) =>
    api<{ user_id: string; status: string; changed: boolean }>(
      `/admin/users/${encodeURIComponent(userId)}/enable`,
      { method: 'POST', body: {} },
    ),
  /** Hard-delete (cascade). Existing endpoint; client wrapper for completeness. */
  remove: (userId: string, confirm_email: string) =>
    api<{ user_id: string; email: string; deleted: Record<string, number> }>(
      `/admin/users/${encodeURIComponent(userId)}`,
      { method: 'DELETE', body: { confirm_email } },
    ),
};

/**
 * Convenience helper: send a password-reset email to a user. Wraps the
 * public /auth/forgot-password endpoint so the Admin > Users "Reset
 * password" action goes through the same flow the user would use from
 * the login screen (the API rate-limits + audits).
 */
export const adminPasswordReset = {
  send: (email: string) =>
    api<{ message: string }>('/auth/forgot-password', {
      method: 'POST',
      body: { email },
    }),
};

export const adminAudit = {
  list: (q: {
    site_id?: string; study_id?: string; user_id?: string; action?: string;
    from?: string; to?: string; page?: number; limit?: number;
  } = {}) => {
    const params = new URLSearchParams();
    Object.entries(q).forEach(([k, v]) => { if (v !== undefined && v !== '') params.set(k, String(v)); });
    const qs = params.toString();
    return api<{ rows: AdminAuditRow[]; page: number; limit: number; total: number; has_more: boolean }>(
      `/admin/audit${qs ? `?${qs}` : ''}`,
    );
  },
};

export const adminHealth = {
  get: () => api<AdminSystemHealth>('/admin/system-health'),
};

// =====================================================================
// Sprint-15 — eISF Master Data (platform_admin only)
//
// Manages `ctms_document_type_definitions` (the catalog) plus the
// per-type approver junction (`ctms_document_type_approvers`) that drives
// multi-approver routing. Add/edit/retire types; downstream binders
// inherit immediately. Approvers are submitted as an inline list on
// create and update — the BE replaces the junction atomically.
// =====================================================================

export type ApprovalMode = 'single' | 'parallel' | 'sequential';

export interface DocumentTypeApproverRow {
  approver_id: string;
  document_type_code: string;
  /** Role enum value — must match the `Role` union on the BE. */
  role: string;
  sequence_order: number;
  signature_meaning: string;
  created_at: string;
  created_by: string;
}

/** Inline shape for create / update payloads — no IDs, BE generates them. */
export interface DocumentTypeApproverInput {
  role: string;
  sequence_order: number;
  signature_meaning: string;
}

export interface DocumentTypeRow {
  document_type_def_id: string;
  document_type_name: string;
  document_type_code: string;
  definition: string;
  lifecycle: string;
  who_can_create_json: string;
  can_create_site_admin: boolean;
  can_create_site_staff: boolean;
  required_fields_json: string;
  create_new_draft_versions: string;
  create_new_draft_versions_bool: boolean;
  draft_state_workflows_json: string;
  steady_state_workflows_json: string;
  can_monitors_view: string;
  can_monitors_view_bool: boolean;
  monitor_view_conditions: string | null;
  site_viewer_can_download: boolean;
  monitor_can_download: boolean;
  shareable_via_myveeva: boolean;
  naming_format_template: string;
  duplicate_check_enabled: boolean;
  docuseal_template_id: string | null;
  approval_mode: ApprovalMode;
  any_of_threshold: number | null;
  /**
   * Phase 1 Item 11 AC11 — when true, uploads of this document type
   * spawn a pending training certification per assigned study staff
   * member (see /training page + BE finaliseVersion wire-up).
   */
  training_required?: boolean | null;
  /**
   * Training module Phase 1 — JSON array of study-role codes this
   * training applies to. Null/empty = every delegate on the study
   * (legacy behaviour). Raw string as stored; write side sends a real
   * array — see `DocumentTypeEditable`.
   */
  training_required_roles?: string | null;
  source_notes: string | null;
  source_version: string;
  is_active: boolean;
  // 2026-07-28 Feedback #17/#18/#6 - catalog scope. Legacy rows may be
  // null (treated as 'platform').
  scope?: CatalogScope | null;
  owner_site_id?: string | null;
  owner_study_id?: string | null;
  /** F5 - persisted as 'true'|'false' by the server; null on legacy rows. */
  visible_to_site_users?: string | null;
  /** Persisted as 'true'|'false' by the server; null on legacy rows. */
  is_mandatory?: string | null;
  /**
   * Explicit FK to BINDER_SECTIONS[].code (in-repo DIA taxonomy). Required
   * on new rows via the Zod schema; nullable on legacy rows (they show up
   * in the "Unmapped" bucket in the eISF Catalog for manual assignment).
   */
  parent_section_code?: string | null;
  created_at: string;
  created_by: string;
  updated_at: string;
}

export type CatalogScope = 'platform' | 'site' | 'study';

/** Type rows returned by the list endpoint carry their approver junction inline. */
export interface DocumentTypeWithApprovers extends DocumentTypeRow {
  approvers: DocumentTypeApproverRow[];
}

/** Fields a platform admin can edit on an existing row. */
export type DocumentTypeEditable = Pick<
  DocumentTypeRow,
  | 'document_type_name'
  | 'definition'
  | 'lifecycle'
  | 'who_can_create_json'
  | 'can_create_site_admin'
  | 'can_create_site_staff'
  | 'required_fields_json'
  | 'create_new_draft_versions'
  | 'draft_state_workflows_json'
  | 'steady_state_workflows_json'
  | 'can_monitors_view'
  | 'monitor_view_conditions'
  | 'site_viewer_can_download'
  | 'monitor_can_download'
  | 'shareable_via_myveeva'
  | 'approval_mode'
  | 'any_of_threshold'
  | 'training_required'
  | 'source_notes'
  | 'is_active'
  | 'parent_section_code'
> & {
  visible_to_site_users?: boolean;
  is_mandatory?: boolean;
  /** Real Role[] array on write; server JSON-encodes it to storage. */
  training_required_roles?: string[] | null;
};

export interface AdminListDocumentTypesResponse {
  document_types: DocumentTypeWithApprovers[];
  total: number;
  active: number;
  inactive: number;
}

export interface AdminGetDocumentTypeResponse {
  document_type: DocumentTypeRow;
  approvers: DocumentTypeApproverRow[];
}

// =====================================================================
// Sprint-16 — DoA Master Data (platform_admin only)
//
// The canonical vocabulary of delegable responsibilities (the columns of
// the DoA grid). Junction `ctms_doa_row_delegations` references each by
// the immutable `code` natural key.
// =====================================================================

export interface DoaResponsibilityRow {
  responsibility_id: string;
  code: string;
  label: string;
  definition: string;
  sort_order: number;
  is_active: boolean;
  // Feedback #17/#18/#6 - catalog scope. Legacy rows may be null
  // (treated as 'platform').
  scope?: CatalogScope | null;
  owner_site_id?: string | null;
  owner_study_id?: string | null;
  /** F5 - server persists as 'true'|'false'. Null on legacy = hidden. */
  visible_to_site_users?: string | null;
  created_at: string;
  created_by: string;
  updated_at: string;
  updated_by: string;
}

export type DoaResponsibilityEditable = Pick<
  DoaResponsibilityRow,
  'label' | 'definition' | 'sort_order' | 'is_active'
> & {
  // F5 - client sends boolean; server persists as 'true'|'false' STRING.
  visible_to_site_users?: boolean;
};

export interface AdminListResponsibilitiesResponse {
  responsibilities: DoaResponsibilityRow[];
  total: number;
  active: number;
  inactive: number;
}

// =====================================================================
// Sprint-17 — DoA grid editor surface
// =====================================================================

export interface DoaGridResponsibility {
  responsibility_id: string;
  code: string;
  label: string;
  definition: string;
  sort_order: number;
}

export type DoaQualificationStatus =
  | 'qualified'
  | 'doc_expiring'
  | 'qualification_gap'
  | 'no_docs_on_file';

export interface DoaQualifyingDoc {
  document_id: string;
  reference_number: string | null;
  document_type_name: string;
  status: string;
  slot_expires_at: string | null;
  status_label: 'current' | 'expiring' | 'expired' | 'no_expiry';
}

export interface DoaGridRow {
  doa_row_id: string;
  staff_user_id: string;
  staff_full_name: string;
  staff_email: string;
  role: string;
  row_status: 'pending_signature' | 'signed' | 'revoked';
  effective_from: string;
  effective_to: string | null;
  delegated_codes: string[];
  legacy_free_text: string | null;
  qualification: {
    user_id: string;
    status: DoaQualificationStatus;
    qualifying_docs: DoaQualifyingDoc[];
  };
  /** Punch-list DT2-BLIND-5 — null when the study isn't blinded, or when no designation has been chosen yet. */
  blinded: boolean | null;
}

export interface DoaGridResponse {
  /**
   * Sprint-18 — null when no DoA version exists yet for the binder.
   * FE renders an empty-state with a "Create first DoA" CTA in this case.
   */
  version: {
    doa_version_id: string;
    reference_number: string | null;
    binder_id: string;
    version_number: number;
    status: string;
    effective_at: string | null;
    pi_signed_at: string | null;
  } | null;
  is_editable: boolean;
  responsibilities: DoaGridResponsibility[];
  rows: DoaGridRow[];
  /** Sprint-18 — true when the caller can POST /doa/versions to create the first DoA. */
  can_create: boolean;
  is_blinded_study: boolean;
}

export const adminDoa = {
  listResponsibilities: (opts: { include_inactive?: boolean; scope?: CatalogScope } = {}) => {
    const params: string[] = [];
    if (opts.include_inactive) params.push('include_inactive=true');
    if (opts.scope) params.push(`scope=${encodeURIComponent(opts.scope)}`);
    const qs = params.length ? `?${params.join('&')}` : '';
    return api<AdminListResponsibilitiesResponse>(`/admin/doa/responsibilities${qs}`);
  },
  getResponsibility: (code: string) =>
    api<{ responsibility: DoaResponsibilityRow }>(
      `/admin/doa/responsibilities/${encodeURIComponent(code)}`,
    ),
  createResponsibility: (
    body: DoaResponsibilityEditable & {
      code: string;
      // Feedback #17/#18/#6 - scope selection. Site admins get 'site'
      // default with auto-owner_site_id; platform admins get 'platform'.
      scope?: CatalogScope;
      owner_site_id?: string;
      owner_study_id?: string;
    },
  ) =>
    api<{ responsibility: DoaResponsibilityRow }>(
      '/admin/doa/responsibilities',
      { method: 'POST', body },
    ),
  updateResponsibility: (code: string, patch: Partial<DoaResponsibilityEditable>) =>
    api<{ responsibility: DoaResponsibilityRow }>(
      `/admin/doa/responsibilities/${encodeURIComponent(code)}`,
      { method: 'PATCH', body: patch },
    ),
  deactivateResponsibility: (code: string) =>
    api<{ ok: true; code: string }>(
      `/admin/doa/responsibilities/${encodeURIComponent(code)}`,
      { method: 'DELETE' },
    ),
};

/**
 * F5 - site_user views. Both endpoints return the site-scoped whitelist
 * of catalog rows that the platform / site admin has ticked
 * visible_to_site_users. Every call writes an audit row.
 */
export const siteUser = {
  siteDocumentation: () =>
    api<{
      site_id: string;
      count: number;
      document_types: Array<{
        document_type_code: string;
        document_type_name: string;
        definition: string;
        lifecycle: string;
      }>;
    }>('/site-user/site-documentation'),
  staffDocumentation: () =>
    api<{
      site_id: string;
      count: number;
      responsibilities: Array<{
        code: string;
        label: string;
        definition: string;
        sort_order: number;
      }>;
    }>('/site-user/staff-documentation'),
};

export const adminEisf = {
  listDocumentTypes: (opts: { include_inactive?: boolean; scope?: CatalogScope } = {}) => {
    const params: string[] = [];
    if (opts.include_inactive) params.push('include_inactive=true');
    if (opts.scope) params.push(`scope=${encodeURIComponent(opts.scope)}`);
    const qs = params.length ? `?${params.join('&')}` : '';
    return api<AdminListDocumentTypesResponse>(`/admin/eisf/document-types${qs}`);
  },
  getDocumentType: (code: string) =>
    api<AdminGetDocumentTypeResponse>(
      `/admin/eisf/document-types/${encodeURIComponent(code)}`,
    ),
  createDocumentType: (
    body: Omit<DocumentTypeEditable, 'parent_section_code'> & {
      document_type_code?: string;
      naming_format_template?: string;
      approvers: DocumentTypeApproverInput[];
      scope?: CatalogScope;
      owner_site_id?: string;
      owner_study_id?: string;
      /**
       * Existing FK OR omit when supplying `new_section` inline.
       * Server enforces exactly-one-of {parent_section_code, new_section}.
       */
      parent_section_code?: string;
      /**
       * Inline-create a custom zone as part of the same POST. Platform
       * admin only. Server rolls back on any downstream failure so
       * orphan zones don't accumulate.
       */
      new_zone?: { name: string };
      new_section?: {
        name: string;
        artifact_type?: string;
        inclusion?: 'core' | 'recommended';
        parent_zone_name?: string;
      };
    },
  ) =>
    api<{ document_type: DocumentTypeRow; approvers: DocumentTypeApproverRow[] }>(
      '/admin/eisf/document-types',
      { method: 'POST', body },
    ),
  updateDocumentType: (
    code: string,
    patch: Partial<DocumentTypeEditable> & { approvers?: DocumentTypeApproverInput[] },
  ) =>
    api<{ document_type: DocumentTypeRow; approvers: DocumentTypeApproverRow[] }>(
      `/admin/eisf/document-types/${encodeURIComponent(code)}`,
      { method: 'PATCH', body: patch },
    ),
  deactivateDocumentType: (code: string) =>
    api<{ ok: true; code: string }>(
      `/admin/eisf/document-types/${encodeURIComponent(code)}`,
      { method: 'DELETE' },
    ),
  bulkSetParentSection: (body: { codes: string[]; parent_section_code: string }) =>
    api<{
      target_section_code: string;
      results: Array<{ document_type_code: string; ok: boolean; error?: string }>;
      updated: number;
      failed: number;
    }>('/admin/eisf/document-types/bulk-parent-section', { method: 'POST', body }),
  getTaxonomy: () =>
    api<{
      zones: Array<{
        zone: string;
        sortOrder: number;
        /**
         * `true` for runtime custom zones (ctms_custom_zones).
         * DIA reference zones are `false`. Optional on the wire so
         * legacy callers keep working; treat missing as false.
         */
        isCustom?: boolean;
        sections: Array<{
          code: string;
          name: string;
          artifactType: string;
          inclusion: 'core' | 'recommended';
          subartifacts: string[];
          /** `true` for runtime custom sections; optional on the wire. */
          isCustom?: boolean;
        }>;
      }>;
    }>(`/admin/eisf/taxonomy`),

  /**
   * 2026-07-28 - one-shot backfill that mirrors historical source records
   * (Blank ICF / Signed ICF / CSR / DoA log) into their eISF binder
   * slots. Idempotent, safe to re-run. platform_admin / application_admin.
   */
  backfillMirrors: () =>
    api<{
      blank_icf: BackfillCounters;
      signed_icf: BackfillCounters;
      clinical_study_report: BackfillCounters;
      doa_log: BackfillCounters;
      duration_ms: number;
    }>(`/admin/eisf/backfill-mirrors`, { method: 'POST', body: {} }),
};

// ---------------------------------------------------------------------------
// Custom eISF taxonomy - platform-scoped runtime add-ons to the DIA reference.
// ---------------------------------------------------------------------------

export interface CustomZoneRow {
  custom_zone_id: string;
  name: string;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  created_by: string;
  updated_at: string;
  updated_by: string;
}

export interface CustomSectionRow {
  custom_section_id: string;
  /** Auto-minted `custom.<n>` slug; the FK target for parent_section_code. */
  code: string;
  parent_zone_name: string;
  name: string;
  artifact_type: string;
  inclusion: 'core' | 'recommended';
  sort_order: number;
  is_active: boolean;
  created_at: string;
  created_by: string;
  updated_at: string;
  updated_by: string;
}

export const adminCustomTaxonomy = {
  // Zones
  listZones: (opts: { include_inactive?: boolean } = {}) => {
    const qs = opts.include_inactive ? '?include_inactive=true' : '';
    return api<{ zones: CustomZoneRow[]; total: number; active: number; inactive: number }>(
      `/admin/eisf/custom-taxonomy/zones${qs}`,
    );
  },
  createZone: (body: { name: string; sort_order?: number }) =>
    api<{ zone: CustomZoneRow }>('/admin/eisf/custom-taxonomy/zones', {
      method: 'POST',
      body,
    }),
  updateZone: (
    id: string,
    patch: { name?: string; sort_order?: number; is_active?: boolean },
  ) =>
    api<{ zone: CustomZoneRow }>(
      `/admin/eisf/custom-taxonomy/zones/${encodeURIComponent(id)}`,
      { method: 'PATCH', body: patch },
    ),
  deactivateZone: (id: string) =>
    api<{ ok: true; custom_zone_id: string; is_active: false }>(
      `/admin/eisf/custom-taxonomy/zones/${encodeURIComponent(id)}`,
      { method: 'DELETE' },
    ),

  // Sections
  listSections: (opts: { include_inactive?: boolean } = {}) => {
    const qs = opts.include_inactive ? '?include_inactive=true' : '';
    return api<{
      sections: CustomSectionRow[];
      total: number;
      active: number;
      inactive: number;
    }>(`/admin/eisf/custom-taxonomy/sections${qs}`);
  },
  createSection: (body: {
    parent_zone_name: string;
    name: string;
    artifact_type: string;
    inclusion?: 'core' | 'recommended';
    sort_order?: number;
  }) =>
    api<{ section: CustomSectionRow }>('/admin/eisf/custom-taxonomy/sections', {
      method: 'POST',
      body,
    }),
  updateSection: (
    id: string,
    patch: {
      parent_zone_name?: string;
      name?: string;
      artifact_type?: string;
      inclusion?: 'core' | 'recommended';
      sort_order?: number;
      is_active?: boolean;
    },
  ) =>
    api<{ section: CustomSectionRow }>(
      `/admin/eisf/custom-taxonomy/sections/${encodeURIComponent(id)}`,
      { method: 'PATCH', body: patch },
    ),
  deactivateSection: (id: string) =>
    api<{ ok: true; custom_section_id: string; is_active: false }>(
      `/admin/eisf/custom-taxonomy/sections/${encodeURIComponent(id)}`,
      { method: 'DELETE' },
    ),
};

export interface BackfillCounters {
  seen: number;
  mirrored: number;
  skipped: number;
  failed: number;
}

// =====================================================================
// F8 - eISF Binder Template Configuration
// The default template (is_default=true) is read-only; the UI hides
// delete + folder-edit affordances for that row and the backend enforces.
// =====================================================================

export interface BinderTemplateRow {
  template_id: string;
  reference_number: string;
  name: string;
  description: string;
  is_default: 'true' | 'false';
  is_active: 'true' | 'false';
  scope: 'platform' | 'site';
  blinding_scope: BinderTemplateBlindingScope;
  owner_site_id: string | null;
  created_at: string;
  created_by: string;
  updated_at: string;
  updated_by: string;
  /** Present on the list endpoint. Absent on rows returned by create. */
  readiness?: BinderTemplateReadiness;
}

export interface BinderTemplateFolderRow {
  template_folder_id: string;
  template_id: string;
  master_folder_template_id: string | null;
  display_name_override: string | null;
  parent_template_folder_id: string | null;
  sort_order: number;
  tier?: string | null;
  /**
   * CR-2026-09-29 — JSON array of document_type_code values that auto-file
   * into this folder, e.g. `["protocol"]`. Sent and received as a string;
   * the editor reads it through parseMappedDocTypeCodes below.
   */
  mapped_doc_type_codes?: string | null;
  is_local: 'true' | 'false';
  created_at: string;
  created_by: string;
}

/** A document every binder template must route to one of its folders. */
export interface MandatoryDocument {
  code: string;
  name: string;
  zone_name: string | null;
  section_name: string | null;
  artifact_type: string | null;
  /** The folder currently holding it, or null when unmapped. */
  mapped_template_folder_id: string | null;
}

export interface BinderTemplateReadiness {
  ready: boolean;
  missing: Array<{ code: string; name: string }>;
}

/** Tolerant parse of a folder's mapped_doc_type_codes JSON. */
export function parseMappedDocTypeCodes(
  folder: Pick<BinderTemplateFolderRow, 'mapped_doc_type_codes'> | null | undefined,
): string[] {
  const raw = folder?.mapped_doc_type_codes;
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((c): c is string => typeof c === 'string') : [];
  } catch {
    return [];
  }
}

export interface MasterFolderTemplateRow {
  template_id: string;
  artifact_type: string;
  zone_name: string | null;
  section_name: string | null;
  inclusion: 'Core' | 'Recommended' | null;
  is_active: boolean;
}

export interface BinderTemplateDetail {
  template: BinderTemplateRow;
  folders: BinderTemplateFolderRow[];
  masters: MasterFolderTemplateRow[];
  mandatory_artifact_types?: string[];
  /** CR-2026-09-29 — one row per mandatory document + the folder holding it. */
  mandatory_documents?: MandatoryDocument[];
  readiness?: BinderTemplateReadiness;
}

export const adminBinderTemplates = {
  list: (opts: { include_inactive?: boolean } = {}) => {
    const qs = opts.include_inactive ? '?include_inactive=true' : '';
    return api<{ templates: BinderTemplateRow[]; not_ready?: number }>(
      `/admin/binder-templates${qs}`,
    );
  },
  get: (templateId: string) =>
    api<BinderTemplateDetail>(`/admin/binder-templates/${encodeURIComponent(templateId)}`),
  usage: (templateId: string) =>
    api<{ template_id: string; active_studies: number; sample_names: string[] }>(
      `/admin/binder-templates/${encodeURIComponent(templateId)}/usage`,
    ),
  /** Punch-list f22 — version-history log (create/rename/reactivate/folder add-remove-rename-move). */
  history: (templateId: string) =>
    api<{
      template_id: string;
      events: Array<{
        audit_id: string;
        timestamp: string;
        action: string;
        user_id: string | null;
        user_name: string | null;
        old_value: string | null;
        new_value: string | null;
      }>;
    }>(`/admin/binder-templates/${encodeURIComponent(templateId)}/history`),
  create: (body: {
    name: string;
    blinding_scope: BinderTemplateBlindingScope;
    description?: string;
    master_folder_template_ids?: string[];
    outline?: string;
    clone_from_template_id?: string;
  }) =>
    api<{
      template: BinderTemplateRow;
      folders: BinderTemplateFolderRow[];
      warnings: string[];
    }>('/admin/binder-templates', { method: 'POST', body }),
  update: (
    templateId: string,
    patch: { name?: string; description?: string; is_active?: boolean },
  ) =>
    api<{ template: BinderTemplateRow }>(
      `/admin/binder-templates/${encodeURIComponent(templateId)}`,
      { method: 'PATCH', body: patch },
    ),
  deactivate: (templateId: string) =>
    api<{ ok: true; template_id: string; is_active: false }>(
      `/admin/binder-templates/${encodeURIComponent(templateId)}`,
      { method: 'DELETE' },
    ),
  addFolder: (
    templateId: string,
    body: {
      master_folder_template_id?: string;
      display_name_override?: string;
      parent_template_folder_id?: string | null;
      sort_order?: number;
      // The backend has accepted `tier` since 2026-09-24; it was simply
      // missing here, so a new folder could never be created with one.
      tier?: 'common' | 'unblinded_only' | 'general' | null;
      mapped_doc_type_codes?: string[] | null;
    },
  ) =>
    api<{ folder: BinderTemplateFolderRow }>(
      `/admin/binder-templates/${encodeURIComponent(templateId)}/folders`,
      { method: 'POST', body },
    ),
  updateFolder: (
    templateId: string,
    folderId: string,
    patch: {
      display_name_override?: string | null;
      parent_template_folder_id?: string | null;
      sort_order?: number;
      tier?: 'common' | 'unblinded_only' | 'general' | null;
      /** Which mandatory documents auto-file here. `[]` or null clears. */
      mapped_doc_type_codes?: string[] | null;
      /** Re-point at a different standard folder; null makes it custom. */
      master_folder_template_id?: string | null;
    },
  ) =>
    api<{ folder: BinderTemplateFolderRow }>(
      `/admin/binder-templates/${encodeURIComponent(templateId)}/folders/${encodeURIComponent(folderId)}`,
      { method: 'PATCH', body: patch },
    ),
  deleteFolder: (templateId: string, folderId: string) =>
    api<{ ok: true; template_folder_id: string }>(
      `/admin/binder-templates/${encodeURIComponent(templateId)}/folders/${encodeURIComponent(folderId)}`,
      { method: 'DELETE' },
    ),
};

// =====================================================================
// DREAM-22 (eConsent) — DREAM-24 / 25 / 26
// =====================================================================

export type ConsentApprovalStatus = 'draft' | 'pending_approval' | 'approved' | 'rejected';

export interface ConsentDocumentRow {
  consent_document_id: string;
  study_id: string;
  version_label: string;
  file_name: string;
  file_size: number;
  mime_type: string;
  s3_key: string;
  is_active: boolean;
  uploaded_at: string;
  uploaded_by: string;
  activated_at: string | null;
  activated_by: string | null;
  docuseal_template_id?: string | null;
  irb_name?: string | null;
  irb_approval_date?: string | null;
  version_date?: string | null;
  // 2026-07-28 Feedback #4 - approval workflow. Pre-flow rows are
  // backfilled to 'approved' so existing versions behave unchanged.
  approval_status?: ConsentApprovalStatus | null;
  approval_submitted_at?: string | null;
  approval_submitted_by?: string | null;
  approved_at?: string | null;
  approved_by?: string | null;
  approval_notes?: string | null;
  /**
   * Phase 1 item 10 (AC3) — 'true' once the coordinator saves a
   * drag-drop layout on this version. Signing sessions REQUIRE this
   * to be 'true' before they can start; otherwise the BE refuses.
   */
  has_drag_drop_layout?: string | null;
  /** Cached from pdf-lib at upload; drives the FE layout editor. */
  page_count?: number | null;
  /** Serialised layout JSON (canonical shape parsed by fieldLayout.ts). */
  field_positions_json?: string | null;
}

export interface ConsentRecordRow {
  consent_record_id: string;
  subject_id: string;
  study_id: string;
  site_id: string;
  consent_document_id: string;
  record_type: 'initial' | 're_consent';
  subject_full_legal_name: string;
  date_of_birth: string;
  signature_meaning: string;
  docuseal_submission_id: string;
  docuseal_signed_pdf_url: string | null;
  /**
   * S3 key of the archived, known-correct flattened signed PDF (see
   * services/jobs/jobHandlers/signedConsentArchive.ts). Null until that
   * async job runs (normally seconds after signing). Use
   * consent.getSignedDocumentUrl() to view it — never
   * docuseal_signed_pdf_url directly, which points at an unauthenticated
   * DocuSeal URL that can still serve the pre-flatten template.
   */
  signed_pdf_s3_key?: string | null;
  archived_at?: string | null;
  signed_at: string | null;
  declined_at: string | null;
  old_version_label: string | null;
  new_version_label: string | null;
  superseded_by_record_id: string | null;
  // Sprint-7 ECON-003 / ECON-004 / ECON-006 — optional pending Caspio rollout.
  signer_role?: 'subject' | 'parent' | 'assenting_minor' | 'lar' | null;
  parent_consent_record_id?: string | null;
  lar_full_legal_name?: string | null;
  lar_date_of_birth?: string | null;
  lar_relationship_to_subject?: string | null;
  parent_full_legal_name?: string | null;
  parent_date_of_birth?: string | null;
  parent_relationship?: string | null;
  withdrawn_at?: string | null;
  withdrawn_by?: string | null;
  withdrawal_reason?: string | null;
  created_at?: string | null;
  /**
   * Step 2's consent-process answers (NIDCR "Documenting the Consent
   * Process"), as JSON: { documented_at, documented_by_user_id,
   * consent_method, cf_version_label, answers, evidence }. Null on records
   * created before 2026-09-28.
   */
  process_documentation_json?: string | null;
}

export interface ConsentPresignResponse {
  consent_document_id: string;
  upload_url: string;
  s3_bucket: string;
  s3_key: string;
}

export interface ConsentFinaliseResponse {
  consent_document_id: string;
  version_label: string;
  is_active: boolean;
  approval_status?: ConsentApprovalStatus;
  re_consent_flagged: number;
  queue_items_created: number;
}

export interface ConsentApprovalResponse {
  consent_document_id: string;
  version_label: string;
  is_active?: boolean;
  approval_status: ConsentApprovalStatus;
  approved_at: string;
  re_consent_flagged?: number;
}

export interface ConsentSessionResponse {
  consent_record_id: string;
  embed_url: string;
  docuseal_submission_id: string;
  version_label: string;
}

export interface SignatureQueueItem {
  queue_item_id: string;
  assignee_user_id: string;
  site_id: string;
  study_id: string;
  /** 'eisf_approval' = eISF document awaiting this user's approval signature. */
  item_type: 'econsent' | 'visit' | 'training' | 'doa' | 'doa_countersign' | 'eisf_approval';
  resource_id: string;
  document_name: string;
  document_version: string;
  subject_id: string | null;
  subject_ref: string | null;
  generated_at: string;
  requires_review: boolean;
  status: 'pending' | 'signed' | 'cancelled';
  /** Present on 'eisf_approval' items: where Sign Now opens the document. */
  eisf?: EisfDocumentLocation;
}

export interface EisfDocumentLocation {
  binder_ref: string;
  folder_id: string;
  document_id: string;
  version_id: string;
}

/**
 * Binder page deep link that selects the folder and opens the document at
 * that version (BinderDetailPage reads these params). Same link the
 * approval invitation email carries.
 */
export function eisfDocumentHref(at: EisfDocumentLocation): string {
  const p = new URLSearchParams({ folder: at.folder_id, document: at.document_id, version: at.version_id });
  return `/binder/${encodeURIComponent(at.binder_ref)}?${p.toString()}`;
}

/**
 * 2026-09-28 — NIDCR "Documenting the Consent Process" (v2.0) answers,
 * captured on Step 2 before any consent session starts. Mirrors
 * services/consent/processDocumentation.ts on the backend.
 */
export interface ConsentProcessDocumentation {
  subject_primary_language: string;
  cf_language: 'english' | 'spanish' | 'other';
  cf_language_other?: string;
  interpreter_available?: boolean;
  interpreter_name?: string;
  time_for_questions: boolean;
  time_for_questions_reason?: string;
  copy_provided: boolean;
  copy_provided_reason?: string;
  signed_before_procedures: boolean;
  signed_before_procedures_reason?: string;
  witness_present: boolean;
  witness_name?: string;
  risks_reviewed: boolean;
  additional_notes?: string;
}

export interface StaffConsentEvidence {
  staff_user_id: string;
  staff_name: string | null;
  trainings: Array<{
    reference_number: string;
    document_type_name: string;
    document_title: string;
    completed_at: string | null;
    countersigned_at: string | null;
  }>;
  trained: boolean;
  protocol_or_icf_trained: boolean;
  delegation: { doa_row_id: string; row_status: string; responsibilities: string[] } | null;
  delegated: boolean;
  consent_responsibility: boolean;
  evidence_errors?: string[];
}

export const consent = {
  presignDocument: (body: { study_id: string; file_name: string; mime_type: string }) =>
    api<ConsentPresignResponse>('/consent/documents/presign', { method: 'POST', body }),

  finaliseDocument: (body: {
    consent_document_id: string;
    study_id: string;
    file_name: string;
    file_size: number;
    mime_type: string;
    s3_key: string;
    s3_version_id?: string | null;
    /**
     * Server-minted when omitted (see [[auto-generate-identifiers]]). Legacy
     * BE-to-BE callers can still supply an IRB-approved label; the wizard
     * omits it and lets nextVersionLabel(prior) decide (v1.0 → v1.1 → …).
     */
    version_label_override?: string;
    docuseal_template_id?: string | null;
    irb_name?: string | null;
    irb_approval_date?: string | null;
    version_date?: string | null;
  }) =>
    api<ConsentFinaliseResponse>('/consent/documents/finalise', { method: 'POST', body }),

  listDocuments: (studyId: string) =>
    api<{ documents: ConsentDocumentRow[] }>(`/consent/documents?study_id=${encodeURIComponent(studyId)}`),

  approveDocument: (consentDocumentId: string, body: { notes?: string } = {}) =>
    api<ConsentApprovalResponse>(
      `/consent/documents/${encodeURIComponent(consentDocumentId)}/approve`,
      { method: 'POST', body },
    ),

  rejectDocument: (consentDocumentId: string, body: { notes: string }) =>
    api<ConsentApprovalResponse>(
      `/consent/documents/${encodeURIComponent(consentDocumentId)}/reject`,
      { method: 'POST', body },
    ),

  documentDownloadUrl: (consentDocumentId: string) =>
    api<{ consent_document_id: string; file_name: string; version_label: string; url: string; expires_in_seconds: number }>(
      `/consent/documents/${encodeURIComponent(consentDocumentId)}/download`,
    ),

  startSession: (body: {
    subject_id: string;
    template_id?: number;
    signer_email: string;
    subject_full_legal_name: string;
    date_of_birth: string;
    signature_meaning?: string;
    record_type?: 'initial' | 're_consent';
    // Sprint-7 ECON-003 / ECON-004
    signer_role?: 'subject' | 'parent' | 'assenting_minor' | 'lar';
    lar_full_legal_name?: string;
    lar_date_of_birth?: string;
    lar_relationship_to_subject?: 'spouse' | 'parent' | 'guardian' | 'other';
    // Staff co-signer (required for ICH-GCP compliance, added in Sprint-8)
    staff_user_id?: string;
    staff_email?: string;
    staff_name?: string;
    staff_role?: string;
    parent_full_legal_name?: string;
    parent_date_of_birth?: string;
    parent_relationship?: string;
    consent_mode?: 'remote' | 'in_person_esign';
    process_documentation: ConsentProcessDocumentation;
  }) => api<ConsentSessionResponse>('/consent/sessions', { method: 'POST', body }),

  // Sprint-7 ECON-003 — link a minor/assent record to a parent/guardian
  // consent record.
  linkParent: (consentRecordId: string, body: { parent_consent_record_id: string }) =>
    api<{ consent_record_id: string; parent_consent_record_id: string; updated: number }>(
      `/consent/records/${encodeURIComponent(consentRecordId)}/link-parent`,
      { method: 'POST', body },
    ),

  saveDraft: (body: { subject_id: string; payload: Record<string, string | boolean> }) =>
    api<{ ok: true; payload: Record<string, string | boolean> }>('/consent/drafts', {
      method: 'POST',
      body,
    }),

  /**
   * 2026-09-28 — read-only preview of the system-derived half of the
   * "Documenting the Consent Process" sheet for the chosen staff co-signer.
   */
  processEvidence: (subjectId: string, staffUserId: string) =>
    api<StaffConsentEvidence>(
      `/consent/process-evidence?subject_id=${encodeURIComponent(subjectId)}&staff_user_id=${encodeURIComponent(staffUserId)}`,
    ),

  listRecordsForSubject: (subjectId: string) =>
    api<{ records: ConsentRecordRow[] }>(`/consent/records?subject_id=${encryptId(subjectId)}`),

  /**
   * Presigned URL for the ARCHIVED, known-correct flattened signed PDF —
   * never the raw docuseal_signed_pdf_url (see getSignedDocumentUrl.ts's
   * header comment for why that direct link was wrong). Only call once
   * signed_pdf_s3_key is set on the record; the backend 400s otherwise.
   */
  getSignedDocumentUrl: (consentRecordId: string) =>
    api<{ consent_record_id: string; url: string; expires_in_seconds: number }>(
      `/consent/records/${encodeURIComponent(consentRecordId)}/signed-document-url`,
    ),

  // Phase 1 Item 5 — coordinator dispatches ONLY the signing link. The OTP
  // is issued later by requestOtp when the subject clicks the link. Legacy
  // `sendPin` (combined PIN + link) removed with this refactor.
  sendLink: (
    consentRecordId: string,
    body?: {
      channels?: 'both' | 'email_only' | 'sms_only';
      contact_email?: string;
      consent_mode?: 'remote' | 'in_person_esign';
    },
  ) =>
    api<{ signing_url: string; delivered: Array<'email' | 'sms'> }>(
      `/consent/sessions/${encodeURIComponent(consentRecordId)}/send-link`,
      { method: 'POST', body: body ?? {} },
    ),

  // Public — subject requests their OTP from the remote consent page.
  requestOtp: (body: { consent_record_id: string; channel?: 'both' | 'email_only' | 'sms_only' }) =>
    api<{
      delivered: Array<'email' | 'sms'>;
      expires_at: string;
      channels_masked: { email: string | null; sms: string | null };
    }>('/consent/remote/request-otp', { method: 'POST', body }),

  submissionStatus: (consentRecordId: string) =>
    api<{
      subject_signed: boolean;
      staff_signed: boolean;
      subject_completed_at: string | null;
      staff_completed_at: string | null;
      queue_item_exists: boolean;
    }>(`/consent/sessions/${encodeURIComponent(consentRecordId)}/submission-status`),

  resendStaffEmail: (consentRecordId: string) =>
    api<{ staff_email: string; staff_name: string }>(
      `/consent/sessions/${encodeURIComponent(consentRecordId)}/resend-staff-email`,
      { method: 'POST', body: {} },
    ),

  finalizeConsent: (consentRecordId: string) =>
    api<{ ok: boolean; already_finalized: boolean }>(
      `/consent/sessions/${encodeURIComponent(consentRecordId)}/finalize`,
      { method: 'POST', body: {} },
    ),

  verifyPin: (body: { consent_record_id: string; pin: string }) =>
    api<{
      consent_record_id: string;
      embed_url: string;
      subject_full_legal_name: string;
      version_label: string;
    }>('/consent/remote/verify-pin', { method: 'POST', body }),

  verifyStaffPin: (body: { consent_record_id: string; pin: string }) =>
    api<{
      consent_record_id: string;
      embed_url: string;
      staff_name: string;
      subject_full_legal_name: string;
    }>('/consent/remote/verify-staff-pin', { method: 'POST', body }),

  remoteStatus: (consentRecordId: string, perspective?: 'subject' | 'staff') =>
    api<{ consent_record_id: string; signed: boolean; signed_at: string | null }>(
      `/consent/remote/status?cr=${encodeURIComponent(consentRecordId)}${perspective ? `&perspective=${perspective}` : ''
      }`,
      { method: 'GET' },
    ),

  wizardContext: (subjectId: string) =>
    api<{
      subject: {
        subject_id: string;
        study_id: string;
        site_id: string;
        external_ref: string;
        status: SubjectStatus;
        re_consent_required: boolean;
        active_consent_record_id?: string | null;
      };
      active_consent_document: ConsentDocumentRow | null;
      draft: { payload: Record<string, string>; updated_at: string } | null;
      prior_version_label: string | null;
      prior_consent_mode: string | null;
    }>(`/consent/wizard-context?subject_id=${encryptId(subjectId)}`),

  /** In-person: generate pre-filled consent PDF, upload to S3, return presigned download URL. */
  generateDocument: (body: {
    subject_id: string;
    signer_role: 'subject' | 'parent' | 'assenting_minor' | 'lar';
    subject_full_legal_name: string;
    date_of_birth: string;
    signature_meaning: string;
    record_type: 'initial' | 're_consent';
    staff_user_id: string;
    staff_name: string;
    staff_role: string;
    lar_full_legal_name?: string;
    lar_date_of_birth?: string;
    lar_relationship_to_subject?: string;
    parent_full_legal_name?: string;
    parent_date_of_birth?: string;
    parent_relationship?: string;
    process_documentation: ConsentProcessDocumentation;
  }) =>
    api<{ consent_record_id: string; download_url: string; version_label: string }>(
      '/consent/sessions/generate-document',
      { method: 'POST', body },
    ),

  /** In-person step 2: get a presigned PUT URL to upload the scanned signed PDF. */
  presignSignedUpload: (body: { consent_record_id: string }) =>
    api<{ upload_url: string; s3_key: string }>(
      '/consent/sessions/presign-signed-upload',
      { method: 'POST', body },
    ),

  /** In-person step 3: confirm the signed PDF has been uploaded → marks consent complete. */
  confirmSignedUpload: (body: {
    consent_record_id: string;
    s3_key: string;
    /**
     * The ICF version the paper copy was signed against. Omit for "whatever
     * version this session was opened with", which is the active one.
     */
    consent_document_id?: string;
  }) =>
    api<{ ok: true; consent_record_id: string }>(
      '/consent/sessions/confirm-signed-upload',
      { method: 'POST', body },
    ),

  // ECON-006 — withdraw a signed consent record. Flips the subject's
  // status to 'withdrawn' and clears active_consent_record_id server-side.
  withdraw: (consentRecordId: string, body: { reason: string; scope?: 'subject_initiated' | 'coordinator_initiated' }) =>
    api<{ ok: true; subject_id: string; status: 'withdrawn' }>(
      `/consent/records/${encodeURIComponent(consentRecordId)}/withdraw`,
      { method: 'POST', body },
    ),
};

export const signatureQueue = {
  list: (opts: {
    limit?: number;
    page?: number;
    subject_ref?: string;
    item_type?: string[];
    date_from?: string;
    date_to?: string;
  } = {}) => {
    const p = new URLSearchParams();
    if (opts.limit) p.set('limit', String(opts.limit));
    if (opts.page != null) p.set('page', String(opts.page));
    if (opts.subject_ref) p.set('subject_ref', opts.subject_ref);
    if (opts.item_type?.length) p.set('item_type', opts.item_type.join(','));
    if (opts.date_from) p.set('date_from', opts.date_from);
    if (opts.date_to) p.set('date_to', opts.date_to);
    const qs = p.toString();
    return api<{ items: SignatureQueueItem[]; total?: number; hasMore?: boolean }>(
      `/signature-queue${qs ? `?${qs}` : ''}`,
    );
  },
  count: () => api<{ count: number }>('/signature-queue/count'),
};

// ===========================================================================
// Sprint-9 — eISF Binder
// ===========================================================================

export type BinderStatus =
  | 'draft'
  | 'preview'
  | 'in_approval'
  | 'current'
  | 'superseded'
  | 'archived';

export interface StudySiteBinderRow {
  binder_id: string;
  /** Sprint-12: per-tenant readable identifier, e.g. `BIN-0001`. Prefer this
   *  over `binder_id` for any user-facing surface (URL, list card, header). */
  reference_number: string | null;
  /** Optional display label override; falls back to "<protocol> @ <site>". */
  binder_name?: string | null;
  study_id: string;
  site_id: string;
  study_site_id: string;
  status: 'active' | 'archived';
  blinding_scope?: BinderBlindingScope | null;
  template_source_version: string | null;
  created_at: string;
  created_by: string;
  updated_at: string;
  archived_at?: string | null;
  archived_by?: string | null;
}

export interface BinderFolderRow {
  folder_id: string;
  binder_id: string;
  template_id: string | null;
  parent_folder_id: string | null;
  folder_level: string;
  zone_name: string;
  section_name: string;
  artifact_type: string;
  subartifact_label: string | null;
  inclusion: 'core' | 'recommended' | null;
  sort_order: number;
  display_name_override: string | null;
  display_name?: string;
  is_active: boolean;
  created_at: string;
  /**
   * CR-2026-09-29 — JSON array of document type codes this folder is the
   * filing target for, carried down from the binder template. Lets the
   * upload dialog offer the real catalog type on a sponsor-named folder
   * instead of typing the document after the folder's own name.
   */
  mapped_doc_type_codes?: string | null;
}

export interface BinderDocumentRow {
  document_id: string;
  /** Sprint-12: per-tenant readable identifier (DOC-####). */
  reference_number?: string | null;
  binder_id: string;
  folder_id: string;
  document_type_name: string;
  current_version_id: string | null;
  title: string;
  status: BinderStatus;
  is_locked: boolean;
  expires_at: string | null;
  phi_present: boolean;
  /** Sprint-14: accountable party. Defaults to created_by; reassignable. */
  owner_user_id?: string | null;
  /** Sprint-14: currently routed-to user. Null when no assignment. */
  assignee_user_id?: string | null;
  created_at: string;
  created_by: string;
  updated_at: string;
}

export interface DocumentVersionRow {
  version_id: string;
  document_id: string;
  version_label: string;
  version_number: number;
  s3_bucket: string;
  s3_key: string;
  s3_version_id: string | null;
  file_name: string;
  file_size: number;
  mime_type: string;
  checksum_sha256: string | null;
  checksum_status: 'pending' | 'verified' | 'failed';
  status: BinderStatus;
  uploaded_at: string;
  uploaded_by: string;
  signed_at: string | null;
  signed_by_user_id: string | null;
  docuseal_submission_id: string | null;
  superseded_by_version_id: string | null;
  change_reason: string | null;
  change_reason_details: string | null;
  effective_at: string | null;
  expires_at: string | null;
}

/**
 * Sprint-18 — payload for the document viewer modal. Returns slot
 * metadata, version metadata, a presigned GET URL, and the version
 * history list in one round-trip.
 *
 * `preview_url` vs `presigned_get_url`:
 *   - PDF version: both are the same URL (the original is renderable).
 *   - DOCX (today): `preview_url` is null; modal falls back to a
 *     "Download to view" prompt + `presigned_get_url`.
 *   - DOCX (after step 4 ships): `preview_url` points to the rendered
 *     PDF rendition's presigned URL.
 */
export interface DocumentPreviewResponse {
  document: {
    document_id: string;
    reference_number: string | null;
    binder_id: string;
    folder_id: string;
    folder_breadcrumb: string;
    document_type_name: string;
    title: string;
    status: BinderStatus;
    current_version_id: string | null;
    is_locked: boolean;
    phi_present: boolean;
    expires_at: string | null;
  };
  version: {
    version_id: string;
    version_label: string;
    version_number: number;
    file_name: string;
    file_size: number;
    mime_type: string;
    checksum_status: 'pending' | 'verified' | 'failed';
    status: BinderStatus;
    uploaded_at: string;
    uploaded_by_user_id: string;
    uploaded_by_name: string | null;
    signed_at: string | null;
  };
  presigned_get_url: string;
  preview_url: string | null;
  preview_mime_type: 'application/pdf' | null;
  /**
   * Sprint-18 rendition state. Branch the viewer state on this:
   *   not_needed -> render PDF inline (preview_url is the original)
   *   rendered   -> render PDF inline (preview_url is the rendition)
   *   pending    -> show "Generating preview…" and poll every 5 s
   *   failed     -> show "Preview unavailable — download to view"
   */
  preview_status: 'not_needed' | 'pending' | 'rendered' | 'failed';
  version_history: Array<{
    version_id: string;
    version_label: string;
    version_number: number;
    uploaded_at: string;
    status: BinderStatus;
  }>;
  expires_in_seconds: number;
}

export interface DocumentTypeDefinitionRow {
  document_type_def_id: string;
  document_type_name: string;
  definition: string | null;
  lifecycle: string | null;
  naming_format_template: string | null;
  duplicate_check_enabled: boolean;
}

/**
 * Sprint-9 — A single expired document reported by the eISF expiration gate.
 * Mirrors `ExpiredDocumentReport` in
 * DreamIT-Backend/src/services/eisf/expirationGate.ts.
 */
export interface ExpiredDocumentReport {
  document_id: string;
  document_type_name: string;
  title: string;
  policy: 'hard_block' | 'warn';
  expires_at: string | null;
  days_overdue: number;
}

/**
 * Sprint-12 Phase A — eISF document type catalog row. Returned by
 * `documentTypes.list()`. Slim shape (only what NewDocumentSlotDialog needs).
 */
export interface DocumentTypeRow {
  document_type_def_id: string;
  document_type_name: string;
  document_type_code: string;
  definition: string;
  naming_format_template: string;
  duplicate_check_enabled: boolean;
  /** Sprint-13: per-doc-type DocuSeal template id. Null/empty → env fallback. */
  docuseal_template_id: string | null;
  is_active: boolean;
  /** false = "Locked" type; once approved it cannot be replaced by a new version upload. */
  create_new_draft_versions_bool: boolean;
}

export interface DocumentTypeApproversResponse {
  document_type_code: string;
  document_type_name: string;
  approval_mode: string;
  any_of_threshold: number | null;
  approvers: Array<{
    role: string;
    sequence_order: number;
    signature_meaning: string;
  }>;
}

export const documentTypes = {
  /**
   * Full 88-row eISF document type catalog. Backs the type picker in
   * the "+ New document" dialog. Server filters out is_active=false rows.
   */
  list: () => api<{ document_types: DocumentTypeRow[] }>('/document-types'),

  /**
   * Sprint-18 (A) — configured approver roles for a single document type.
   * Surfaces inline on the upload dialog so users see WHO has to approve
   * before picking an assignee (different concept — see helper text in
   * NewDocumentSlotDialog).
   */
  getApprovers: (code: string) =>
    api<DocumentTypeApproversResponse>(
      `/document-types/${encodeURIComponent(code)}/approvers`,
    ),
};

/**
 * Sprint-15 — eISF version signing (§11.200 second factor).
 * Two-step OTP flow: (1) request code by email, (2) verify code +
 * receive the DocuSeal embed URL for this approver's signature slot.
 * Mirrors the DoA row signing shape.
 */
export const eisfSigning = {
  startOtp: (versionId: string) =>
    api<{ challenge_token: string; destination_masked: string }>(
      `/eisf/versions/${encodeURIComponent(versionId)}/sign/start-otp`,
      { method: 'POST', body: {} },
    ),
  verifyOtp: (
    versionId: string,
    body: { challenge_token: string; otp_code: string },
  ) =>
    api<{ embed_url: string }>(
      `/eisf/versions/${encodeURIComponent(versionId)}/sign/verify-otp`,
      { method: 'POST', body },
    ),
  /**
   * Sprint-18 — poll + finalize fallback, mirroring the eConsent pattern.
   * The DocuSeal webhook is the fast path; these cover local dev (webhook
   * can't reach localhost) and webhook delivery failures in any env.
   */
  syncStatus: (versionId: string) =>
    api<{ version_signed_at: string | null; any_newly_completed: boolean }>(
      `/eisf/versions/${encodeURIComponent(versionId)}/sign/status`,
    ),
  finalizeSync: (versionId: string) =>
    api<{ synced_count: number; version_signed_at: string | null }>(
      `/eisf/versions/${encodeURIComponent(versionId)}/sign/finalize`,
      { method: 'POST', body: {} },
    ),
};

export interface EisfTaskRow {
  task_id: string;
  kind: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  site_id: string;
  study_id: string | null;
  assignee_user_id: string | null;
  assignee_role: string | null;
  related_resource_type: string;
  related_resource_id: string;
  message: string;
  details_json: string | null;
  status: 'open' | 'resolved' | 'dismissed';
  created_at: string;
  resolved_at: string | null;
  resolved_by_user_id: string | null;
}

export const me = {
  /**
   * Sprint-14 (FR-DOC-11): cross-binder list of document slots
   * routed to / owned by the caller. Filter must be 'assigned' or
   * 'owned'. Server intersects with binders visible to the active
   * site to prevent cross-tenant leakage.
   */
  files: (filter: 'assigned' | 'owned') =>
    api<{ filter: 'assigned' | 'owned'; documents: BinderDocumentRow[] }>(
      `/me/files?filter=${encodeURIComponent(filter)}`,
    ),

  /**
   * Sprint-18 (eISF spec §edge case — Expired Delegation Log auto-task) —
   * tasks targeted at the caller (either by user_id or by role). Default
   * status='open'; pass 'resolved' / 'dismissed' for history views.
   */
  listTasks: (status: 'open' | 'resolved' | 'dismissed' = 'open') =>
    api<{ tasks: EisfTaskRow[] }>(`/me/tasks?status=${encodeURIComponent(status)}`),

  /**
   * Sprint-18 — transition an open task to resolved (fixed) or
   * dismissed (user declines). The daily generator may re-create
   * a dismissed task if the underlying issue persists; resolved tasks
   * stay closed.
   */
  resolveTask: (taskId: string, status: 'resolved' | 'dismissed') =>
    api<{ task_id: string; status: 'resolved' | 'dismissed' }>(
      `/me/tasks/${encodeURIComponent(taskId)}/resolve`,
      { method: 'POST', body: { status } },
    ),
};

/** Sprint-14 site-user row used by the assignee picker. */
export interface SiteUserRow {
  user_id: string;
  full_name: string;
  email: string;
  status: 'active' | 'invited' | 'locked' | 'disabled';
}

export const siteUsers = {
  /**
   * Sprint-14: slim list of users at the active session's site,
   * scoped server-side via claims.site_id. Disabled users are
   * filtered out; active users come first, then alphabetical.
   * Backs the Save & Assign picker on NewDocumentSlotDialog.
   */
  listMine: () =>
    api<{ site_id: string; users: SiteUserRow[] }>('/sites/me/users'),
};

/**
 * Sprint-15 — Per-approver result returned by `binder.initiateApproval`.
 * Surfaces the resolved approver list so the FE can show "sent to N people"
 * confirmation and the per-routing embed URLs.
 */
export interface InitiateApprovalApprover {
  routing_id: string;
  role: string;
  user_id: string;
  user_email: string;
  user_name: string;
  sequence_order: number;
  initial_status: 'pending' | 'waiting';
  embed_url: string | null;
  docuseal_submitter_id: string;
}

export interface InitiateApprovalResponse {
  docuseal_submission_id: string;
  workflow_initiated: string;
  approval_mode: 'single' | 'parallel' | 'sequential';
  any_of_threshold: number | null;
  approvers: InitiateApprovalApprover[];
  version_id: string;
}

/**
 * Sprint-15 — Live multi-approver routing state for a version. Returned by
 * `binder.getApprovalRouting`. One `approver` row per required signer; carries
 * the immutable contract (sequence_order, signature_meaning, who was asked)
 * plus live state (status, signed_at, rejected_at).
 */
export interface ApprovalRoutingApprover {
  routing_id: string;
  version_id: string;
  document_type_code: string;
  role: string;
  user_id: string;
  user_name: string;
  user_email: string;
  sequence_order: number;
  signature_meaning: string;
  status: 'waiting' | 'pending' | 'signed' | 'rejected' | 'skipped';
  docuseal_submitter_uuid: string | null;
  signed_at: string | null;
  rejected_at: string | null;
  rejection_reason: string | null;
  created_at: string;
  updated_at: string;
}

export interface GetApprovalRoutingResponse {
  version_id: string;
  approval_mode: 'single' | 'parallel' | 'sequential' | null;
  any_of_threshold: number | null;
  approvers: ApprovalRoutingApprover[];
}

/**
 * Punch-list b14 — a highlight/note on a document version. `x`/`y`/
 * `width`/`height` are fractions (0-1) of the page's width/height, same
 * convention as DoA/consent signature-field placement. `locked_at` is
 * set once the parent version is signed — the FE hides delete/edit for
 * a locked annotation.
 */
export interface DocumentAnnotationRow {
  annotation_id: string;
  document_id: string;
  version_id: string;
  page: number;
  x: number;
  y: number;
  width: number;
  height: number;
  note_text: string | null;
  relevance: 'relevant' | 'not_relevant' | null;
  author_user_id: string;
  created_at: string;
  locked_at: string | null;
}

export const binder = {
  /** List all binders the caller can see (one per study-site). */
  list: () => api<{ binders: StudySiteBinderRow[] }>('/binders'),

  /**
   * Binders of the studies the caller is on the team of, whether or not their
   * DoA row is approved yet — for the DoA pages only (Delegations, Open DOA).
   * The binder's contents still need an approved delegation.
   */
  listForDoa: () => api<{ binders: StudySiteBinderRow[] }>('/binders?purpose=doa'),

  /** Get a single binder + its folder tree. */
  get: (id: string) =>
    api<{
      binder: StudySiteBinderRow;
      folders: BinderFolderRow[];
      /** Sprint-18 Item #2 — keyed by folder_id; 'complete' / 'incomplete' / 'optional_only'. */
      folder_completeness?: Record<
        string,
        { folder_id: string; required_count: number; present_count: number; status: 'complete' | 'incomplete' | 'optional_only' }
      >;
      blinded?: boolean;
      blinded_role_master_ref?: string | null;
    }>(
      `/binders/${encodeURIComponent(id)}`,
    ),

  /** Document types valid for a specific folder (filtered by TMF section). */
  listFolderDocumentTypes: (id: string, folderId: string) =>
    api<{ document_types: DocumentTypeRow[]; section_name: string | null }>(
      `/binders/${encodeURIComponent(id)}/folders/${encodeURIComponent(folderId)}/document-types`,
    ),

  /** List documents (slots) for a folder + a map of current-version metadata. */
  listFolderDocuments: (id: string, folderId: string, includeArchived = false) =>
    api<{
      /** Archived slots in this folder, whether or not they are included. */
      archived_count?: number;
      documents: BinderDocumentRow[];
      current_versions: Record<string, DocumentVersionRow | null>;
      /** Sprint-18 — per-document tag_id arrays. Missing keys = empty array. */
      tags?: Record<string, string[]>;
      /** Sprint-18 Item #3 — resolved uploader names keyed by user_id. */
      uploader_names?: Record<string, string>;
      /**
       * Phase 1 item 12 (AC14) — server-side redaction flag. When true,
       * the caller is blinded on this binder's study; documents +
       * current_versions + tags + uploader_names are all empty and the
       * UI should render an explicit "Content hidden — you're blinded
       * on this study" state rather than the normal empty folder.
       */
      blinded?: boolean;
      blinded_role_master_ref?: string | null;
    }>(
      `/binders/${encodeURIComponent(id)}/folders/${encodeURIComponent(folderId)}/documents`
        + (includeArchived ? '?include_archived=true' : ''),
    ),

  /** Create a new document slot inside a folder. */
  createSlot: (
    id: string,
    folderId: string,
    body: {
      document_type_name: string;
      title?: string;
      phi_present?: boolean;
      expires_at?: string;
      /** Sprint-14: optional initial assignee ("Save & Assign"). */
      assignee_user_id?: string;
    },
  ) =>
    api<{ document: BinderDocumentRow }>(
      `/binders/${encodeURIComponent(id)}/folders/${encodeURIComponent(folderId)}/documents`,
      { method: 'POST', body },
    ),

  /**
   * Sprint-14: patch the owner and/or assignee of a slot. Pass
   * `assignee_user_id: null` to clear an assignment.
   */
  updateAssignment: (
    id: string,
    docId: string,
    body: {
      owner_user_id?: string;
      assignee_user_id?: string | null;
    },
  ) =>
    api<{
      document_id: string;
      owner_user_id: string | null;
      assignee_user_id: string | null;
    }>(
      `/binders/${encodeURIComponent(id)}/documents/${encodeURIComponent(docId)}/assignment`,
      { method: 'PATCH', body },
    ),

  /** Reserve a version row + return a presigned PUT URL for the file. */
  presignVersion: (
    id: string,
    docId: string,
    body: { file_name: string; mime_type: string },
  ) =>
    api<{
      upload_url: string;
      version_id: string;
      version_label: string;
      s3_bucket: string;
      s3_key: string;
      expires_in_seconds: number;
    }>(
      `/binders/${encodeURIComponent(id)}/documents/${encodeURIComponent(docId)}/versions/presign`,
      { method: 'POST', body },
    ),

  /** Record the completed S3 upload; server-side checksum job is enqueued. */
  finaliseVersion: (
    id: string,
    docId: string,
    body: {
      version_id: string;
      version_label: string;
      s3_bucket: string;
      s3_key: string;
      file_name: string;
      file_size: number;
      mime_type: string;
      client_sha256?: string;
      /** Required by BE when this upload supersedes a Current version. */
      change_reason?: 'replace' | 'amendment' | 'correction' | 'other';
      change_reason_details?: string;
      /**
       * Training module Phase 2 — per-upload override of the doc type's
       * catalog training defaults. Omit to use the catalog default.
       */
      training_required?: boolean;
      training_required_roles?: string[];
      /** Training module Phase 3 — effective / expiry date metadata, ISO 8601. */
      effective_at?: string;
      version_expires_at?: string;
      /** Values for the doc type's catalog-declared required_fields_json. */
      metadata_fields?: Record<string, string>;
    },
  ) =>
    api<{ version: DocumentVersionRow }>(
      `/binders/${encodeURIComponent(id)}/documents/${encodeURIComponent(docId)}/versions/finalise`,
      { method: 'POST', body },
    ),

  /**
   * Document Library "pick from library" integration — files an
   * already-uploaded library file into this binder version without
   * re-uploading the bytes. The backend copies the S3 object under the
   * binder's own versioning key; the library file itself is untouched.
   */
  finaliseVersionFromLibrary: (
    id: string,
    docId: string,
    body: {
      library_file_id: string;
      change_reason?: 'replace' | 'amendment' | 'correction' | 'other';
      change_reason_details?: string;
      training_required?: boolean;
      training_required_roles?: string[];
      effective_at?: string;
      version_expires_at?: string;
      metadata_fields?: Record<string, string>;
    },
  ) =>
    api<{ version: DocumentVersionRow }>(
      `/binders/${encodeURIComponent(id)}/documents/${encodeURIComponent(docId)}/versions/from-library`,
      { method: 'POST', body },
    ),

  /** Returns every retained version for a document (newest first). */
  listVersions: (id: string, docId: string) =>
    api<{ versions: DocumentVersionRow[]; uploader_names: Record<string, string> }>(
      `/binders/${encodeURIComponent(id)}/documents/${encodeURIComponent(docId)}/versions`,
    ),

  /**
   * Sprint-18 — One-shot payload for the document viewer modal: slot
   * metadata + version metadata + presigned GET URL + version history.
   * Optional version_id query forces a specific historical version.
   */
  getDocumentPreview: (id: string, docId: string, versionId?: string) =>
    api<DocumentPreviewResponse>(
      `/binders/${encodeURIComponent(id)}/documents/${encodeURIComponent(docId)}/preview${versionId ? `?version_id=${encodeURIComponent(versionId)}` : ''}`,
    ),

  /**
   * Sprint-18 (eISF spec §audit events + §permission matrix) — PATCH
   * safe metadata on a document slot. Fires `document.metadata.changed`
   * audit with a structured diff on the server.
   */
  updateDocumentMetadata: (
    id: string,
    docId: string,
    body: {
      title?: string;
      expires_at?: string | null;
      phi_present?: boolean;
    },
  ) =>
    api<{ document_id: string; changed: Array<'title' | 'expires_at' | 'phi_present'> }>(
      `/binders/${encodeURIComponent(id)}/documents/${encodeURIComponent(docId)}/metadata`,
      { method: 'PATCH', body },
    ),

  /** Sprint-18 (eISF spec §3) — claim a check-out on a document slot. */
  checkOutDocument: (id: string, docId: string, reason?: string) =>
    api<{
      document_id: string;
      checked_out_by_user_id: string;
      checked_out_at: string;
      check_out_reason: string | null;
    }>(
      `/binders/${encodeURIComponent(id)}/documents/${encodeURIComponent(docId)}/check-out`,
      { method: 'POST', body: reason ? { reason } : {} },
    ),

  /**
   * Sprint-18 (eISF spec §3) — release a check-out. `force: true` lets a
   * Reg Coord / Site Admin override another user's check-out.
   */
  checkInDocument: (id: string, docId: string, force?: boolean) =>
    api<{ document_id: string }>(
      `/binders/${encodeURIComponent(id)}/documents/${encodeURIComponent(docId)}/check-in`,
      { method: 'POST', body: force ? { force: true } : {} },
    ),

  /**
   * Sprint-18 (eISF spec §functional cap #5) — apply the desired tag set
   * to a document. Server diff-and-applies against the junction.
   */
  setDocumentTags: (id: string, docId: string, tagIds: string[]) =>
    api<{ document_id: string; added: string[]; removed: string[] }>(
      `/binders/${encodeURIComponent(id)}/documents/${encodeURIComponent(docId)}/tags`,
      { method: 'PUT', body: { tag_ids: tagIds } },
    ),

  /**
   * Sprint-18 (eISF spec §edge case — DocuSeal Envelope Expiration) —
   * operator-triggered recovery when a signing envelope dies. Closes
   * stale routing rows + creates fresh envelopes via the same initiate
   * service.
   */
  reinitiateApproval: (
    id: string,
    docId: string,
    versionId: string,
    body: {
      workflow_name:
      | 'Send for PI Signature'
      | 'Send for Review'
      | 'Send for eSignature Approval'
      | 'Send for Read and Understand';
      reason: string;
    },
  ) =>
    api<{
      version_id: string;
      closed_routing_count: number;
      prior_attempt_count: number;
    }>(
      `/binders/${encodeURIComponent(id)}/documents/${encodeURIComponent(docId)}/versions/${encodeURIComponent(versionId)}/approval/re-initiate`,
      { method: 'POST', body },
    ),

  /**
   * Sprint-18 (eISF spec §audit events — Bulk Export). Returns a manifest
   * of every current version (or full history) with presigned GET URLs.
   */
  bulkExport: (
    id: string,
    body?: { include_history?: boolean; max_versions?: number },
  ) =>
    api<BulkExportResponse>(
      `/binders/${encodeURIComponent(id)}/export`,
      { method: 'POST', body: body ?? {} },
    ),

  /** Transition a version through the binder lifecycle (draft → preview → in_approval → current → superseded/archived). */
  transitionVersionStatus: (
    id: string,
    docId: string,
    versionId: string,
    body: {
      target_status: BinderStatus;
      change_reason?: string;
      change_reason_details?: string;
    },
  ) =>
    api<{ version: DocumentVersionRow }>(
      `/binders/${encodeURIComponent(id)}/documents/${encodeURIComponent(docId)}/versions/${encodeURIComponent(versionId)}/transition`,
      { method: 'POST', body },
    ),

  initiateApproval: (
    id: string,
    docId: string,
    versionId: string,
    body: {
      workflow_name:
      | 'Send for PI Signature'
      | 'Send for Review'
      | 'Send for eSignature Approval'
      | 'Send for Read and Understand';
    },
  ) =>
    api<InitiateApprovalResponse>(
      `/binders/${encodeURIComponent(id)}/documents/${encodeURIComponent(docId)}/versions/${encodeURIComponent(versionId)}/approval/initiate`,
      { method: 'POST', body },
    ),

  /**
   * Sprint-15 — Read-only multi-approver routing state for a version.
   * Backs the "Approval progress" panel that shows who has signed, who
   * is currently pending, and who is waiting on an earlier sequence step.
   */
  getApprovalRouting: (id: string, docId: string, versionId: string) =>
    api<GetApprovalRoutingResponse>(
      `/binders/${encodeURIComponent(id)}/documents/${encodeURIComponent(docId)}/versions/${encodeURIComponent(versionId)}/approval/routing`,
    ),

  /**
   * Punch-list b14 — highlight/note annotations on a document version.
   * Visible to everyone who can view the document; locked once the
   * version is signed.
   */
  listAnnotations: (id: string, docId: string, versionId: string) =>
    api<{ version_id: string; annotations: DocumentAnnotationRow[] }>(
      `/binders/${encodeURIComponent(id)}/documents/${encodeURIComponent(docId)}/versions/${encodeURIComponent(versionId)}/annotations`,
    ),

  createAnnotation: (
    id: string,
    docId: string,
    versionId: string,
    body: {
      page: number;
      x: number;
      y: number;
      width: number;
      height: number;
      note_text?: string;
      relevance?: 'relevant' | 'not_relevant';
    },
  ) =>
    api<{ annotation: DocumentAnnotationRow }>(
      `/binders/${encodeURIComponent(id)}/documents/${encodeURIComponent(docId)}/versions/${encodeURIComponent(versionId)}/annotations`,
      { method: 'POST', body },
    ),

  deleteAnnotation: (id: string, docId: string, versionId: string, annotationId: string) =>
    api<{ deleted: true; annotation_id: string }>(
      `/binders/${encodeURIComponent(id)}/documents/${encodeURIComponent(docId)}/versions/${encodeURIComponent(versionId)}/annotations/${encodeURIComponent(annotationId)}`,
      { method: 'DELETE' },
    ),

  /**
   * Sprint-15 (option 2) — Exceptional unlock of a locked document slot.
   * Mandatory justification captured in the audit row. Role-gated on the
   * BE (site_admin / regulatory_specialist / platform admins only).
   */
  unlockDocument: (id: string, docId: string, body: { reason: string }) =>
    api<{ document: BinderDocumentRow }>(
      `/binders/${encodeURIComponent(id)}/documents/${encodeURIComponent(docId)}/unlock`,
      { method: 'POST', body },
    ),

  /**
   * Sprint-9 — Returns the eISF expiration assessment for a (study, site)
   * pair: hard-block + warning rows partitioned by policy, plus scan
   * totals. Mirrors `ExpirationAssessment` from the BE expirationGate.
   */
  getExpirationAssessment: (studyId: string, siteId: string) =>
    api<{
      assessment: {
        hard_blocks: ExpiredDocumentReport[];
        warnings: ExpiredDocumentReport[];
        total_documents_scanned: number;
        current_documents_expired: number;
      };
    }>(
      `/studies/${encodeURIComponent(studyId)}/sites/${encodeURIComponent(siteId)}/eisf/expiration-assessment`,
    ),
};

// ===========================================================================
// Document Library — site-wide shared folder/file space (client feedback
// 2026-08-04). Separate from the per-study eISF binder above. Any
// authenticated user at the site can create folders, upload, and download
// — no role gate.
// ===========================================================================

export interface DocLibraryFolderRow {
  folder_id: string;
  reference_number: string;
  site_id: string;
  /** Self-referential. Null = top-level folder. */
  parent_folder_id: string | null;
  name: string;
  sort_order: number;
  is_active: string;
  created_at: string;
  created_by: string;
  updated_at: string;
  updated_by: string;
}

export interface DocLibraryFileRow {
  file_id: string;
  reference_number: string;
  site_id: string;
  /** Null = root-level file. */
  folder_id: string | null;
  file_name: string;
  s3_bucket: string;
  s3_key: string;
  mime_type: string;
  file_size: number;
  uploaded_at: string;
  uploaded_by: string;
  updated_at: string | null;
  updated_by: string | null;
  is_active: string;
  deleted_at: string | null;
  deleted_by: string | null;
}

export const docLibrary = {
  listFolders: () => api<{ folders: DocLibraryFolderRow[] }>('/doc-library/folders'),

  createFolder: (body: { name: string; parent_folder_id?: string }) =>
    api<{ folder: DocLibraryFolderRow }>('/doc-library/folders', { method: 'POST', body }),

  updateFolder: (id: string, body: { name?: string; parent_folder_id?: string | null }) =>
    api<{ folder_id: string; updated_at: string }>(
      `/doc-library/folders/${encodeURIComponent(id)}`,
      { method: 'PATCH', body },
    ),

  deleteFolder: (id: string) =>
    api<{ folder_id: string; deleted: true }>(
      `/doc-library/folders/${encodeURIComponent(id)}`,
      { method: 'DELETE', body: {} },
    ),

  /** Omit for every active file at the site; pass 'root' for root-level only, or a folder_id. */
  listFiles: (folderId?: string | 'root') =>
    api<{ files: DocLibraryFileRow[] }>(`/doc-library/files${qs({ folder_id: folderId })}`),

  presignUpload: (body: { folder_id?: string; file_name: string; mime_type: string }) =>
    api<{
      upload_url: string;
      file_id: string;
      s3_bucket: string;
      s3_key: string;
      expires_in_seconds: number;
    }>('/doc-library/files/presign', { method: 'POST', body }),

  finaliseUpload: (body: {
    file_id: string;
    folder_id?: string;
    file_name: string;
    file_size: number;
    mime_type: string;
    s3_bucket: string;
    s3_key: string;
  }) => api<{ file: DocLibraryFileRow }>('/doc-library/files/finalise', { method: 'POST', body }),

  getDownloadUrl: (id: string) =>
    api<{ download_url: string; expires_in_seconds: number }>(
      `/doc-library/files/${encodeURIComponent(id)}/download-url`,
    ),

  deleteFile: (id: string) =>
    api<{ file_id: string; deleted: true }>(
      `/doc-library/files/${encodeURIComponent(id)}`,
      { method: 'DELETE', body: {} },
    ),
};

// ===========================================================================
// Sprint-10 — Delegation of Authority (DoA)
// ===========================================================================

export type DoaVersionStatus =
  | 'draft'
  | 'in_review'
  | 'active'
  | 'superseded'
  | 'archived';

export type DoaRowStatus = 'pending_signature' | 'signed' | 'revoked';

/**
 * Mirrors the BE row shape returned by listVersions / getActive / getVersion.
 * Field names are exact matches for the Caspio columns (no FE-side renames)
 * so the JSON parses straight onto this type without a transformer.
 */
export interface DoaVersionRow {
  doa_version_id: string;
  /** Human-readable reference, e.g. "DOA-0042-v2". Nullable until backfill. */
  reference_number: string | null;
  binder_id: string;
  /** Monotonic per-binder counter starting at 1. The "version label" the UI
   *  shows ("v1", "v2", …) is derived as `v${version_number}` — no separate
   *  field exists on the BE row. */
  version_number: number;
  status: DoaVersionStatus;
  template_source_version: string | null;
  /** Timestamp this version was activated. Null while draft / in_review. */
  effective_at: string | null;
  /** End of this version's effective window; set when a newer version is
   *  approved. Null while current. */
  effective_to?: string | null;
  superseded_by_version_id: string | null;
  pi_user_id: string | null;
  pi_signature_envelope_id: string | null;
  pi_signed_at: string | null;
  pi_signed_pdf_url: string | null;
  pi_signature_meaning: string | null;
  notes: string | null;
  created_at: string;
  created_by: string;
  updated_at: string;
}

/**
 * Mirrors the BE DoaRowRow returned alongside the version (and from addRow
 * after a row insert). Caspio column names verbatim.
 */
export interface DoaRowRow {
  doa_row_id: string;
  doa_version_id: string;
  staff_user_id: string;
  staff_full_name: string;
  staff_email: string;
  role: string;
  responsibilities: string | null;
  effective_from: string;
  effective_to: string | null;
  sort_order: number;
  row_status: DoaRowStatus;
  row_envelope_id: string | null;
  signed_at: string | null;
  signed_by_user_id: string | null;
  signed_pdf_url: string | null;
  pi_countersigned_at?: string | null;
  /**
   * Set when the log was generated after the staff member had left: the row
   * went to the PI alone, no staff signature (Harish, 7 Oct 2026).
   */
  staff_signature_waived_reason?: 'removed' | 'inactive' | null;
}

export const doa = {
  /**
   * Create a new draft DoA version on a binder. Response matches the BE
   * `CreateDraftVersionResponse` shape, not the full version row.
   */
  createDraft: (
    binderId: string,
    body: { pi_user_id?: string; copy_from_version_id?: string } = {},
  ) =>
    api<{ doa_version_id: string; version_number: number }>(
      `/binders/${encodeURIComponent(binderId)}/doa/versions/draft`,
      { method: 'POST', body },
    ),

  /**
   * Add a row (delegated user) to a draft DoA version. Field names match the
   * BE zod schema verbatim (staff_user_id, staff_full_name, staff_email,
   * effective_from as ISO datetime). The picker mode resolves a user_id
   * client-side and passes it through here; the manual-entry mode still has
   * to resolve to a user_id (BE rejects an entry without one — invite the
   * user via Study Onboarding instead).
   */
  addRow: (
    binderId: string,
    versionId: string,
    body: {
      staff_user_id: string;
      staff_full_name: string;
      staff_email: string;
      role: string;
      responsibility_codes: string[];
      /** ISO 8601 datetime (e.g. "2026-06-29T00:00:00Z"). The date-only
       *  format produced by `<input type="date">` is NOT accepted. */
      effective_from: string;
      /** ISO 8601 datetime, optional. Open-ended delegation when omitted. */
      effective_to?: string;
    },
  ) =>
    api<{ doa_row_id: string; sort_order: number }>(
      `/binders/${encodeURIComponent(binderId)}/doa/versions/${encodeURIComponent(versionId)}/rows`,
      { method: 'POST', body },
    ),

  /** Patch an existing row on a draft DoA version. */
  updateRow: (
    binderId: string,
    versionId: string,
    rowId: string,
    body: {
      role?: string;
      responsibilities?: string;
      effective_from?: string;
      effective_to?: string | null;
    },
  ) =>
    api<{ row: DoaRowRow }>(
      `/binders/${encodeURIComponent(binderId)}/doa/versions/${encodeURIComponent(versionId)}/rows/${encodeURIComponent(rowId)}`,
      { method: 'PATCH', body },
    ),

  /** Remove a row from a draft DoA version (or revoke if already signed). */
  removeRow: (
    binderId: string,
    versionId: string,
    rowId: string,
    body: { reason?: string } = {},
  ) =>
    api<{ ok: true; row_id: string; status: DoaRowStatus }>(
      `/binders/${encodeURIComponent(binderId)}/doa/versions/${encodeURIComponent(versionId)}/rows/${encodeURIComponent(rowId)}`,
      { method: 'DELETE', body },
    ),

  /** Initiate the DocuSeal multi-signer flow for a DoA version. */
  initiate: (binderId: string, versionId: string) =>
    api<{
      rows_count: number;
      envelopes_created: number;
    }>(
      `/binders/${encodeURIComponent(binderId)}/doa/versions/${encodeURIComponent(versionId)}/initiate`,
      { method: 'POST', body: {} },
    ),

  /** Explicit "Send Invitations" step — emails staff still pending signature. */
  sendInvitations: (binderId: string, versionId: string) =>
    api<{
      sent_count: number;
      skipped_count: number;
    }>(
      `/binders/${encodeURIComponent(binderId)}/doa/versions/${encodeURIComponent(versionId)}/send-invitations`,
      { method: 'POST', body: {} },
    ),

  /** Get the currently-active DoA version + its rows for a binder. */
  getActive: (binderId: string) =>
    api<{ version: DoaVersionRow | null; rows: DoaRowRow[] }>(
      `/binders/${encodeURIComponent(binderId)}/doa/active`,
    ),

  /** List every DoA version on a binder (newest first). */
  listVersions: (binderId: string) =>
    api<{ versions: DoaVersionRow[] }>(
      `/binders/${encodeURIComponent(binderId)}/doa/versions`,
    ),

  /** Get a specific DoA version + its rows. */
  getVersion: (binderId: string, versionId: string) =>
    api<{ version: DoaVersionRow; rows: DoaRowRow[] }>(
      `/binders/${encodeURIComponent(binderId)}/doa/versions/${encodeURIComponent(versionId)}`,
    ),

  /**
   * Sprint-17 — Grid editor surface. Returns the editable Draft version
   * (or active fallback) with responsibilities, rows, delegations, and
   * qualification chips in one round-trip.
   */
  getGrid: (binderId: string) =>
    api<DoaGridResponse>(`/binders/${encodeURIComponent(binderId)}/doa/grid`),

  /**
   * Sprint-17 — Save the grid's current delegation state. BE computes
   * the diff against the live junction and applies the minimum
   * inserts + deletes. Only valid on Draft versions.
   */
  saveDelegations: (
    binderId: string,
    body: {
      doa_version_id: string;
      rows: Array<{
        doa_row_id: string;
        responsibility_codes: string[];
        /** DT2-BLIND-3 — per-staff blinded/unblinded pick. Omit to leave untouched. */
        blinded?: boolean;
      }>;
    },
  ) =>
    api<{
      version_id: string;
      changes: Array<{ doa_row_id: string; added: string[]; removed: string[] }>;
    }>(`/binders/${encodeURIComponent(binderId)}/doa/grid/delegations`, {
      method: 'PUT',
      body,
    }),

  /**
   * Sprint-18 — On-demand creation of the first DoA version. Backs the
   * grid empty-state CTA. Server pulls study delegations the same way
   * the activation hook does — same staff list, same row shape.
   */
  createFirstVersion: (binderId: string) =>
    api<{ doa_version_id: string; staff_count: number; created: boolean }>(
      `/binders/${encodeURIComponent(binderId)}/doa/versions`,
      { method: 'POST', body: {} },
    ),

  /**
   * Step 1 of OTP-gated DoA signing.
   * Sends a 6-digit code to the staff member's email.
   * Returns challenge_token (held in state to pair with the code on verify).
   */
  startRowOtp: (rowId: string) =>
    api<{ challenge_token: string; destination_masked: string }>(
      `/doa/rows/${encodeURIComponent(rowId)}/sign/start-otp`,
      { method: 'POST', body: {} },
    ),

  /**
   * Step 2 of OTP-gated DoA signing.
   * Verifies the 6-digit code; on success returns the DocuSeal embed URL.
   */
  verifyRowOtp: (
    rowId: string,
    body: { challenge_token: string; otp_code: string },
  ) =>
    api<{ embed_url: string }>(
      `/doa/rows/${encodeURIComponent(rowId)}/sign/verify-otp`,
      { method: 'POST', body },
    ),

  /**
   * PIN-gated DoA signing (eConsent-style).
   * Staff enter the 6-digit PIN from their PIN email directly on the /doa/sign page.
   * The PIN was pre-sent at initiation — no "request code" step needed.
   */
  verifyRowPin: (rowId: string, body: { pin: string }) =>
    api<{ embed_url: string }>(
      `/doa/rows/${encodeURIComponent(rowId)}/sign/verify-pin`,
      { method: 'POST', body },
    ),

  resendRowEmails: (rowId: string) =>
    api<{ resent: boolean }>(
      `/doa/rows/${encodeURIComponent(rowId)}/sign/resend`,
      { method: 'POST', body: {} },
    ),

  /**
   * Poll + finalize fallback — DoA was the only signing module with no
   * self-heal path if the DocuSeal webhook doesn't land. See
   * useDoaSignatureSelfHeal.ts / services/doa/doaSignatureSync.ts.
   */
  getSignStatus: (binderId: string, versionId: string) =>
    api<{
      status: string;
      rows_pending: number;
      rows_signed: number;
      pi_signed_at: string | null;
      any_newly_completed: boolean;
    }>(`/binders/${encodeURIComponent(binderId)}/doa/versions/${encodeURIComponent(versionId)}/sign-status`),

  finalizeSignatures: (binderId: string, versionId: string) =>
    api<{
      synced: boolean;
      rows_newly_signed: number;
      pi_newly_signed: boolean;
      /** PI countersignatures written back by this call (2 Oct 2026). */
      rows_newly_countersigned?: number;
      status: string;
    }>(`/binders/${encodeURIComponent(binderId)}/doa/versions/${encodeURIComponent(versionId)}/finalize-signatures`, {
      method: 'POST',
      body: {},
    }),

  /**
   * Authenticated direct-embed for a DoA row or PI countersignature the
   * CALLER is assigned to — powers the eSignature Queue's "Sign Now"
   * action. `resourceId` is a doa_row_id or a doa_version_id; the backend
   * tries both. See doaSignEmbed.ts's header comment for why this exists
   * alongside the public OTP/PIN-gated routes.
   */
  getSignEmbed: (resourceId: string) =>
    api<{ embed_url: string; binder_id: string; doa_version_id: string }>(
      `/doa/sign-embed/${encodeURIComponent(resourceId)}`,
    ),
};

// ===========================================================================
// Sprint-11 — Remote Access Grants (external reviewers: sponsor monitors + auditors)
// ===========================================================================

/**
 * Mirrors `RemoteAccessScope` in
 * DreamIT-Backend/src/services/caspio/remoteAccessGrants.ts. Drives PHI
 * redaction + folder filtering at read time.
 */
export type RemoteAccessScope =
  | 'view_full_binder'
  | 'view_pii_redacted'
  | 'view_specific_folders';

/** Mirrors `RemoteAccessRole` on the BE — drives UI labelling + audit categorisation. */
export type RemoteAccessRole = 'sponsor_monitor' | 'auditor';

/**
 * Mirrors `RemoteAccessStatus` on the BE.
 *   - pending: grant created, reviewer has not yet verified an OTP
 *   - active : reviewer has verified at least one OTP; session JWTs may be minted
 *   - expired: now > valid_to (lifecycle flip — independent of revocation)
 *   - revoked: hard-revoked by site staff; revoked_at populated
 */
export type RemoteAccessStatus = 'pending' | 'active' | 'expired' | 'revoked';

/**
 * Mirrors `RemoteAccessGrantRow` in
 * DreamIT-Backend/src/services/caspio/remoteAccessGrants.ts. One row per
 * (binder, external_reviewer). The coordinator dashboard reads this shape
 * verbatim from `externalGrants.list()`.
 *
 * Sensitive fields (`magic_link_token`, `otp_code_hash`) are typed
 * faithfully here so the BE wire contract is fully captured, but the BE
 * SHOULD scrub them before returning rows to the dashboard — the FE never
 * needs to render either.
 */
export interface RemoteAccessGrantRow {
  grant_id: string;
  binder_id: string;
  granted_to_email: string;
  granted_to_name: string;
  granted_to_role: RemoteAccessRole;
  scope: RemoteAccessScope;
  /** JSON-encoded array of folder_ids when scope='view_specific_folders'. Null/empty otherwise. */
  scope_folder_ids: string | null;
  valid_from: string;
  valid_to: string;
  status: RemoteAccessStatus;
  magic_link_token: string;
  magic_link_expires_at: string;
  otp_code_hash: string | null;
  otp_expires_at: string | null;
  otp_attempts: number;
  last_login_at: string | null;
  last_login_ip: string | null;
  last_login_user_agent: string | null;
  granted_by_user_id: string;
  revoked_at: string | null;
  revoked_by: string | null;
  revocation_reason: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Body for `externalGrants.create`. Mirrors the BE Zod schema in
 * DreamIT-Backend/src/handlers/access/createGrant.ts.
 */
export interface CreateExternalGrantInput {
  granted_to_email: string;
  granted_to_name: string;
  granted_to_role: RemoteAccessRole;
  scope: RemoteAccessScope;
  /** Required when scope === 'view_specific_folders'. */
  scope_folder_ids?: string[];
  valid_from_iso: string;
  valid_to_iso: string;
  /** Punch-list DT2-BLIND (Spike F fix) — meaningless on an Open-Label study. */
  blinded?: boolean;
}

export interface CreateExternalGrantResponse {
  grant_id: string;
  magic_link_url: string;
}

export interface ListExternalGrantsResponse {
  grants: RemoteAccessGrantRow[];
}

export interface RevokeExternalGrantInput {
  /** 4-2000 char free-text reason (BE Zod min(4).max(2000)). Recorded in the audit trail. */
  reason: string;
}

export interface RevokeExternalGrantResponse {
  grant_id: string;
  revoked: true;
}

/**
 * Coordinator-dashboard side of the external-reviewer flow. All three
 * calls run with the regular internal session JWT (added by `api()`); the
 * BE enforces the site_id tenant check + the editing-role gate.
 */
export const externalGrants = {
  /** POST /binders/{binderId}/external/grants — create + email a magic-link grant. */
  create: (binderId: string, body: CreateExternalGrantInput) =>
    api<CreateExternalGrantResponse>(
      `/binders/${encodeURIComponent(binderId)}/external/grants`,
      { method: 'POST', body },
    ),

  /**
   * GET /binders/{binderId}/external/grants?include_revoked=...
   * Revoked rows are hidden by default; pass `includeRevoked=true` for the
   * audit-history view.
   */
  list: (binderId: string, includeRevoked = false) =>
    api<ListExternalGrantsResponse>(
      `/binders/${encodeURIComponent(binderId)}/external/grants${includeRevoked ? '?include_revoked=true' : ''
      }`,
    ),

  /** POST /binders/{binderId}/external/grants/{grantId}/revoke — hard-revoke. Idempotent. */
  revoke: (binderId: string, grantId: string, body: RevokeExternalGrantInput) =>
    api<RevokeExternalGrantResponse>(
      `/binders/${encodeURIComponent(binderId)}/external/grants/${encodeURIComponent(
        grantId,
      )}/revoke`,
      { method: 'POST', body },
    ),
};

// ===========================================================================
// Sprint-11 — External Access (PUBLIC landing page + scoped reviewer session)
// ===========================================================================

/**
 * The four `externalAccess.*` calls are special:
 *   - `startOtp` + `verifyOtp` are fully UNAUTHENTICATED — they MUST NOT
 *     send any `Authorization` header (a stale internal session token
 *     would only confuse the BE).
 *   - `getBinder` + `listDocs` take an explicit `sessionToken` (the scoped
 *     external-review JWT minted by `verifyOtp`) and must NOT reuse the
 *     internal session token from sessionStorage.
 *
 * The shared `api()` helper auto-injects the internal token from
 * sessionStorage, so these four routes go through a dedicated `externalApi`
 * helper that controls the Authorization header explicitly. Error handling
 * (status + code + details) mirrors `api()` so callers can `catch (e:
 * ApiError)` uniformly.
 */
interface ExternalApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  /** When set, sent as `Authorization: Bearer ${sessionToken}`. */
  sessionToken?: string;
}

async function externalApi<T>(path: string, opts: ExternalApiOptions = {}): Promise<T> {
  const headers: HeadersInit = { Accept: 'application/json' };
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if (opts.sessionToken) headers.Authorization = `Bearer ${opts.sessionToken}`;

  const response = await fetch(`${API_BASE}${path}`, {
    method: opts.method ?? 'GET',
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
    credentials: 'omit',
  });

  if (response.status === 204) return undefined as T;
  const text = await response.text();
  const data = text ? JSON.parse(text) : null;

  if (!response.ok) {
    const err = data?.error;
    // Deliberately DO NOT trigger the global unauthorized-handler here.
    // A 401 on /external/* must NOT wipe the internal session — these
    // routes are for off-platform reviewers who have no internal session
    // in the first place, and a coordinator can legitimately preview the
    // landing page in the same browser as their staff session.
    throw new ApiError(
      response.status,
      err?.code ?? 'UNKNOWN',
      err?.message ?? data?.message ?? response.statusText ?? 'Request failed',
      err?.details,
    );
  }
  return data as T;
}

export interface StartOtpInput {
  magic_link_token: string;
}

export interface StartOtpResponse {
  grant_id: string;
  email: string;
  otp_expires_at: string;
}

export interface VerifyOtpInput {
  grant_id: string;
  otp_code: string;
}

export interface VerifyOtpResponse {
  session_token: string;
}

export interface ExternalGetBinderResponse {
  binder: StudySiteBinderRow;
  folders: BinderFolderRow[];
  /** Punch-list DT2-BLIND (Spike F fix) — true when this reviewer's grant is blinded. */
  blinded?: boolean;
}

/**
 * Per-version row returned to external reviewers — extends the internal
 * `DocumentVersionRow` with the synthetic `signed_pdf_url` field used by
 * the BE redaction matrix (always null today; see
 * DreamIT-Backend/src/handlers/access/externalListDocs.ts for the rationale).
 */
export type ExternalDocumentVersionRow = DocumentVersionRow & {
  signed_pdf_url: string | null;
};

export interface ExternalListDocsResponse {
  documents: BinderDocumentRow[];
  current_versions: Record<string, ExternalDocumentVersionRow | null>;
  /** Punch-list DT2-BLIND (Spike F fix) — true when this reviewer's grant is blinded. */
  blinded?: boolean;
}

/**
 * Off-platform reviewer side of the external-access flow. Used by the
 * public `/external/grant` landing page + the scoped binder viewer.
 *
 * NONE of these calls touch the internal `dreamit2:session` token.
 * `startOtp` / `verifyOtp` are fully unauthenticated; `getBinder` /
 * `listDocs` take the scoped external-review session token as an explicit
 * argument so the caller controls exactly which credential goes on the wire.
 */
export const externalAccess = {
  /**
   * POST /external/grant/start-otp — resolve the magic-link token to a
   * grant, mint a fresh 6-digit OTP, and email it to the reviewer.
   * UNAUTHENTICATED.
   */
  startOtp: (magic_link_token: string) =>
    externalApi<StartOtpResponse>('/external/grant/start-otp', {
      method: 'POST',
      body: { magic_link_token } satisfies StartOtpInput,
    }),

  /**
   * POST /external/grant/verify-otp — verify the 6-digit OTP, mint a
   * scope-bounded session JWT. UNAUTHENTICATED.
   */
  verifyOtp: (grant_id: string, otp_code: string) =>
    externalApi<VerifyOtpResponse>('/external/grant/verify-otp', {
      method: 'POST',
      body: { grant_id, otp_code } satisfies VerifyOtpInput,
    }),

  /**
   * GET /external/binders/{binderId} — fetch the binder + folder tree for
   * the external reviewer. Sends the scoped session token as `Authorization:
   * Bearer ...` instead of reusing the internal session token.
   */
  getBinder: (binderId: string, sessionToken: string) =>
    externalApi<ExternalGetBinderResponse>(
      `/external/binders/${encodeURIComponent(binderId)}`,
      { sessionToken },
    ),

  /**
   * GET /external/binders/{binderId}/folders/{folderId}/documents — fetch
   * the documents in a folder with PHI redaction applied per the grant's
   * scope.
   */
  listDocs: (binderId: string, folderId: string, sessionToken: string) =>
    externalApi<ExternalListDocsResponse>(
      `/external/binders/${encodeURIComponent(binderId)}/folders/${encodeURIComponent(
        folderId,
      )}/documents`,
      { sessionToken },
    ),

  /**
   * Sprint-18 (eISF spec §6 + permission matrix) — external preview of a
   * document. Server refuses the URL when the grant scope is
   * view_pii_redacted AND the doc has phi_present=true.
   */
  getDocumentPreview: (binderId: string, docId: string, sessionToken: string) =>
    externalApi<ExternalDocumentPreviewResponse>(
      `/external/binders/${encodeURIComponent(binderId)}/documents/${encodeURIComponent(docId)}/preview`,
      { sessionToken },
    ),
};

// ============================================================================
// Sprint-18 — eISF spec extensions: tags + advanced search + permissions matrix
// ============================================================================

export interface RegulatoryTagRow {
  tag_id: string;
  tenant_id: string;
  code: string;
  label: string;
  category: string;
  is_active: boolean;
  created_at: string;
  created_by: string;
}

export const regulatoryTags = {
  /** All active tags visible to the caller's tenant (own rows + globals). */
  list: () => api<{ tags: RegulatoryTagRow[] }>('/regulatory-tags'),

  /** Mint a new tenant-scoped tag. Site Admin / Reg Coord only. */
  create: (body: { code: string; label: string; category: string }) =>
    api<{ tag: RegulatoryTagRow }>('/regulatory-tags', { method: 'POST', body }),
};

export interface SearchDocumentsParams {
  text?: string;
  study_ids?: string[];
  binder_ids?: string[];
  document_type_names?: string[];
  statuses?: string[];
  tag_ids?: string[];
  phi_only?: boolean;
  expires_within_days?: number;
  limit?: number;
  offset?: number;
}

export interface SearchDocumentResultRow {
  document_id: string;
  reference_number: string | null;
  title: string;
  document_type_name: string;
  status: string;
  phi_present: boolean;
  expires_at: string | null;
  binder_id: string;
  binder_reference_number: string | null;
  study_id: string;
  tag_ids: string[];
}

export interface SearchDocumentsResponse {
  total: number;
  limit: number;
  offset: number;
  rows: SearchDocumentResultRow[];
}

export const eisfSearch = {
  /** Sprint-18 (eISF spec §functional cap #5) — advanced search across binders. */
  documents: (params: SearchDocumentsParams = {}) =>
    api<SearchDocumentsResponse>('/search/documents', { method: 'POST', body: params }),
};

export type EisfOperation =
  | 'configure_binder_templates'
  | 'create_or_rename_folder'
  | 'upload_document_version'
  | 'edit_document_metadata'
  | 'start_approval_workflow'
  | 'approve_document'
  | 'initiate_esignature'
  | 'lock_or_unlock_document'
  | 'configure_expiration_rules'
  | 'grant_remote_access'
  | 'view_binder_full'
  | 'download_documents'
  | 'manage_regulatory_tags'
  | 'checkout_document'
  | 'bulk_export_binder'
  /** Phase 2 Defect 2.1 — Create a new document slot in a folder. */
  | 'create_document_slot'
  /** Punch-list b14 — highlight/note a document before sign-off. */
  | 'annotate_document'
  /** Monitors/auditors: read the granted binder, PHI documents redacted (1 Oct 2026). */
  | 'view_binder_redacted';

export interface EisfPermissionsResponse {
  role: string;
  matrix: Record<EisfOperation, string[]>;
  your_permissions: Record<EisfOperation, boolean>;
}

export const eisfPermissions = {
  /** Sprint-18 (eISF spec §permission matrix) — server's matrix + caller's allow-set. */
  get: () => api<EisfPermissionsResponse>('/eisf/permissions'),
};

// ----- Type stubs referenced above -----

export interface ExternalDocumentPreviewResponse {
  document: {
    document_id: string;
    reference_number: string | null;
    folder_id: string;
    folder_breadcrumb: string;
    document_type_name: string;
    title: string;
    phi_present: boolean;
    is_locked: boolean;
  };
  version: {
    version_id: string;
    version_label: string;
    version_number: number;
    file_name: string;
    file_size: number;
    mime_type: string;
    uploaded_at: string;
    signed_at: string | null;
  };
  presigned_get_url: string;
  preview_url: string | null;
  preview_mime_type: 'application/pdf' | null;
  expires_in_seconds: number;
}

export interface BulkExportManifestRow {
  document_id: string;
  document_reference_number: string | null;
  document_title: string;
  document_type_name: string;
  folder_breadcrumb: string;
  version_id: string;
  version_label: string;
  version_number: number;
  status: string;
  mime_type: string;
  file_name: string;
  file_size: number;
  uploaded_at: string;
  checksum_sha256: string | null;
  phi_present: boolean;
  download_url: string;
}

export interface BulkExportResponse {
  binder_id: string;
  binder_reference_number: string | null;
  generated_at: string;
  expires_at: string;
  total_versions: number;
  phi_count: number;
  include_history: boolean;
  rows: BulkExportManifestRow[];
}

// ---------------------------------------------------------------------------
// Phase 5 — Adverse Event / SAE tracking
// ---------------------------------------------------------------------------

export type AdverseEventCurrentStatus =
  | 'ongoing'
  | 'resolved'
  | 'resolved_with_sequelae'
  | 'condition_worsening';

export type AdverseEventActionTaken =
  | 'none'
  | 'reduced'
  | 'interrupted'
  | 'discontinued'
  | 'other';

export interface AdverseEventRow {
  ae_id: string;
  reference_number: string;
  study_id: string;
  site_id: string;
  subject_id: string;
  visit_id: string | null;
  description: string;
  start_date: string;
  stop_date: string | null;
  is_serious: boolean;
  grade: string;
  relationship: string;
  is_anticipated: boolean;
  action_taken: AdverseEventActionTaken;
  other_actions: string | null;
  reported_to_sponsor: boolean;
  reported_to_sponsor_date: string | null;
  reported_to_irb: boolean;
  reported_to_irb_date: string | null;
  pi_initials: string;
  pi_signed_date: string;
  sae_category: string | null;
  current_status: AdverseEventCurrentStatus;
  created_at: string;
  created_by: string;
  updated_at: string;
  updated_by: string;
}

export interface AdverseEventStatusHistoryRow {
  history_id: string;
  ae_id: string;
  from_status: AdverseEventCurrentStatus | null;
  to_status: AdverseEventCurrentStatus;
  visit_id: string | null;
  changed_at: string;
  changed_by: string;
  reason_notes: string;
}

export interface AeOption {
  value: string;
  label: string;
}

export interface StudyAeConfigResponse {
  config: {
    config_id: string;
    study_id: string;
    grading_scale_key: 'descriptive' | 'ctcae_v5' | 'other';
    grading_options: AeOption[];
    relationship_options: AeOption[];
    sae_categories: AeOption[] | null;
    updated_at: string;
    updated_by: string;
  };
}

export interface AdverseEventCreateBody {
  subject_id: string;
  visit_id?: string;
  description: string;
  start_date: string;
  stop_date?: string;
  is_serious: boolean;
  grade: string;
  relationship: string;
  is_anticipated: boolean;
  action_taken: AdverseEventActionTaken;
  other_actions?: string;
  reported_to_sponsor: boolean;
  reported_to_sponsor_date?: string;
  reported_to_irb: boolean;
  reported_to_irb_date?: string;
  pi_initials: string;
  pi_signed_date: string;
  sae_category?: string;
  initial_status?: AdverseEventCurrentStatus;
}

export const adverseEvents = {
  create: (body: AdverseEventCreateBody) =>
    api<{ ae_id: string; reference_number: string; current_status: AdverseEventCurrentStatus; created_at: string }>(
      '/adverse-events',
      { method: 'POST', body },
    ),

  list: (filters: {
    study_id?: string;
    subject_id?: string;
    status?: AdverseEventCurrentStatus;
    is_serious?: boolean;
    site_id?: string;
    limit?: number;
  } = {}) =>
    api<{ rows: AdverseEventRow[]; count: number }>(`/adverse-events${qs(filters)}`),

  detail: (aeId: string) =>
    api<{ adverse_event: AdverseEventRow; status_history: AdverseEventStatusHistoryRow[] }>(
      `/adverse-events/${encodeURIComponent(aeId)}`,
    ),

  update: (aeId: string, patch: Partial<AdverseEventCreateBody>) =>
    api<{ ae_id: string; updated_at: string }>(
      `/adverse-events/${encodeURIComponent(aeId)}`,
      { method: 'PATCH', body: patch },
    ),

  updateStatus: (
    aeId: string,
    body: { to_status: AdverseEventCurrentStatus; reason_notes: string; visit_id?: string },
  ) =>
    api<{ ae_id: string; current_status: AdverseEventCurrentStatus; changed_at: string }>(
      `/adverse-events/${encodeURIComponent(aeId)}/status`,
      { method: 'POST', body },
    ),

  listForVisit: (visitId: string) =>
    api<{ visit_id: string; subject_id: string; rows: AdverseEventRow[]; count: number }>(
      `/visits/${encodeURIComponent(visitId)}/open-adverse-events`,
    ),
};

export const studyAeConfig = {
  get: (studyId: string) =>
    api<StudyAeConfigResponse>(`/studies/${encodeURIComponent(studyId)}/ae-config`),

  update: (studyId: string, patch: {
    grading_scale_key?: 'descriptive' | 'ctcae_v5' | 'other';
    grading_options?: AeOption[];
    relationship_options?: AeOption[];
    sae_categories?: AeOption[] | null;
  }) =>
    api<{ study_id: string; updated_at: string; fields_changed: string[] }>(
      `/studies/${encodeURIComponent(studyId)}/ae-config`,
      { method: 'PATCH', body: patch },
    ),
};

// ---------------------------------------------------------------------------
// Phase 1 Item 11 — Training log module
// ---------------------------------------------------------------------------

export type TrainingCertificationStatus =
  | 'assigned'
  | 'in_progress'
  | 'completed'
  | 'superseded';

export interface TrainingCertificationRow {
  certification_id: string;
  reference_number: string;
  user_id: string;
  /** Mutually exclusive with manual_training_id below. */
  document_id: string | null;
  document_version_id: string | null;
  /** Standalone/external training — set instead of document_id/document_version_id. */
  manual_training_id?: string | null;
  study_id: string;
  site_id: string;
  task_id: string | null;
  status: TrainingCertificationStatus;
  assigned_at: string;
  completed_at: string | null;
  signer_user_id: string | null;
  signature_meaning: string | null;
  superseded_by_certification_id: string | null;
  superseded_at: string | null;
  /** Training module Phase 4 — read gate. Null = not yet confirmed. */
  read_confirmed_at?: string | null;
  /** Training module Phase 4 — PI/delegate countersignature. */
  countersigner_user_id?: string | null;
  countersigned_at?: string | null;
  /** Training e-sign — the trainee-then-PI DocuSeal envelope. */
  docuseal_submission_id?: string | null;
  docuseal_template_id?: string | null;
  signed_pdf_url?: string | null;
  audit_log_url?: string | null;
  binder_document_id?: string | null;
  binder_version_id?: string | null;
  /** Training e-sign — set once the PI hands countersignature off to a Sub-Investigator. */
  countersign_delegate_user_id?: string | null;
  countersign_reassigned_at?: string | null;
  countersign_reassigned_by?: string | null;
  /** The study's actual PI at read time — resolves who may act as countersigner (countersign_delegate_user_id when set, else this). Null when the study has no PI delegated. */
  study_pi_user_id?: string | null;
  /** Punch-list b9 — training log Study column + group-by-study. */
  study_protocol_number?: string | null;
  /**
   * The study's NAME. Client rule, 2026-10-09: "use Name as Primary always".
   * The log grouped on the protocol number alone, so the page read
   * "STD-2026-0020 (6)" instead of naming the study.
   */
  study_title?: string | null;
  /** Standalone/external training — optional trainee-attached completion certificate. */
  completion_certificate_s3_bucket?: string | null;
  completion_certificate_s3_key?: string | null;
  completion_certificate_file_name?: string | null;
  completion_certificate_file_size?: number | null;
  created_at: string;
  created_by: string;
  updated_at: string;
  updated_by: string;
  /** Server-enriched — see services/training/enrichCertifications.ts. */
  trainee_name: string;
  trainee_email: string;
  document_type_name: string;
  document_title: string;
  countersigner_name: string | null;
  /** Standalone/external training only — undefined for binder-document-sourced rows. */
  manual_training_kind?: 'internal' | 'external';
  manual_training_external_link?: string | null;
}

// ---------------------------------------------------------------------------
// Phase 7A — eSource Designer form module
// ---------------------------------------------------------------------------

export type FormStatus = 'draft' | 'published' | 'archived';

export type FormFieldType =
  | 'text'
  | 'number'
  | 'date'
  | 'datetime'
  | 'select'
  | 'radio'
  | 'checkbox'
  | 'textarea'
  | 'calculated';

export interface FormRow {
  form_id: string;
  reference_number: string;
  study_id: string;
  form_key: string;
  name: string;
  description: string;
  status: FormStatus;
  current_version_id: string | null;
  draft_fields_json: string | null;
  created_at: string;
  created_by: string;
  updated_at: string;
  updated_by: string;
  /** Present when the forms list is scoped to a subject+visit. */
  instance_status?: 'not_started' | 'in_progress' | 'completed';
  /** The subject+visit's instance of this form, when one exists. */
  instance_id?: string | null;
}

export interface FormVersionRow {
  form_version_id: string;
  form_id: string;
  version_label: string;
  version_number: number;
  fields_json: string;
  published_at: string;
  published_by: string;
  superseded_at: string | null;
  superseded_by_version_id: string | null;
}

export interface FormFieldDefinitionRow {
  field_id: string;
  form_version_id: string;
  field_key: string;
  field_label: string;
  field_type: FormFieldType;
  field_order: number;
  config_json: string;
}

export type FormInstanceStatus = 'not_started' | 'in_progress' | 'completed';

export interface FormInstanceRow {
  instance_id: string;
  subject_id: string;
  visit_id: string;
  form_id: string;
  form_version_id: string;
  status: FormInstanceStatus;
  started_at: string | null;
  submitted_at: string | null;
  submitted_by: string | null;
}

export interface FormFieldResponseRow {
  response_id: string;
  reference_number: string;
  instance_id: string;
  field_id: string;
  value_text: string | null;
  value_number: number | null;
  value_date: string | null;
  value_json: string | null;
  entered_at: string;
  entered_by: string;
  changed_at: string;
  changed_by: string;
  correction_reason: string | null;
}

/**
 * Mutable form <-> visit-template bindings. Powers the visit-first
 * Forms UI: coordinator opens a visit, sees every form in the study
 * with a checkbox indicating whether it's assigned to this visit.
 */

export interface FormForVisitRow {
  form_id: string;
  reference_number: string;
  form_key: string;
  name: string;
  description: string;
  status: 'draft' | 'published' | 'archived';
  current_version_id: string | null;
  assigned: boolean;
  visibility_rule_json?: string | null;
}

export const formVisitBindings = {
  assign: (
    formId: string,
    visitTemplateId: string,
    body?: { visibility_rule?: ConditionExpr | null },
  ) =>
    api<{
      form_visit_binding_id: string;
      form_id: string;
      visit_template_id: string;
      assigned_at: string;
      visibility_rule_json: string | null;
      created: boolean;
    }>(
      `/forms/${encodeURIComponent(formId)}/visit-templates/${encodeURIComponent(visitTemplateId)}`,
      { method: 'PUT', body: body ?? {} },
    ),

  /** DELETE — idempotent unassign. Returns `{ deleted: true|false }`. */
  unassign: (formId: string, visitTemplateId: string) =>
    api<{ form_id: string; visit_template_id: string; deleted: boolean }>(
      `/forms/${encodeURIComponent(formId)}/visit-templates/${encodeURIComponent(visitTemplateId)}`,
      { method: 'DELETE' },
    ),

  /** GET — all forms in the visit's study, each with an `assigned` flag for this visit. */
  listFormsForVisit: (visitTemplateId: string) =>
    api<{
      visit_template_id: string;
      visit_name: string;
      visit_order: number;
      study_id: string;
      /** Locked study: attach, detach, conditions and sequence are frozen. */
      study_locked?: boolean;
      /** Assigned forms first, in the visit's sequence; unassigned after. */
      forms: FormForVisitRow[];
    }>(`/visit-templates/${encodeURIComponent(visitTemplateId)}/forms`),

  /** PUT — the visit's attached forms in sequence; must list every one exactly once. */
  reorder: (visitTemplateId: string, formIds: string[]) =>
    api<{ visit_template_id: string; form_ids: string[]; changed: boolean }>(
      `/visit-templates/${encodeURIComponent(visitTemplateId)}/forms/order`,
      { method: 'PUT', body: { form_ids: formIds } },
    ),

  /** GET — combined preview of every assigned + published form for a visit.
   *  Skips drafts and unpublished forms; there is nothing definitive to
   *  preview until they're published. */
  previewForVisit: (visitTemplateId: string) =>
    api<{
      visit_template_id: string;
      visit_name: string;
      visit_order: number;
      study_id: string;
      forms: Array<{
        form_id: string;
        reference_number: string;
        form_key: string;
        name: string;
        description: string;
        version_label: string;
        version_number: number;
        fields: Array<{
          field_key: string;
          field_label: string;
          field_type: string;
          field_order?: number;
          config?: {
            required?: boolean;
            help_text?: string;
            min?: number;
            max?: number;
            max_length?: number;
            options?: Array<{ value: string; label: string }>;
          };
        }>;
      }>;
    }>(`/visit-templates/${encodeURIComponent(visitTemplateId)}/preview`),
};

/**
 * Field-Template Library — reusable field-group templates the Form
 * Designer palette drags onto forms. Each template's
 * `template_fields_json` is a JSON string of field defs; the caller
 * treats it as an opaque payload (parses at drop time in the designer).
 */
export interface FieldTemplateRow {
  field_template_id: string;
  reference_number: string;
  template_key: string;
  name: string;
  description: string | null;
  category: string | null;
  icon: string | null;
  template_fields_json: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export const fieldTemplates = {
  create: (body: {
    template_key: string;
    name: string;
    description?: string;
    category?: string;
    icon?: string;
    template_fields_json: string;
    is_active?: boolean;
  }) =>
    api<{ field_template_id: string; reference_number: string; created_at: string }>(
      '/field-templates',
      { method: 'POST', body },
    ),

  list: (params?: { category?: string; activeOnly?: boolean }) => {
    const qs = new URLSearchParams();
    if (params?.category) qs.set('category', params.category);
    if (params?.activeOnly) qs.set('active_only', 'true');
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return api<{ templates: FieldTemplateRow[] }>(`/field-templates${suffix}`);
  },

  detail: (fieldTemplateId: string) =>
    api<
      FieldTemplateRow & { created_by: string; updated_by: string }
    >(`/field-templates/${encodeURIComponent(fieldTemplateId)}`),

  update: (
    fieldTemplateId: string,
    body: {
      template_key?: string;
      name?: string;
      description?: string;
      category?: string;
      icon?: string;
      template_fields_json?: string;
      is_active?: boolean;
    },
  ) =>
    api<{ field_template_id: string; updated_at: string }>(
      `/field-templates/${encodeURIComponent(fieldTemplateId)}`,
      { method: 'PATCH', body },
    ),

  remove: (fieldTemplateId: string) =>
    api<{ field_template_id: string; deleted: boolean }>(
      `/field-templates/${encodeURIComponent(fieldTemplateId)}`,
      { method: 'DELETE' },
    ),
};

export const forms = {
  create: (body: {
    study_id: string;
    form_key?: string;
    name: string;
    description?: string;
    draft_fields_json?: string;
  }) =>
    api<{
      form_id: string;
      reference_number: string;
      status: 'draft';
      created_at: string;
    }>('/forms', { method: 'POST', body }),

  /**
   * `visit_id` — Phase 7E AC16 (visit-binding policy A).
   * When supplied, BE returns only forms whose current published version
   * is bound to this visit's template. Unscheduled visits return all
   * published + bound forms. Drives VisitFormsSection on the Visits &
   * eSource page.
   */
  list: (filters: { study_id?: string; visit_id?: string; subject_id?: string; status?: FormStatus; limit?: number } = {}) =>
    api<{
      rows: FormRow[];
      count: number;
      blinded?: boolean;
      blinded_role_master_ref?: string | null;
      esource_restricted?: boolean;
      esource_restricted_study_ids?: string[];
    }>(`/forms${qs(filters)}`),

  detail: (idOrRef: string) =>
    api<{
      form: FormRow;
      current_version: FormVersionRow | null;
      field_definitions: FormFieldDefinitionRow[];
      blinded?: boolean;
      blinded_role_master_ref?: string | null;
      esource_restricted?: boolean;
      /**
       * True when this form is live on an active study and re-publishing it
       * needs an approved protocol amendment first (Feedback, Study details
       * item 5). The refusal itself is enforced by POST /forms/{id}/publish.
       */
      publish_requires_amendment?: boolean;
      publish_blocked_reason?: string | null;
    }>(`/forms/${encodeURIComponent(idOrRef)}`),

  updateDefinition: (
    formId: string,
    patch: {
      name?: string;
      description?: string;
      draft_fields_json?: string;
    },
  ) =>
    api<{ form_id: string; updated_at: string }>(
      `/forms/${encodeURIComponent(formId)}/definition`,
      { method: 'PATCH', body: patch },
    ),

  publish: (
    formId: string,
    body: {
      version_label?: string;
      /** Phase 7E AC16 — bind version to protocol amendment. */
      protocol_version_id?: string;
      /**
       * Phase 7E AC16 (visit-binding policy A) — REQUIRED, non-empty.
       * BE rejects publish without at least one visit_template_id;
       * forms with no binding stay in draft (invisible to data entry).
       */
      visit_template_ids: string[];
      /** DoA responsibility a user must hold (delegation approved) to fill. */
      required_responsibility_code?: string;
      /** Restrict filling to Principal / Sub-Investigator only. */
      pi_si_only?: boolean;
    },
  ) =>
    api<{
      form_id: string;
      form_version_id: string;
      version_label: string;
      version_number: number;
      published_at: string;
      field_count: number;
    }>(`/forms/${encodeURIComponent(formId)}/publish`, { method: 'POST', body }),

  /** Server-built single PDF of a visit's forms (returns a download URL). */
  visitPdf: (subjectId: string, visitId: string) =>
    api<{ download_url: string; file_name: string; form_count: number; expires_in_seconds: number }>(
      `/subjects/${encodeURIComponent(subjectId)}/visits/${encodeURIComponent(visitId)}/forms/pdf`,
    ),

  /** Global read-only CDISC form-template library. */
  library: () =>
    api<{
      rows: Array<{
        form_template_id: string;
        reference_number: string;
        template_key: string;
        name: string;
        description: string;
        category: string | null;
        domain_code: string | null;
      }>;
      count: number;
    }>('/forms/library'),

  /** Clone a global form template into a study as a new draft form. */
  cloneTemplate: (templateId: string, body: { target_study_id: string; name?: string }) =>
    api<{
      form_id: string;
      reference_number: string;
      status: 'draft';
      created_at: string;
    }>(`/forms/library/${encodeURIComponent(templateId)}/clone`, { method: 'POST', body }),

  /** `target_study_id` lands the clone on a different study than the
   *  source — omit to keep the original same-study behavior. */
  clone: (idOrRef: string, body: { form_key?: string; name?: string; target_study_id?: string } = {}) =>
    api<{
      form_id: string;
      reference_number: string;
      form_key: string;
      status: 'draft';
      source_form_id: string;
      source_reference_number: string;
      created_at: string;
    }>(`/forms/${encodeURIComponent(idOrRef)}/clone`, {
      method: 'POST',
      body,
    }),

  /** Step 2 of the "Copy to sites" modal — studies at a chosen target site. */
  listCopyToSitesStudies: (idOrRef: string, siteId: string) =>
    api<{
      site_id: string;
      studies: Array<{ study_id: string; protocol_number: string; title: string }>;
    }>(
      `/forms/${encodeURIComponent(idOrRef)}/copy-to-sites/studies?site_id=${encodeURIComponent(siteId)}`,
    ),

  /**
   * Bug found live (UAT: "Copy form design to sites is looking for Study
   * to be available in the target sites, this should be independent of
   * study"). No more protocol-number auto-matching — the caller resolves
   * a site, then an explicit multi-select of that site's studies (with
   * "All"), and sends the real target study ids directly.
   */
  copyToSites: (
    idOrRef: string,
    body: { target_study_ids: string[] },
  ) =>
    api<{
      source_form_id: string;
      source_reference_number: string;
      results: Array<{
        study_id: string;
        site_id: string | null;
        status:
        | 'copied'
        | 'skipped_self'
        | 'skipped_duplicate'
        | 'skipped_not_found'
        | 'skipped_error';
        form_id: string | null;
        reference_number: string | null;
        reason: string | null;
      }>;
      copied_count: number;
      total_targets: number;
    }>(`/forms/${encodeURIComponent(idOrRef)}/copy-to-sites`, {
      method: 'POST',
      body,
    }),

  versions: (idOrRef: string) =>
    api<{
      form_id: string;
      reference_number: string;
      current_version_id: string | null;
      rows: Array<{
        form_version_id: string;
        version_label: string;
        version_number: number;
        published_at: string;
        published_by: string;
        is_current: boolean;
        superseded_at: string | null;
        superseded_by_version_id: string | null;
      }>;
      count: number;
    }>(`/forms/${encodeURIComponent(idOrRef)}/versions`),

  versionDetail: (idOrRef: string, versionId: string) =>
    api<{
      form_id: string;
      version: FormVersionRow;
      field_definitions: FormFieldDefinitionRow[];
      is_current: boolean;
    }>(
      `/forms/${encodeURIComponent(idOrRef)}/versions/${encodeURIComponent(versionId)}`,
    ),
};

// ---------------------------------------------------------------------------
// Phase 7D — Procedure Library + Conditional Rules
// ---------------------------------------------------------------------------

export type ProcedureScope = 'global' | 'tenant' | 'study';

export interface ProcedureRow {
  procedure_id: string;
  reference_number: string;
  scope: ProcedureScope;
  scope_id: string | null;
  code: string;
  label: string;
  description: string;
  current_version_id: string | null;
  active: string;
  created_at: string;
  created_by: string;
  current_fields_json: string;
  current_version_number: number | null;
}

export type ConditionOperator =
  | 'eq' | 'neq' | 'gt' | 'gte' | 'lt' | 'lte'
  | 'in' | 'notin' | 'exists' | 'empty' | 'between';

export interface ConditionPredicate {
  op: ConditionOperator;
  field: string;
  value?: string | number | boolean | Array<string | number | boolean> | null;
}

export type ConditionExpr =
  | ConditionPredicate
  | { all: ConditionExpr[] }
  | { any: ConditionExpr[] };

export interface FormFieldConditionalRuleRow {
  rule_id: string;
  form_version_id: string;
  field_id: string;
  action: 'show' | 'hide';
  condition_expr: string;
  created_at: string;
  created_by: string;
}

export const procedures = {
  list: (filters: { study_id?: string; active_only?: boolean } = {}) => {
    const qsFilters: Record<string, string> = {};
    if (filters.study_id) qsFilters.study_id = filters.study_id;
    if (filters.active_only === false) qsFilters.active_only = 'false';
    return api<{ rows: ProcedureRow[]; count: number }>(`/procedures${qs(qsFilters)}`);
  },

  create: (body: {
    scope: ProcedureScope;
    study_id?: string;
    code: string;
    label: string;
    description?: string;
    fields: Array<{
      field_key: string;
      field_label: string;
      field_type: FormFieldType;
      field_order?: number;
      config?: Record<string, unknown>;
    }>;
  }) =>
    api<{
      procedure_id: string;
      reference_number: string;
      procedure_version_id: string;
      version_label: string;
      field_count: number;
      created_at: string;
    }>('/procedures', { method: 'POST', body }),
};

// Phase 7E AC19 — Schedule-of-Activities matrix.

export interface VisitTemplateRow {
  template_visit_id: string;
  study_id: string;
  visit_name: string;
  visit_order: number;
  nominal_day: number;
  window_minus_days: number;
  window_plus_days: number;
  required_visit_type: string;
  created_at: string;
  reference_number?: string | null;
  is_required?: string | null;
  include_standard_vitals?: string | null;
  created_by?: string | null;
  updated_at?: string | null;
  updated_by?: string | null;
}

export interface VisitProcedureAssignmentRow {
  assignment_id: string;
  reference_number: string;
  visit_template_id: string;
  procedure_id: string;
  created_at: string;
  created_by: string;
}

export const schedule = {
  get: (studyId: string) =>
    api<{
      study_id: string;
      visits: VisitTemplateRow[];
      assignments: VisitProcedureAssignmentRow[];
      counts: { visit_count: number; assignment_count: number };
    }>(`/studies/${encodeURIComponent(studyId)}/schedule`),

  createVisit: (
    studyId: string,
    body: {
      visit_name: string;
      visit_order?: number;
      day_offset: number;
      window_minus_days?: number;
      window_plus_days?: number;
      is_required?: boolean;
      include_standard_vitals?: boolean;
    },
  ) =>
    api<{
      template_visit_id: string;
      reference_number: string;
      created_at: string;
    }>(`/studies/${encodeURIComponent(studyId)}/schedule/visits`, {
      method: 'POST',
      body,
    }),

  updateVisit: (
    studyId: string,
    templateVisitId: string,
    body: {
      visit_name?: string;
      visit_order?: number;
      day_offset?: number;
      window_minus_days?: number;
      window_plus_days?: number;
      is_required?: boolean;
      include_standard_vitals?: boolean;
    },
  ) =>
    api<{ template_visit_id: string; updated_at: string }>(
      `/studies/${encodeURIComponent(studyId)}/schedule/visits/${encodeURIComponent(templateVisitId)}`,
      { method: 'PATCH', body },
    ),

  deleteVisit: (studyId: string, templateVisitId: string) =>
    api<{ template_visit_id: string; deleted: true }>(
      `/studies/${encodeURIComponent(studyId)}/schedule/visits/${encodeURIComponent(templateVisitId)}`,
      { method: 'DELETE' },
    ),

  putAssignments: (
    studyId: string,
    templateVisitId: string,
    body: { procedure_ids: string[] },
  ) =>
    api<{ template_visit_id: string; assignments: number }>(
      `/studies/${encodeURIComponent(studyId)}/schedule/visits/${encodeURIComponent(templateVisitId)}/procedures`,
      { method: 'PUT', body },
    ),
};

export const formRules = {
  list: (idOrRef: string, versionId: string) =>
    api<{ rows: FormFieldConditionalRuleRow[]; count: number }>(
      `/forms/${encodeURIComponent(idOrRef)}/versions/${encodeURIComponent(versionId)}/rules`,
    ),

  add: (
    idOrRef: string,
    versionId: string,
    body: {
      field_id: string;
      action: 'show' | 'hide';
      condition_expr: ConditionExpr;
    },
  ) =>
    api<{ rule: FormFieldConditionalRuleRow }>(
      `/forms/${encodeURIComponent(idOrRef)}/versions/${encodeURIComponent(versionId)}/rules`,
      { method: 'POST', body },
    ),

  remove: (ruleId: string) =>
    api<{ rule_id: string; deleted: true }>(
      `/form-rules/${encodeURIComponent(ruleId)}`,
      { method: 'DELETE' },
    ),
};

/** Phase 7B — per-field validation violation returned by both saveDraft (soft) and submit-failure (hard). */
export interface FormFieldViolation {
  field_id: string;
  field_key: string;
  rule: 'required' | 'min' | 'max' | 'max_length';
  message: string;
}

export const formInstances = {
  start: (subjectId: string, visitId: string, formId: string) =>
    api<{
      instance_id: string;
      form_version_id: string;
      status: FormInstanceStatus;
      already_existed: boolean;
      started_at?: string;
    }>(
      `/subjects/${encodeURIComponent(subjectId)}/visits/${encodeURIComponent(visitId)}/forms/${encodeURIComponent(formId)}/start`,
      { method: 'POST', body: {} },
    ),

  get: (instanceId: string) =>
    api<{
      instance: FormInstanceRow;
      form_version: FormVersionRow | null;
      field_definitions: FormFieldDefinitionRow[];
      current_values: FormFieldResponseRow[];
      response_history: FormFieldResponseRow[];
      conditional_rules: FormFieldConditionalRuleRow[];
      hidden_field_ids: string[];
      subject_attrs?: Record<string, unknown>;
      study_attrs?: Record<string, unknown>;
      blinded?: boolean;
      blinded_role_master_ref?: string | null;
      esource_restricted?: boolean;
      /** Set when the form is hidden because the caller lacks its delegated responsibility. */
      esource_restricted_reason?: 'responsibility_required';
      /** False when a PI/SI-only form is viewed by someone who is not PI/SI. */
      can_fill?: boolean;
      fill_restricted_message?: string | null;
    }>(`/form-instances/${encodeURIComponent(instanceId)}`),

  saveDraft: (
    instanceId: string,
    body: {
      responses: Array<{
        field_id: string;
        value: string | number | string[] | null;
        correction_reason?: string;
      }>;
    },
  ) =>
    api<{
      instance_id: string;
      responses_saved: number;
      corrections: number;
      status: FormInstanceStatus;
      changed_at: string;
      violations: FormFieldViolation[];
      hidden_field_ids: string[];
      /** Bug found live (UAT audit): a per-field DB insert can fail
       *  independently of validation — these never made it to storage
       *  despite `responses_saved` not counting them, so the caller
       *  must not assume "no violations" means "everything persisted". */
      failed_fields: Array<{ field_id: string; field_key: string; message: string }>;
    }>(`/form-instances/${encodeURIComponent(instanceId)}/draft`, {
      method: 'PATCH',
      body,
    }),

  submit: (instanceId: string) =>
    api<{
      instance_id: string;
      status: 'completed';
      submitted_at: string;
    }>(`/form-instances/${encodeURIComponent(instanceId)}/submit`, {
      method: 'POST',
      body: {},
    }),

  /** Open a submitted form for correction (visit must be unlocked). */
  correct: (instanceId: string, reason: string) =>
    api<{ instance_id: string; status: 'in_progress' }>(
      `/form-instances/${encodeURIComponent(instanceId)}/correct`,
      { method: 'POST', body: { reason } },
    ),

  undoField: (
    instanceId: string,
    fieldId: string,
    body: { reason?: string } = {},
  ) =>
    api<{
      response_id: string;
      reference_number: string;
      undo_of_response_id: string;
      undo_of_reference_number: string;
      changed_at: string;
    }>(
      `/form-instances/${encodeURIComponent(instanceId)}/fields/${encodeURIComponent(fieldId)}/undo`,
      { method: 'POST', body },
    ),

  fieldHistory: (instanceId: string, fieldId: string) =>
    api<{
      instance_id: string;
      field_id: string;
      field_key: string;
      field_label: string;
      entries: Array<{
        response_id: string;
        reference_number: string;
        field_id: string;
        field_key: string;
        field_label: string;
        field_type: string;
        subject_id: string;
        visit_id: string;
        study_id: string;
        site_id: string;
        old_value: {
          value_text: string | null;
          value_number: number | null;
          value_date: string | null;
          value_json: string | null;
        } | null;
        new_value: {
          value_text: string | null;
          value_number: number | null;
          value_date: string | null;
          value_json: string | null;
        };
        correction_reason: string | null;
        changed_at: string;
        changed_by: string;
        changed_by_name: string | null;
        changed_by_email: string | null;
        is_create: boolean;
      }>;
      count: number;
    }>(
      `/form-instances/${encodeURIComponent(instanceId)}/fields/${encodeURIComponent(fieldId)}/history`,
    ),
};

export const training = {
  listMine: (filters: { status?: TrainingCertificationStatus; limit?: number } = {}) =>
    api<{ rows: TrainingCertificationRow[]; count: number }>(`/training/me${qs(filters)}`),

  list: (
    filters: {
      site_id?: string;
      study_id?: string;
      user_id?: string;
      document_id?: string;
      document_version_id?: string;
      status?: TrainingCertificationStatus;
      limit?: number;
    } = {},
  ) =>
    api<{ rows: TrainingCertificationRow[]; count: number }>(
      `/training/certifications${qs(filters)}`,
    ),

  /** Training module Phase 4 — read gate. Must succeed before start-signing(). */
  confirmRead: (certificationId: string) =>
    api<{ certification_id: string; read_confirmed_at: string }>(
      `/training/certifications/${encodeURIComponent(certificationId)}/confirm-read`,
      { method: 'POST', body: {} },
    ),

  /** Training module Phase 4 — presigned URL for the read-gate PDF viewer. */
  getDocumentUrl: (certificationId: string) =>
    api<{ url: string; file_name: string; expires_in_seconds: number }>(
      `/training/certifications/${encodeURIComponent(certificationId)}/document-url`,
    ),

  /** Training e-sign — mints (or resumes) the trainee-then-PI DocuSeal envelope. */
  startSigning: (certificationId: string) =>
    api<{ certification_id: string; embed_url: string }>(
      `/training/certifications/${encodeURIComponent(certificationId)}/start-signing`,
      { method: 'POST', body: {} },
    ),

  /** Training e-sign — PI's (or delegate's) embed URL for the same envelope. */
  startCountersigning: (certificationId: string) =>
    api<{ certification_id: string; embed_url: string }>(
      `/training/certifications/${encodeURIComponent(certificationId)}/start-countersigning`,
      { method: 'POST', body: {} },
    ),

  /** Training e-sign — PI hands off countersignature to a Sub-Investigator. */
  reassignCountersign: (certificationId: string, delegateUserId: string) =>
    api<{ certification_id: string; embed_url: string }>(
      `/training/certifications/${encodeURIComponent(certificationId)}/reassign-countersign`,
      { method: 'POST', body: { delegate_user_id: delegateUserId } },
    ),

  /**
   * Training e-sign — poll + finalize fallback for the signing dialog,
   * mirroring eisfSigning.syncStatus/finalizeSync. The DocuSeal webhook
   * is currently unreachable (see webhook RCA), so this is the path that
   * actually closes out a training certification today.
   */
  syncStatus: (certificationId: string) =>
    api<{
      status: TrainingCertificationStatus;
      completed_at: string | null;
      countersigned_at: string | null;
      any_newly_completed: boolean;
    }>(`/training/certifications/${encodeURIComponent(certificationId)}/sign/status`),

  finalizeSync: (certificationId: string) =>
    api<{
      synced: boolean;
      status: TrainingCertificationStatus;
      completed_at: string | null;
      countersigned_at: string | null;
    }>(`/training/certifications/${encodeURIComponent(certificationId)}/sign/finalize`, {
      method: 'POST',
      body: {},
    }),

  /**
   * Standalone/external training — optional completion certificate a
   * trainee can attach when acknowledging an External (LMS-link)
   * training, at the same step as their e-signature.
   */
  presignCompletionCertificate: (certificationId: string, body: { file_name: string; mime_type: string }) =>
    api<{ upload_url: string; s3_bucket: string; s3_key: string; expires_in_seconds: number }>(
      `/training/certifications/${encodeURIComponent(certificationId)}/completion-certificate/presign`,
      { method: 'POST', body },
    ),

  attachCompletionCertificate: (
    certificationId: string,
    body: { s3_bucket: string; s3_key: string; file_name: string; file_size: number },
  ) =>
    api<{ certification_id: string; attached: boolean }>(
      `/training/certifications/${encodeURIComponent(certificationId)}/completion-certificate`,
      { method: 'POST', body },
    ),

  /**
   * Standalone/external training — replaces the old binder-routed
   * assignManual/listBinderDocuments (client: "Not in binder... this is
   * only on training record, training table"). Creation does NOT
   * generate certifications — it goes to the study PI for approval
   * first (see manual.approve/reject below).
   */
  manual: {
    presignUpload: (body: { file_name: string; mime_type: string }) =>
      api<{ upload_url: string; source_key: string; s3_bucket: string; s3_key: string; expires_in_seconds: number }>(
        '/training/manual/presign-upload',
        { method: 'POST', body },
      ),

    create: (body: {
      study_id: string;
      site_id: string;
      training_name: string;
      training_kind: 'internal' | 'external';
      target_completion_date?: string;
      external_link?: string;
      source?: { s3_bucket: string; s3_key: string; file_name: string; file_size: number; mime_type: string };
      user_ids?: string[];
      roles?: string[];
    }) => api<{ manual_training: ManualTrainingRow }>('/training/manual', { method: 'POST', body }),

    approve: (manualTrainingId: string) =>
      api<{ manual_training_id: string; assigned: number; skipped: number }>(
        `/training/manual/${encodeURIComponent(manualTrainingId)}/approve`,
        { method: 'POST', body: {} },
      ),

    reject: (manualTrainingId: string, reason: string) =>
      api<{ manual_training_id: string; rejected: boolean }>(
        `/training/manual/${encodeURIComponent(manualTrainingId)}/reject`,
        { method: 'POST', body: { reason } },
      ),

    /** Training page "Rejected" tab (and any other status-filtered oversight view). */
    list: (params?: { study_id?: string; status?: 'pending_approval' | 'approved' | 'rejected' }) => {
      const qs = new URLSearchParams();
      if (params?.study_id) qs.set('study_id', params.study_id);
      if (params?.status) qs.set('status', params.status);
      const suffix = qs.toString() ? `?${qs.toString()}` : '';
      return api<{ rows: EnrichedManualTrainingRow[]; count: number; blinded: boolean }>(
        `/training/manual${suffix}`,
      );
    },
  },
};

export interface ManualTrainingRow {
  manual_training_id: string;
  reference_number: string;
  study_id: string;
  site_id: string;
  training_name: string;
  training_kind: 'internal' | 'external';
  target_completion_date: string | null;
  external_link: string | null;
  source_s3_bucket: string | null;
  source_s3_key: string | null;
  source_file_name: string | null;
  source_file_size: number | null;
  source_mime_type: string | null;
  assignee_user_ids_json: string | null;
  assignee_roles_json: string | null;
  status: 'pending_approval' | 'approved' | 'rejected';
  created_by: string;
  created_at: string;
  approved_by: string | null;
  approved_at: string | null;
  rejected_by: string | null;
  rejected_at: string | null;
  rejection_reason: string | null;
  binder_document_id: string | null;
  binder_version_id: string | null;
  task_id: string | null;
}

export interface EnrichedManualTrainingRow extends ManualTrainingRow {
  created_by_name: string;
  approved_by_name: string | null;
  rejected_by_name: string | null;
}

// ---------------------------------------------------------------------------
// Phase 6 — Remote Monitoring + SDV
// ---------------------------------------------------------------------------

export interface MonitorAssignmentRow {
  assignment_id: string;
  reference_number: string;
  monitor_user_id: string;
  study_id: string;
  site_id: string;
  subject_id: string | null;
  window_start_at: string | null;
  window_end_at: string | null;
  assigned_at: string;
  assigned_by: string;
  active: string;
  revoked_at: string | null;
  revoked_by: string | null;
  /** Resolved by the list endpoint (13 Oct 2026) so the page shows names, not ids. */
  monitor_name?: string | null;
  monitor_email?: string | null;
  study_protocol_number?: string | null;
  study_title?: string | null;
  study_blinded?: boolean;
  /** Blinded studies only; null on open-label. */
  blinding?: 'blinded' | 'unblinded' | null;
  subject_reference?: string | null;
}

export interface SdvVerificationRow {
  verification_id: string;
  reference_number: string;
  monitor_user_id: string;
  study_id: string;
  site_id: string;
  subject_id: string;
  visit_id: string | null;
  form_id: string | null;
  field_ref: string | null;
  verified_at: string;
  notes: string | null;
}

export type SdvGranularity = 'field' | 'form' | 'visit';

export type SdvStatus = 'not_started' | 'in_progress' | 'complete';

export const remoteMonitoring = {
  listSubjects: (studyId: string) =>
    api<{
      study_id: string;
      site_id: string;
      wildcard: boolean;
      windows: Array<{
        reference_number: string;
        window_start_at: string | null;
        window_end_at: string | null;
      }>;
      subjects: Array<{
        subject_id: string;
        external_ref: string;
        ivrf_number?: string | null;
        status: string;
      }>;
      count: number;
    }>(`/remote-monitoring/subjects${qs({ study_id: studyId })}`),

  getSubject: (subjectId: string) =>
    api<{
      subject: {
        subject_id: string;
        external_ref: string;
        ivrf_number?: string | null;
        status: string;
        study_id: string;
        site_id: string;
      };
      instances: Array<{
        instance_id: string;
        visit_id: string;
        /** Names resolved server-side (13 Oct 2026) so the view shows names, not ids. */
        visit_name?: string | null;
        visit_order?: number | null;
        visit_status?: string | null;
        /** The visit's appointment (scheduled, else planned). */
        visit_planned_at?: string | null;
        /** The day the visit actually took place, once recorded. */
        visit_actual_date?: string | null;
        form_id: string;
        form_name?: string | null;
        form_reference_number?: string | null;
        form_version_id: string;
        status: FormInstanceStatus;
        submitted_at: string | null;
        sdv_status: SdvStatus;
        sdv_verified_at: string | null;
        sdv_reference_number: string | null;
      }>;
      verifications: SdvVerificationRow[];
      config: {
        granularity: SdvGranularity;
        completion_thresholds: { in_progress: number; complete: number };
        default?: boolean;
      };
    }>(`/remote-monitoring/subjects/${encodeURIComponent(subjectId)}`),

  markSdv: (body: {
    subject_id: string;
    visit_id?: string;
    form_id?: string;
    field_ref?: string;
    notes?: string;
  }) =>
    api<{
      verification_id: string;
      reference_number: string;
      verified_at: string;
    }>('/remote-monitoring/sdv-verify', { method: 'POST', body }),

  sdvQueue: (studyId: string) =>
    api<{
      study_id: string;
      site_id: string;
      rows: Array<{
        instance_id: string;
        subject_id: string;
        visit_id: string;
        form_id: string;
        status: FormInstanceStatus;
        submitted_at: string | null;
        subject_external_ref: string | null;
        subject_status: string | null;
      }>;
      count: number;
      subject_count: number;
    }>(`/remote-monitoring/sdv-queue${qs({ study_id: studyId })}`),

  sdvAging: (studyId: string) =>
    api<{
      study_id: string;
      site_id: string;
      buckets: {
        under_3d: number;
        three_to_seven: number;
        seven_to_fourteen: number;
        over_14d: number;
      };
      total_outstanding: number;
      subject_count: number;
      instance_count: number;
    }>(`/dashboard/sdv-aging${qs({ study_id: studyId })}`),

  rbmExport: (body: {
    study_id: string;
    site_id?: string;
    date_from?: string;
    date_to?: string;
    format?: 'csv' | 'json';
  }) =>
    api<{
      format: 'csv' | 'json';
      summary: unknown;
      payload: string;
    }>('/rbm-export', { method: 'POST', body }),
};

export const adminMonitoring = {
  list: (siteId?: string) =>
    api<{
      site_id: string;
      rows: MonitorAssignmentRow[];
      count: number;
    }>(`/admin/monitoring/assignments${qs(siteId ? { site_id: siteId } : {})}`),

  /** Subject picker for New assignment — reference numbers only, no PHI. */
  subjects: (studyId: string, siteId?: string) =>
    api<{
      study_id: string;
      subjects: Array<{ subject_id: string; reference_number: string; status: string }>;
    }>(`/admin/monitoring/subjects${qs({ study_id: studyId, ...(siteId ? { site_id: siteId } : {}) })}`),

  /** Monitor/Auditor picker for New assignment — active reviewers at the site. */
  candidates: (siteId?: string) =>
    api<{
      site_id: string;
      candidates: Array<{ user_id: string; full_name: string | null; email: string; role: string }>;
    }>(`/admin/monitoring/candidates${qs(siteId ? { site_id: siteId } : {})}`),

  create: (body: {
    monitor_user_id: string;
    study_id: string;
    site_id?: string;
    subject_id?: string;
    // F4 - REQUIRED. Server rejects create without both bounds
    // (window_end_at must be strictly after window_start_at).
    window_start_at: string;
    window_end_at: string;
    /** Required on a blinded study; ignored on open-label. */
    blinded?: boolean;
  }) =>
    api<{ assignment: MonitorAssignmentRow }>(
      '/admin/monitoring/assignments',
      { method: 'POST', body },
    ),

  update: (
    assignmentId: string,
    body: {
      window_start_at?: string | null;
      window_end_at?: string | null;
      subject_id?: string | null;
    },
  ) =>
    api<{ assignment_id: string; updated: true }>(
      `/admin/monitoring/assignments/${encodeURIComponent(assignmentId)}`,
      { method: 'PATCH', body },
    ),

  revoke: (assignmentId: string) =>
    api<{ assignment_id: string; revoked: true; revoked_at: string }>(
      `/admin/monitoring/assignments/${encodeURIComponent(assignmentId)}`,
      { method: 'DELETE' },
    ),
};

// ---------------------------------------------------------------------------
// Phase 8 slice — PI Sign Visit + Reopen + Randomization + Screen Failure
// ---------------------------------------------------------------------------

export type VisitLockState = 'unlocked' | 'signed_pending_sdv' | 'locked';

/** Ready for PI signature = visit date recorded + every required form submitted. */
export interface VisitSignReadiness {
  ready: boolean;
  date_recorded: boolean;
  forms_required: number;
  forms_submitted: number;
}

export type VisitReAuthMethod = 'password' | 'password_otp';

export interface VisitSignatureRow {
  signature_id: string;
  reference_number: string;
  visit_id: string;
  subject_id: string;
  study_id: string;
  site_id: string;
  signed_by_user_id: string;
  signed_by_name: string | null;
  signed_by_email: string | null;
  signed_at: string;
  signature_meaning: string;
  signed_visit_version: number;
  re_authenticated_at: string;
  re_auth_method: VisitReAuthMethod;
  supersedes_signature_id: string | null;
}

export interface VisitReopenRow {
  reopen_id: string;
  reference_number: string;
  visit_id: string;
  reopened_by_user_id: string;
  reopened_by_name: string | null;
  reopened_at: string;
  reason_text: string;
  prior_signature_id: string;
  re_authenticated_at: string;
  re_auth_method: VisitReAuthMethod;
}

// ---------------------------------------------------------------------------
// Phase 8 base AC18 — Per-visit documents

export type PiRelevance = 'unset' | 'yes' | 'no';

export interface VisitDocumentRow {
  visit_document_id: string;
  reference_number: string;
  visit_id: string;
  subject_id: string;
  study_id: string;
  site_id: string;
  s3_bucket: string;
  s3_key: string;
  file_name: string;
  mime_type: string;
  file_size: number;
  pi_relevance: PiRelevance;
  pi_relevance_note: string | null;
  uploaded_at: string;
  uploaded_by: string;
}

// ---------------------------------------------------------------------------
// Phase 8 base AC19 — Next-visit + reminder / out-of-window sweeps

export interface NextVisitPayload {
  visit_id: string;
  visit_name: string | null;
  planned_date: string | null;
  window_minus_days: number | null;
  window_plus_days: number | null;
  window_start_date: string | null;
  window_end_date: string | null;
  reminder_generated_at: string | null;
  is_reminder_active: boolean;
}

export const visitSchedule = {
  nextForSubject: (subjectId: string) =>
    api<{
      subject_id: string;
      next_visit: NextVisitPayload | null;
      upcoming_count: number;
    }>(`/subjects/${encodeURIComponent(subjectId)}/next-visit`),

  reminderSweep: (body: { study_id: string; threshold_days?: number; now?: string }) =>
    api<{
      study_id: string;
      ran_at: string;
      threshold_days: number;
      considered: number;
      reminders_generated: number;
      reminders: Array<{
        visit_id: string;
        subject_id: string;
        planned_date: string | null;
      }>;
    }>('/jobs/visits/reminder-sweep', { method: 'POST', body }),

  outOfWindowSweep: (body: {
    study_id: string;
    reflag_already_flagged?: boolean;
  }) =>
    api<{
      study_id: string;
      ran_at: string;
      considered: number;
      flagged_count: number;
      flagged: Array<{
        visit_id: string;
        subject_id: string;
        days_delta: number;
      }>;
    }>('/jobs/visits/out-of-window-sweep', { method: 'POST', body }),
};

// ---------------------------------------------------------------------------
// Phase 8 base AC15 — I/E checklist

export type IeCriterionType = 'inclusion' | 'exclusion';

export interface IeCriterionRow {
  criterion_id: string;
  reference_number: string;
  study_id: string;
  visit_template_id: string;
  criterion_type: IeCriterionType;
  criterion_text: string;
  order_index: number;
  active: string;
  created_at: string;
  created_by: string;
}

export interface IeResponseRow {
  response_id: string;
  reference_number: string;
  criterion_id: string;
  visit_id: string;
  subject_id: string;
  study_id: string;
  site_id: string;
  met: 'true' | 'false';
  deviation_text: string | null;
  recorded_at: string;
  recorded_by: string;
}

export type IeStatus = 'not_configured' | 'in_progress' | 'complete';

export const ieChecklist = {
  listForStudy: (studyId: string, filters: { visit_template_id?: string; active_only?: boolean } = {}) => {
    const q: Record<string, string> = {};
    if (filters.visit_template_id) q.visit_template_id = filters.visit_template_id;
    if (filters.active_only === false) q.active_only = 'false';
    return api<{ study_id: string; rows: IeCriterionRow[]; count: number }>(
      `/studies/${encodeURIComponent(studyId)}/ie-checklists${qs(q)}`,
    );
  },

  createCriterion: (
    studyId: string,
    body: {
      visit_template_id: string;
      criterion_type: IeCriterionType;
      criterion_text: string;
      order_index?: number;
    },
  ) =>
    api<{ criterion: IeCriterionRow }>(
      `/studies/${encodeURIComponent(studyId)}/ie-checklists`,
      { method: 'POST', body },
    ),

  deactivateCriterion: (studyId: string, criterionId: string) =>
    api<{ criterion_id: string; deactivated: true }>(
      `/studies/${encodeURIComponent(studyId)}/ie-checklists/${encodeURIComponent(criterionId)}`,
      { method: 'DELETE' },
    ),

  getForVisit: (visitId: string) =>
    api<{
      visit_id: string;
      status: IeStatus;
      criteria: IeCriterionRow[];
      responses_latest: IeResponseRow[];
      responses_trail?: IeResponseRow[];
      completion: {
        total_criteria: number;
        answered_criteria: number;
        is_complete: boolean;
        missing_criterion_ids?: string[];
        deviation_criterion_ids?: string[];
      };
    }>(`/visits/${encodeURIComponent(visitId)}/ie-checklist`),

  postResponses: (
    visitId: string,
    body: {
      responses: Array<{
        criterion_id: string;
        met: boolean;
        deviation_text?: string;
      }>;
    },
  ) =>
    api<{
      visit_id: string;
      responses_recorded: number;
      deviations_recorded: number;
      recorded_at: string;
    }>(`/visits/${encodeURIComponent(visitId)}/ie-responses`, {
      method: 'POST',
      body,
    }),
};

export const visitDocuments = {
  presign: (
    visitId: string,
    body: { file_name: string; mime_type: string; file_size: number },
  ) =>
    api<{
      visit_document_id: string;
      upload_url: string;
      s3_bucket: string;
      s3_key: string;
      file_name: string;
    }>(`/visits/${encodeURIComponent(visitId)}/documents/presign`, {
      method: 'POST',
      body,
    }),

  finalise: (
    visitId: string,
    body: {
      visit_document_id: string;
      s3_bucket: string;
      s3_key: string;
      file_name: string;
      mime_type: string;
      file_size: number;
    },
  ) =>
    api<{
      visit_document_id: string;
      reference_number: string;
      uploaded_at: string;
      mirror_result: {
        binder_id: string;
        document_id: string;
        version_id: string;
      } | null;
    }>(`/visits/${encodeURIComponent(visitId)}/documents`, {
      method: 'POST',
      body,
    }),

  list: (visitId: string) =>
    api<{ visit_id: string; rows: VisitDocumentRow[]; count: number }>(
      `/visits/${encodeURIComponent(visitId)}/documents`,
    ),

  setRelevance: (
    documentId: string,
    body: { pi_relevance: PiRelevance; pi_relevance_note?: string },
  ) =>
    api<{
      visit_document_id: string;
      pi_relevance: PiRelevance;
      pi_relevance_note: string | null;
    }>(`/visit-documents/${encodeURIComponent(documentId)}/relevance`, {
      method: 'PATCH',
      body,
    }),
};

export const visitLifecycle = {
  sign: (
    visitId: string,
    body: {
      re_auth_receipt: string;
      re_auth_method: VisitReAuthMethod;
      signature_meaning: string;
    },
  ) =>
    api<{
      signature_id: string;
      reference_number: string;
      signed_at: string;
      signed_visit_version: number;
      lock_state: VisitLockState;
    }>(`/visits/${encodeURIComponent(visitId)}/sign`, { method: 'POST', body }),

  reopen: (
    visitId: string,
    body: {
      re_auth_receipt: string;
      re_auth_method: VisitReAuthMethod;
      reason_text: string;
    },
  ) =>
    api<{
      reopen_id: string;
      reference_number: string;
      reopened_at: string;
      prior_signature_id: string;
    }>(`/visits/${encodeURIComponent(visitId)}/reopen`, { method: 'POST', body }),

  signHistory: (visitId: string) =>
    api<{
      visit_id: string;
      lock_state: VisitLockState;
      /** Visit sign-off flow (14 Oct 2026). Null when the visit isn't unlocked. */
      sign_readiness: VisitSignReadiness | null;
      current_signature_id: string | null;
      signatures: VisitSignatureRow[];
      reopens: VisitReopenRow[];
    }>(`/visits/${encodeURIComponent(visitId)}/sign-history`),
};

export const subjectLifecycle = {
  recordRandomization: (subjectId: string, body: { randomization_number: string }) =>
    api<{
      subject_id: string;
      randomization_number: string;
      captured_at: string;
      status: 'enrolled';
    }>(`/subjects/${encodeURIComponent(subjectId)}/randomization`, {
      method: 'POST',
      body,
    }),

  recordScreenFailure: (
    subjectId: string,
    body: { reason: string; criterion_ids?: string[] },
  ) =>
    api<{
      subject_id: string;
      status: 'withdrawn';
      reason: string;
      recorded_at: string;
    }>(`/subjects/${encodeURIComponent(subjectId)}/screen-failure`, {
      method: 'POST',
      body,
    }),
};

// ---------------------------------------------------------------------------
// Phase 1 item 10 (AC3) — Consent drag-drop signature field layout
// ---------------------------------------------------------------------------

export type ConsentFieldType = 'signature' | 'initial' | 'name' | 'date' | 'time' | 'text' | 'checkbox';
/**
 * Mirrors FieldRole in the backend's services/consent/fieldLayout.ts.
 * `witness` is legacy-only — it is not offered in the layout editor and the
 * positioned template builder rejects it; kept in the union so layouts saved
 * before 2026-07-29 still load and can be re-assigned to parent / lar.
 */
export type ConsentFieldRole = 'subject' | 'parent' | 'lar' | 'witness' | 'staff' | 'pi';

export interface ConsentFieldPosition {
  field_id: string;
  field_type: ConsentFieldType;
  role: ConsentFieldRole;
  page_index: number;
  x: number;
  y: number;
  width: number;
  height: number;
  required: boolean;
  label: string;
}

// ---------------------------------------------------------------------------
// Phase 8 extended-contract AC2 / AC12 — Part-11 re-authentication.

export type ReAuthPurpose = 'sign_visit' | 'reopen_visit' | 'approve_protocol_amendment' | 'generic';
export type ReAuthMethod = 'password' | 'password_otp';

export const authReAuth = {
  reAuthenticate: (body: {
    password: string;
    totp_code?: string;
    purpose?: ReAuthPurpose;
  }) =>
    api<{
      receipt: string;
      method: ReAuthMethod;
      purpose: ReAuthPurpose;
      expires_at: string;
      ttl_seconds: number;
    }>('/auth/re-authenticate', { method: 'POST', body }),
};

// ---------------------------------------------------------------------------
// Phase 1 item 7 (AC6) — bulk re-consent notification

export type BulkNotifySkippedReason =
  | 'no_email_on_file'
  | 'not_flagged_for_reconsent'
  | 'terminal_status'
  | 'cross_site'
  | 'not_in_allowlist'
  | 'send_failed';

export interface BulkNotifyOutcome {
  subject_id: string;
  external_ref: string;
  /** Subject ID, else REF-SUB-xxxx (server-side, 17 Oct 2026). */
  subject_display_id?: string;
  status: 'notified' | 'skipped';
  reason?: BulkNotifySkippedReason;
  message_id?: string;
}

export const bulkReConsentNotify = {
  run: (
    studyId: string,
    body: {
      consent_document_id?: string;
      subject_ids?: string[];
      contact_email?: string;
      dry_run?: boolean;
    } = {},
  ) =>
    api<{
      study_id: string;
      consent_document_id: string;
      version_label: string;
      dry_run: boolean;
      candidates_considered: number;
      notified_count: number;
      outcomes: BulkNotifyOutcome[];
    }>(`/studies/${encodeURIComponent(studyId)}/reconsent/bulk-notify`, {
      method: 'POST',
      body,
    }),
};

// ---------------------------------------------------------------------------
// Phase 1 item 10 (AC3) — Consent signature events + certificates

// Written by signatureEventWriter from the layout role, so it tracks
// ConsentFieldRole above.
export type ConsentSignerRole = 'subject' | 'parent' | 'lar' | 'witness' | 'staff' | 'pi';
export type ConsentCertificateType =
  | 'completion_certificate'
  | 'signature_summary'
  | 'audit_summary';

export interface ConsentSignatureEventRow {
  signature_event_id: string;
  reference_number: string;
  consent_document_id: string;
  consent_session_id: string | null;
  subject_id: string;
  signer_user_id: string | null;
  signer_role: ConsentSignerRole;
  field_id: string;
  field_type: ConsentFieldType;
  page_index: number;
  signed_at: string;
  signature_meaning: string;
  docuseal_submitter_id: string | null;
  signer_display_name?: string | null;
  signer_email?: string | null;
}

export interface ConsentCertificateRow {
  certificate_id: string;
  reference_number: string;
  consent_document_id: string;
  subject_id: string;
  certificate_type: ConsentCertificateType;
  s3_bucket: string;
  s3_key: string;
  generated_at: string;
  generated_by: string | null;
}

export const consentSignatureEvents = {
  list: (consentDocumentId: string) =>
    api<{
      consent_document_id: string;
      version_label: string;
      rows: ConsentSignatureEventRow[];
      count: number;
    }>(`/consent-documents/${encodeURIComponent(consentDocumentId)}/signature-events`),
};

export const consentCertificates = {
  list: (consentDocumentId: string) =>
    api<{
      consent_document_id: string;
      rows: ConsentCertificateRow[];
      count: number;
    }>(`/consent-documents/${encodeURIComponent(consentDocumentId)}/certificates`),

  generate: (
    consentDocumentId: string,
    subjectId: string,
    body: { certificate_type?: ConsentCertificateType } = {},
  ) =>
    api<{
      certificate_id: string;
      reference_number: string;
      certificate_type: ConsentCertificateType;
      signature_event_count: number;
      page_count: number;
      generated_at: string;
      s3_bucket: string;
      s3_key: string;
    }>(
      `/consent-documents/${encodeURIComponent(consentDocumentId)}/subjects/${encodeURIComponent(subjectId)}/certificate`,
      { method: 'POST', body },
    ),
};

export const consentLayout = {
  get: (consentDocumentId: string) =>
    api<{
      consent_document_id: string;
      page_count: number | null;
      has_drag_drop_layout: boolean;
      layout: { fields: ConsentFieldPosition[] };
      version_label: string;
    }>(`/consent-documents/${encodeURIComponent(consentDocumentId)}/field-layout`),

  put: (consentDocumentId: string, body: { fields: ConsentFieldPosition[] }) =>
    api<{
      consent_document_id: string;
      field_count: number;
      has_drag_drop_layout: boolean;
      saved_at: string;
    }>(`/consent-documents/${encodeURIComponent(consentDocumentId)}/field-layout`, {
      method: 'PUT',
      body,
    }),
};

export { api };
